/**
 * The portal's screens, as view models.
 *
 * A screen here is a pure function from what the API returned to what the page
 * shows. No framework, no DOM: whatever renders these can be swapped without
 * touching a rule, and the rules can be tested without mounting anything.
 *
 * This is also the layer that decides what happens when a report is *refused*.
 * Two of them are refused routinely rather than exceptionally — per-player
 * reporting is off by default (E27), and a locked period changes what a figure
 * means — so "show an error" would be wrong for the common case.
 */

import {
  conversions,
  currencyViewControl,
  figure,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  freshness,
  UNAVAILABLE,
  isApiClientError,
  type ApiClientError,
  type ConversionsView,
  type CurrencyView,
  type CurrencyViewControl,
  type Envelope,
  type FigureView,
  periodDays,
  formatDay,
  geoLabel,
  resolveRequestUrl,
  csvCell,
} from '@affdice/portal-kit';
import {
  attributionNotice,
  linkRow,
  type LinkRow,
  explainStatus,
  funnelRow,
  paymentRow,
  playerColumns,
  SUB_ID_SLOTS,
  type FunnelView,
  type PaymentView,
} from './present.ts';
import { t, type AffiliateMessageKey } from './messages.ts';
import type { AffiliateApi, CampaignTerms, CarryoverDeficit, SubAffiliatesReport, SubAffiliateWhyNot, TermsSummary } from './client.ts';

export interface DashboardView {
  readonly greeting: string;
  /**
   * §6.3g. `code` is the operator's own word for the state and `status` is the
   * reader's — a screen branches on the first and prints the second, because
   * the two are the same string only in English.
   */
  readonly status: { code: string; status: string; meaning: string };
  readonly attribution: string;
  readonly updated: string;
  readonly headline: readonly FigureView[];
  /**
   * §39.4 — the explanation, once, when every headline figure carries the same
   * one. The *mark* stays on each figure; only the repeated sentence is lifted.
   *
   * Printing "March is not locked yet" three times under three numbers is how a
   * caveat becomes wallpaper: the third repetition is what teaches the reader to
   * stop reading it, and then the one figure that says something different gets
   * skipped too.
   */
  readonly headlineNote?: string;
  /** §1.4a — why the two deposit figures differ, when they do. */
  readonly ftdNote?: string;
  readonly funnel: readonly FunnelView[];
}

/** True when every figure carries the identical note, so it can be said once. */
function sharedNote(figures: readonly FigureView[]): string | undefined {
  const first = figures[0]?.note;
  if (first === undefined || figures.length < 2) return undefined;
  return figures.every((f) => f.note === first) ? first : undefined;
}

/**
 * The dashboard.
 *
 * The totals are summed from the same rows the table shows rather than fetched
 * separately, because two queries answering the same question at two moments is
 * how a dashboard comes to disagree with itself — the affiliate adds up the
 * column, gets a different number from the one at the top, and reports a bug
 * that is really a race.
 */
export function dashboard(
  me: {
    display_name: string;
    status: string;
    attribution_model: string;
  },
  report: Envelope<
    {
      day: string;
      clicks: number;
      registrations: number;
      ftds: number;
      payable_ftds: number;
      bot_clicks: number;
      deduplicated_clicks: number;
      geo_blocked_clicks: number;
      os_blocked_clicks: number;
      device_blocked_clicks: number;
      targeting_blocked_clicks: number;
      /**
       * §12.13/§26.11 — the day's counted impressions. Optional: an older
       * response (a fixture built before task A9, or a build that has not
       * deployed the funnel change yet) carries none, which reads the same
       * way a day with a real zero does — see the CTR note below.
       */
      impressions?: number;
    }[]
  >,
  now: Date,
): DashboardView {
  const rows = report.data.map(funnelRow);
  const total = (pick: (r: FunnelView) => number) => rows.reduce((n, r) => n + pick(r), 0);

  const reported = total((r) => r.ftds);
  const payable = total((r) => r.payableFtds);
  const totalClicks = total((r) => r.counted);

  // §12.12/§12.12a — CTR is clicks over impressions (Appendix G.2 `ctr`), and
  // nothing else: an impression prices a CPM line and is counted, but it does
  // not attribute, so CTR here is a plain reporting ratio, never a signal
  // this screen (or anything downstream of it) treats as evidence of a
  // player, a window, or an attribution.
  //
  // Zero impressions reads as "not measured" (`UNAVAILABLE`, the same dash
  // every other unavailable figure on this console uses), never as "0.00%".
  // A CTR of 0.00% is a real, meaningful answer — the banner ran and nobody
  // clicked it — and it is indistinguishable from "no banner ran here at
  // all" unless the two are kept apart. Showing a computed zero for a
  // campaign that never served an impression would read as a judgement on
  // traffic that was never measured, which is worse than a blank (task A9).
  const totalImpressions = report.data.reduce((n, r) => n + (r.impressions ?? 0), 0);
  const ctrDisplay =
    totalImpressions === 0 ? UNAVAILABLE : `${((totalClicks / totalImpressions) * 100).toFixed(2)}%`;

  const headline = [
    // §12.13 — first, because it is what happened first: a banner is served,
    // then clicked. It is also the denominator of the CTR at the other end of
    // this row, and a ratio whose denominator is not on the same screen is a
    // number an affiliate has to take on trust.
    //
    // Blank rather than `0` on a total of zero, for the reason CTR is blank:
    // "nothing was served here" and "impressions are not being counted for
    // this campaign" are different claims, and a headline `0` asserts the
    // first when only the second may be true.
    figure(
      t('figure.impressions'),
      totalImpressions === 0 ? UNAVAILABLE : formatCount(totalImpressions),
      report,
    ),
    // §2.8c — grouped for the reader, like the amounts beside them. A headline
    // that says `1234567` under a label that says "Cliques" is half in one
    // language and half in none.
    figure(t('figure.clicks'), formatCount(totalClicks), report),
    figure(t('figure.registrations'), formatCount(total((r) => r.registrations)), report),
    // §1.4a — "Reporting FTD count and payable FTD count are separate figures
    // and MUST be shown separately." An affiliate shown one number expects to
    // be paid for all of them, and every deduction then arrives as a surprise.
    figure(t('figure.ftds'), formatCount(reported), report),
    figure(t('figure.payableFtds'), formatCount(payable), report),
    // §12.12/§26.11 — last, deliberately. `series` (App.tsx) pairs each
    // headline figure with a sparkline picker **by index**, so this list's
    // order is load-bearing: anything inserted here has to be inserted there
    // too, or four figures quietly acquire their neighbour's chart. A
    // trailing figure with no picker simply draws with no sparkline, which
    // `App.tsx` already handles — and a ratio has no sensible daily chart
    // anyway, since a day that served nothing has no ratio to plot.
    figure(t('figure.ctr'), ctrDisplay, report),
  ];
  const note = sharedNote(headline);

  // §1.4a — the gap is qualification and clawback. Named, because the two
  // numbers sitting side by side with no explanation reads as an error.
  const gap = reported - payable;

  return {
    greeting: me.display_name,
    status: explainStatus(me.status),
    attribution: attributionNotice(me.attribution_model),
    updated: freshness(report.data_as_of, now),
    // The note comes off the figures only when it is being said once below
    // them. `provisional` is untouched, so the mark on each figure stays — that
    // is the part §39.4 requires to travel with the number.
    headline: note ? headline.map(({ note: _, ...rest }) => rest) : headline,
    ...(note ? { headlineNote: note } : {}),
    ...(gap > 0
      ? {
          ftdNote: t(gap === 1 ? 'dash.ftdGap.one' : 'dash.ftdGap.other', { n: gap }),
        }
      : {}),
    funnel: rows,
  };
}

/** One brand's running total, as the dashboard reads it. */
export interface EarningsBrandRow {
  readonly brand: string;
  readonly currency: string;
  /** Lines the commission run has already written. */
  readonly computed: string;
  /** §24.2 — qualified, not priced yet. */
  readonly pipeline: string;
  readonly held: string;
  readonly payable: string;
  /** How many players are behind `pipeline`, when any are. */
  readonly pipelineNote?: string;
  /** Money earned in dates the operator has not opened a period for yet. */
  readonly noPeriodNote?: string;
  /** §24.5b — when the held part releases, so "when will I be paid" is answered here. */
  readonly heldUntil?: string;
  /** "Since {date}" — when this brand's last period closed; or since joining. */
  readonly since: string;
  /** The open period's dates, or that none is open. */
  readonly period: string;
  /**
   * §24.2b — who is not earning here and why, one line per reason, in the
   * words the Terms page uses for the criterion. The answer to "registration
   * and deposit done, so where is my payable", beside the figure.
   */
  readonly notEarning: readonly string[];
}

/**
 * Phase 2 (DESIGN.md) — the one figure this panel leads with, carried on the
 * figure itself. A screen decides its hero by setting this flag when the
 * figure is built, never the component that renders the group: picking it by
 * English label or by position in the array would break the moment the label
 * is translated or the group is reordered. Mirrors `DashboardFigureView` in
 * the operator's screens.ts.
 */
export interface EarningsFigureView extends FigureView {
  readonly hero?: true;
}

export interface EarningsSoFarView {
  /**
   * One per currency. Never one figure across currencies — §21.7f's rule about
   * not restating a billed amount applies with more force to a total that
   * would be arithmetic on two different currencies rather than a conversion.
   *
   * DESIGN.md's "exactly one hero per view" still applies when an affiliate is
   * paid in more than one: the first currency in this array — already sorted
   * below — carries the flag, and the rest render as plain tiles.
   */
  readonly totals: readonly EarningsFigureView[];
  /** Of the total, the part inside a period the operator has opened. */
  readonly inPeriod: readonly FigureView[];
  /** Of the total, the part in dates no period covers yet. */
  readonly notInPeriod: readonly FigureView[];
  /**
   * §39.4 — the provisional sentence, said once beneath the figures when every
   * figure carries the same one. The mark stays on each figure; only the
   * repeated sentence is lifted, exactly as the dashboard's headline does.
   */
  readonly provisionalNote?: string;
  /** Said once when anything is not in a period yet: whose move it is. */
  readonly notInPeriodNote?: string;
  /** Said once when any player is not earning: where the criteria are. */
  readonly notEarningNote?: string;
  /**
   * WP11 — first deposits waiting on a review, how many and what they would
   * earn, said beneath the payable figure they are not in. Neutral words.
   */
  readonly underReviewNote?: string;
  /** §24.5 — stated beside the payable figure, never inside it. */
  readonly held: readonly FigureView[];
  readonly heldNote?: string;
  readonly rows: readonly EarningsBrandRow[];
  /** §30.1a — what to say when there is genuinely nothing. */
  readonly empty?: string;
}

/**
 * §22.7, §39.1 — what the affiliate has earned so far, per brand.
 *
 * The dashboard counted clicks, registrations and deposits and showed no money
 * at all until a period was locked and a statement existed. An affiliate
 * reading it mid-month saw their traffic working and no figure for what it had
 * earned them, which is the gap §39.1 calls a commercial obligation rather
 * than a UI nicety.
 *
 * Every figure here is provisional and marked so by `figure()` off the
 * envelope: §22.7 covers CLOSED as well as OPEN, and §22.7b is why nothing
 * marks a locked one — a locked period's money is on a statement and is not
 * counted here at all.
 */
/**
 * §24.2b — a reason code, in the words the Terms page uses for the same
 * criterion. The engine's names differ from the ref table's short codes the
 * criterion words are keyed by; NO_DEPOSIT_YET and NO_DEAL are not criteria
 * and have words of their own.
 */
const REASON_WORDS: Readonly<Record<string, string>> = {
  KYC_APPROVED: 'criterion.KYC_APPROVED',
  GEO_ELIGIBLE: 'criterion.GEO_ELIGIBLE',
  PRODUCT_ELIGIBLE: 'criterion.PRODUCT_ELIGIBLE',
  DEPOSIT_SETTLED: 'criterion.DEPOSIT_SETTLED',
  DEPOSIT_NOT_REVERSED: 'criterion.NOT_REVERSED',
  NO_FRAUD_FLAG: 'criterion.NO_FRAUD_FLAG',
  NOT_SELF_EXCLUDED: 'criterion.NOT_SELF_EXCLUDED',
  MINIMUM_DEPOSIT: 'criterion.MIN_DEPOSIT',
  MINIMUM_TURNOVER: 'criterion.MIN_TURNOVER',
  MINIMUM_BETS: 'criterion.MIN_BETS',
  MINIMUM_DAYS_ACTIVE: 'criterion.MIN_DAYS_ACTIVE',
  NO_DEPOSIT_YET: 'reason.noDepositYet',
  NO_DEAL: 'reason.noDeal',
  AWAITING_FX_RATE: 'reason.awaitingFxRate',
  // WP9 — the operator excluded the player from the affiliate's CPA.
  OPERATOR_EXCLUDED: 'reason.operatorExcluded',
};

function reasonWords(code: string): string {
  const key = REASON_WORDS[code];
  return key ? t(key as AffiliateMessageKey) : code;
}

/**
 * The reason column of the qualification report. A player held for an
 * exchange rate is not failing anything, so the engine's own sentence is
 * replaced by words saying what it is waiting for; any other row keeps the
 * engine's sentence.
 */
export function qualificationReasonWords(row: {
  readonly reason?: string | null;
  readonly failing_criterion: string | null;
}): string {
  if (row.reason === 'AWAITING_FX_RATE') {
    const words = reasonWords(row.reason);
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return row.failing_criterion ?? '—';
}

/**
 * WP11 — a mail archive row's subject. The archive falls back to the
 * template's code where the operator never reworded the template; a code the
 * portal has words for is shown in the reader's language instead.
 */
const MAIL_SUBJECTS: Readonly<Record<string, AffiliateMessageKey>> = {
  COMMISSION_UNDER_REVIEW: 'mail.template.COMMISSION_UNDER_REVIEW',
};

export function mailSubject(row: { readonly subject: string; readonly template_code: string }): string {
  const key = MAIL_SUBJECTS[row.template_code];
  return key && row.subject === row.template_code ? t(key) : row.subject;
}

/**
 * WP11 — the warning under "Payable so far" while first deposits wait on a
 * review. The count, and the amount the server priced for them (summed per
 * currency, never across), or for those of them it could price; the server
 * judges them by the commission engine's own rule. Never the review's kind.
 */
function reviewWarning(rows: readonly {
  readonly currency: string;
  readonly under_review_players?: number;
  readonly under_review_priced_players?: number;
  readonly under_review_minor?: string;
}[]): string | undefined {
  let players = 0;
  let priced = 0;
  const byCurrency = new Map<string, bigint>();
  for (const r of rows) {
    players += r.under_review_players ?? 0;
    priced += r.under_review_priced_players ?? 0;
    const minor = BigInt(r.under_review_minor ?? '0');
    if (minor > 0n) byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0n) + minor);
  }
  if (players === 0) return undefined;
  const amount = [...byCurrency.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, minor]) => formatMoney({ amount_minor: minor.toString(), currency }))
    .join(', ');
  if (!amount) {
    return t(players === 1 ? 'earnings.underReview.noAmount.one' : 'earnings.underReview.noAmount.other', { n: players });
  }
  if (priced < players) {
    return t('earnings.underReview.partial', { n: players, m: priced, amount });
  }
  return t(players === 1 ? 'earnings.underReview.one' : 'earnings.underReview.other', { n: players, amount });
}

/**
 * Owner, 2026-10-07 — why first deposits are not payable yet, from the
 * figures this screen already has, rather than a pointer to the Terms page:
 * each brand's players waiting for the brand to calculate, and each
 * condition still unmet, with how many players it holds back. Brand names
 * are added only when there is more than one brand.
 */
export function ftdGapReasons(view: EarningsSoFarView | null): string[] {
  if (!view) return [];
  const many = view.rows.length > 1;
  const out: string[] = [];
  for (const r of view.rows) {
    const say = (text: string) => out.push(many ? `${r.brand}: ${text}` : text);
    if (r.pipelineNote) say(r.pipelineNote);
    for (const n of r.notEarning) say(n);
  }
  return out;
}

export function earningsSoFar(
  report: Envelope<
    {
      brand_id: string;
      brand_name: string | null;
      currency: string;
      computed_minor: string;
      held_minor: string;
      pipeline_minor: string;
      pipeline_held_minor: string;
      pipeline_players: number;
      no_period_minor?: string;
      no_period_players?: number;
      not_earning?: { criterion: string; players: number }[];
      not_earning_players?: number;
      under_review_players?: number;
      under_review_priced_players?: number;
      under_review_minor?: string;
      held_release_from?: string | null;
      held_release_to?: string | null;
      payable_minor: string;
      since?: string | null;
      open_period_from?: string | null;
      open_period_to?: string | null;
    }[]
  > & { period_open?: boolean },
): EarningsSoFarView {
  const money = (amount_minor: string, currency: string) =>
    formatMoney({ amount_minor, currency });

  // Summed per currency rather than across them. Two brands paying in two
  // currencies produce two headline figures, not one meaningless one.
  const byCurrency = new Map<string, { payable: bigint; held: bigint; noPeriod: bigint }>();
  for (const r of report.data) {
    const t = byCurrency.get(r.currency) ?? { payable: 0n, held: 0n, noPeriod: 0n };
    t.payable += BigInt(r.payable_minor);
    t.held += BigInt(r.held_minor);
    t.noPeriod += BigInt(r.no_period_minor ?? '0');
    byCurrency.set(r.currency, t);
  }
  const currencies = [...byCurrency.entries()].sort(([a], [b]) => a.localeCompare(b));

  // Three figures, because "what am I owed" has three honest answers when
  // periods are opened by hand: everything since the last close, the part
  // the operator has opened a period for, and the part they have not yet.
  //
  // Phase 2 (DESIGN.md) — exactly one hero per view. `currencies` is already
  // sorted above, so "first" is a stable choice (alphabetically first
  // currency code) rather than whichever the API happened to list first.
  const totals: EarningsFigureView[] = currencies.map(([currency, sums], i) => ({
    ...figure(t('figure.payableSoFar'), money(sums.payable.toString(), currency), report),
    ...(i === 0 ? { hero: true as const } : {}),
  }));
  const inPeriod = currencies.map(([currency, sums]) =>
    figure(t('figure.inOpenPeriod'), money((sums.payable - sums.noPeriod).toString(), currency), report),
  );
  const notInPeriod = currencies.map(([currency, sums]) =>
    figure(t('figure.notInPeriod'), money(sums.noPeriod.toString(), currency), report),
  );
  const anythingOutside = currencies.some(([, sums]) => sums.noPeriod > 0n);

  // Only where there is held money to explain. A held figure of zero on every
  // brand is a row of noughts that teaches the reader to skip the column, and
  // then the period where it says something real gets skipped too.
  const held = [...byCurrency.entries()]
    .filter(([, sums]) => sums.held > 0n)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, sums]) =>
      figure(t('figure.heldSoFar'), money(sums.held.toString(), currency), report),
    );

  const rows = report.data.map((r) => {
    const players = r.pipeline_players;
    const outside = BigInt(r.no_period_minor ?? '0');
    return {
      // A brand with no display name still has to be nameable on the row it
      // owns — a blank cell reads as a bug in the table, not as a brand.
      brand: r.brand_name ?? t('brand.unnamed'),
      currency: r.currency,
      computed: money(r.computed_minor, r.currency),
      pipeline: money(r.pipeline_minor, r.currency),
      held: money(r.held_minor, r.currency),
      payable: money(r.payable_minor, r.currency),
      ...(players > 0
        ? {
            pipelineNote: t(
              players === 1 ? 'earnings.pipeline.one' : 'earnings.pipeline.other',
              { n: players },
            ),
          }
        : {}),
      ...(outside > 0n
        ? { noPeriodNote: t('earnings.noPeriodAmount', { amount: money(outside.toString(), r.currency) }) }
        : {}),
      // §24.5b — the date, or the span of dates, the held money releases on.
      ...(r.held_release_from && r.held_release_to
        ? {
            heldUntil:
              formatDate(r.held_release_from) === formatDate(r.held_release_to)
                ? t('earnings.heldUntil', { date: formatDate(r.held_release_to) })
                : t('earnings.heldUntilRange', {
                    from: formatDate(r.held_release_from),
                    to: formatDate(r.held_release_to),
                  }),
          }
        : {}),
      since: r.since ? t('earnings.since', { date: formatDate(r.since) }) : t('earnings.sinceJoining'),
      period: r.open_period_from && r.open_period_to
        ? t('earnings.openPeriod', {
            from: formatDay(periodDays(r.open_period_from, r.open_period_to).first),
            to: formatDay(periodDays(r.open_period_from, r.open_period_to).last),
          })
        : t('earnings.noOpenPeriod'),
      notEarning: (r.not_earning ?? []).map((n) => {
        // A criterion is a condition still to be met; anything else is a state.
        const awaited = REASON_WORDS[n.criterion]?.startsWith('criterion.') === true;
        const key = awaited
          ? (n.players === 1 ? 'earnings.notEarningWaiting.one' : 'earnings.notEarningWaiting.other')
          : (n.players === 1 ? 'earnings.notEarning.one' : 'earnings.notEarning.other');
        return t(key, { n: n.players, reason: reasonWords(n.criterion) });
      }),
    };
  });
  const anyoneBlocked = report.data.some((r) => (r.not_earning_players ?? 0) > 0);
  const underReviewNote = reviewWarning(report.data);

  // §39.4 — one sentence for all the figures, not the same sentence under
  // each of them. Three tiles each ending in the same two lines is how a
  // caveat becomes wallpaper.
  const every = [...totals, ...inPeriod, ...notInPeriod, ...held];
  const provisionalNote = sharedNote(every);
  // Generic so `totals` keeps its `hero` flag through the strip: a `FigureView`
  // signature here would type it away even though the object still carries it
  // at runtime, and the Figure below would never see it.
  const lift = <T extends FigureView>(figures: readonly T[]): readonly T[] =>
    provisionalNote ? figures.map(({ note: _, ...rest }) => rest as T) : figures;

  return {
    totals: lift(totals),
    inPeriod: lift(inPeriod),
    notInPeriod: lift(notInPeriod),
    ...(provisionalNote ? { provisionalNote } : {}),
    // Said when any of it is outside a period, or when no period is open at
    // all: either way the next move is the operator's, and the affiliate
    // should not read the figure as one the run has refused.
    ...(anythingOutside || report.period_open === false ? { notInPeriodNote: t('earnings.notInPeriodNote') } : {}),
    ...(anyoneBlocked ? { notEarningNote: t('earnings.notEarningNote') } : {}),
    ...(underReviewNote ? { underReviewNote } : {}),
    held: lift(held),
    ...(held.length > 0 ? { heldNote: t('earnings.heldNote') } : {}),
    rows,
    ...(rows.length === 0 ? { empty: t('earnings.none') } : {}),
  };
}

export interface StatementLine {
  /** §23.8 — the id Calculation trace takes, so a row can jump straight there. */
  readonly lineId: string;
  readonly period: string;
  readonly brand: string;
  /** §33.4a — in words. "REVSHARE" is our vocabulary, not the affiliate's. */
  readonly kind: string;
  readonly amount: FigureView;
  /** §33.4a — the rules that produced this number, for the affiliate to cite. */
  readonly because: readonly string[];
}

export interface StatementView {
  readonly updated: string;
  readonly lines: readonly StatementLine[];
  /** §20.3a — where the revenue figure came from. */
  readonly provenance: string;
  /**
   * §27.6 — "The affiliate's statement MUST show: opening carryover, period
   * NGR, closing carryover, and the amount recovered this period. A deficit an
   * affiliate cannot see is a deficit they will dispute."
   *
   * Absent when nothing has ever carried, because a row of zeroes on a deal
   * with no carryover policy invites the question of what deficit is meant.
   */
  readonly carryover?: {
    readonly opening: string;
    readonly movement: string;
    readonly closing: string;
    readonly explanation: string;
  };
}

/** §21.7 — what the reader asked for, what they chose, and what they inherit. */
export interface CurrencyPreference {
  readonly view: CurrencyView;
  readonly chosen: string | null;
  readonly operatorDefault: string;
}

/**
 * Why a line is on the statement, in a sentence.
 *
 * This was `rule_ids.map((id) => `§${id}`)` — the affiliate's "Because" column
 * was a list of rule numbers from a document they have never seen. It also
 * slipped past the check that removed every other citation from the portals,
 * because it is built at runtime: the bundle contains a backtick and a
 * variable, not the string the reader ends up with.
 */
function explainKind(row: {
  readonly type: string;
  /** Read only for a CARRYOVER line, whose sign is the direction the deficit moved. */
  readonly amount_minor?: string | null;
  readonly adjustment_reason_code?: string | null;
  readonly adjustment_justification?: string | null;
  readonly clawback_ground?: string | null;
  readonly bonus_payout_window_days?: number | null;
  readonly bonus_payout_is_percentage?: boolean | null;
  readonly bonus_payout_basis?: string | null;
  readonly bonus_payout_rate_bps?: number | null;
}): string {
  switch (row.type) {
    case 'CPA':
    case 'RECURRING_CPA':
    case 'HYBRID_CPA':
      return t('line.acquisition');
    case 'CPL':
      return t('line.lead');
    case 'REVSHARE':
    case 'HYBRID_RS':
      return t('line.revshare');
    case 'SUB_OVERRIDE':
      return t('line.subOverride');
    case 'FLAT':
    case 'FEE':
      return t('line.flat');
    case 'ADJUSTMENT':
      // §28.5 — meaningful to the affiliate means the actual reason, not a
      // placeholder claiming one was recorded.
      return row.adjustment_reason_code && row.adjustment_justification
        ? `${row.adjustment_reason_code.replaceAll('_', ' ').toLowerCase()} — ` +
            row.adjustment_justification
        : t('line.adjustment');
    case 'CLAWBACK':
      return row.clawback_ground
        ? t('line.clawback.ground', {
            ground: row.clawback_ground.replaceAll('_', ' ').toLowerCase(),
          })
        : t('line.clawback');
    case 'CARRYOVER': {
      // §27.6 — which way the deficit moved is the sign of the movement.
      const sign = amountSign(row.amount_minor);
      return sign < 0 ? t('line.carryover.deficit') : sign > 0 ? t('line.carryover.repaid') : t('line.carryover');
    }
    case 'BONUS_PAYOUT': {
      // §24.9f — its own line, and it says what qualified it: the window, and
      // in percentage mode the basis and rate. Never blended into the
      // acquisition or revenue-share line it rides beside.
      const value = row.bonus_payout_is_percentage
        ? t('line.bonusPayout.percentage', {
            rate: ((row.bonus_payout_rate_bps ?? 0) / 100).toFixed(2),
            basis: bonusBasisWords(row.bonus_payout_basis) ?? t('bonusBasis.fallback'),
          })
        : t('line.bonusPayout.flat');
      return t(
        (row.bonus_payout_window_days ?? 90) === 1 ? 'line.bonusPayout.one' : 'line.bonusPayout.other',
        { days: row.bonus_payout_window_days ?? 90, value },
      );
    }
    default:
      return t('line.unknown');
  }
}

/** §33.4a — the line types, in the affiliate's language rather than ours. */
const LINE_KINDS: ReadonlySet<string> = new Set([
  'CPA',
  'RECURRING_CPA',
  'CPL',
  'REVSHARE',
  'HYBRID_CPA',
  'HYBRID_RS',
  'SUB_OVERRIDE',
  'FLAT',
  'FEE',
  'ADJUSTMENT',
  // §20.8 — a clawback is money being taken back, and the word for that is
  // "clawback" in every language it is written in. Softening it to "adjustment"
  // is how a negative line becomes a dispute nobody can explain the origin of.
  'CLAWBACK',
  // §27.6 — the affiliate has to be able to see a deficit, or they will
  // dispute it. One label for both directions would leave them working out
  // from the sign whether this month made things better or worse.
  'CARRYOVER',
  // §24.9f — its own label, distinct from CPA/RevShare and never blended
  // into either; this is new money on top of them, not the §20.5 deduction.
  'BONUS_PAYOUT',
  // 0085 — a campaign goal firing. Payable since that migration and absent
  // from this set the whole time, so the screen printed the code.
  'GOAL',
]);

function describeKind(type: string, amountMinor?: string | null): string {
  // §27.6 — a carryover line moves the deficit one way or the other, and its
  // sign says which: below zero the deficit deepened and carries forward,
  // above zero this period took some of it back. One label for both left the
  // affiliate reading "+400 Carried forward" as a debt that had grown. A zero
  // or unreadable amount keeps the undirected word.
  if (type === 'CARRYOVER') {
    const sign = amountSign(amountMinor);
    if (sign < 0) return t('kind.CARRYOVER.deficit');
    if (sign > 0) return t('kind.CARRYOVER.repaid');
  }
  // An unmapped type stays its own code, in every language: a kind this build
  // has never seen is not one to invent words for.
  return LINE_KINDS.has(type) ? t(`kind.${type}` as AffiliateMessageKey) : type;
}

/** The statuses `lineState.*` names, the same codes statement-pdf.ts LINE_STATE names. */
const LINE_STATES: ReadonlySet<string> = new Set([
  'PENDING', 'EARNED', 'HELD', 'PAYABLE', 'PAID', 'REVERSED', 'VOID',
]);

/**
 * §30.10, §31.1a — a line's status in words. PAID reads "Marked paid": it is
 * the operator's claim, which the statement PDF already said and this screen
 * printed as the raw code. An unknown status stays its code, as for a kind.
 */
export function describeLineState(state: string): string {
  return LINE_STATES.has(state) ? t(`lineState.${state}` as AffiliateMessageKey) : state;
}

/** -1, 0 or 1 for a minor-unit amount string; 0 when it is absent or not an integer. */
function amountSign(amountMinor: string | null | undefined): -1 | 0 | 1 {
  const s = (amountMinor ?? '').trim();
  if (!/^-?\d+$/.test(s)) return 0;
  const n = BigInt(s);
  return n < 0n ? -1 : n > 0n ? 1 : 0;
}

/**
 * The commission statement.
 *
 * Two rules shape it.
 *
 * §20.3a is the reason for `provenance`: the brand reports its own revenue and
 * the platform never derives it. An affiliate who believes the platform
 * calculated their GGR will dispute the platform's arithmetic; one who knows it
 * came from the operator disputes it with the operator, which is the only place
 * it can actually be corrected.
 *
 * §39.4 is the reason each line is judged on **its own period's** lock state
 * rather than the envelope's. The envelope answers "can anything in this range
 * still move", which is right for a total and wrong for a line: a range
 * covering a locked February and an open March would mark February provisional
 * too, and a screen where every line is always flagged teaches the affiliate to
 * stop reading the flag.
 */
export function statement(
  report: Envelope<
    {
      line_id: string;
      period_label: string;
      period_state: string;
      brand_name: string;
      type: string;
      amount_minor: string;
      currency: string;
      rule_ids: readonly string[];
      adjustment_reason_code?: string | null;
      adjustment_justification?: string | null;
      clawback_ground?: string | null;
      /** §24.9f — only on a bonus payout line. */
      bonus_payout_window_days?: number | null;
      bonus_payout_is_percentage?: boolean | null;
      bonus_payout_basis?: string | null;
      bonus_payout_rate_bps?: number | null;
    }[]
  >,
  now: Date,
): StatementView {
  /*
   * §21.7f — no display conversion here, and none offered. These are the
   * per-line figures a statement and an invoice are built from, and the CSV
   * beside them is what an affiliate reconciles a month against; restating
   * them is the failure §21.7f names, reached one step earlier. The API does
   * not accept a display currency on this route either.
   */
  return {
    updated: freshness(report.data_as_of, now),
    lines: report.data.map((row) => {
      // §39.4 — "locked, not merely closed". A CLOSED period still recomputes
      // (§22.7a), so closed is not final and must not read as final.
      const locked = row.period_state === 'LOCKED';
      return {
        lineId: row.line_id,
        period: row.period_label,
        brand: row.brand_name,
        kind: describeKind(row.type, row.amount_minor),
        amount: figure(
          describeKind(row.type, row.amount_minor),
          formatMoney({ amount_minor: row.amount_minor, currency: row.currency }),
          locked
            ? { is_provisional: false }
            : {
                is_provisional: true,
                ...(report.provisional_because
                  ? { provisional_because: report.provisional_because }
                  : {}),
              },
        ),
        // §33.4a — "our system says so" is not an acceptable answer, and neither
        // is a rule number the affiliate cannot look up. The line says what it
        // is; that is what "because" has to mean here.
        because: [explainKind(row)],
      };
    }),
    provenance: t('statement.provenance'),
  };
}

export interface PaymentsView {
  readonly rows: readonly PaymentView[];
  /** §31.1 — said once, plainly, on the screen that is about money. */
  readonly disclaimer: string;
}

export function payments(rows: Parameters<typeof paymentRow>[0][]): PaymentsView {
  return {
    rows: rows.map(paymentRow),
    disclaimer: t('payments.disclaimer'),
  };
}

/**
 * Per-player reporting, and what to show when the operator has turned it off.
 *
 * E27 made cohort-level the default with per-player off, so a refusal here is
 * the ordinary case. Rendering it as an error would teach affiliates that the
 * portal is broken; rendering it as a setting tells them the truth and gives
 * them someone to ask.
 */
export interface CohortLine {
  readonly month: string;
  /** null on a withheld row (§17.8b): the screen shows `withheld` instead. */
  readonly players: number | null;
  readonly ftds: number | null;
  /** "Insufficient cohort size", on a row under the brand's minimum. */
  readonly withheld?: string;
  readonly deposits: string;
  readonly ggr: string;
  readonly withdrawals: string;
}

export type PlayersView =
  | { readonly kind: 'players'; readonly updated: string; readonly rows: readonly Record<string, unknown>[] }
  | {
      readonly kind: 'cohorts';
      readonly reason: string;
      readonly reference?: string;
      readonly updated: string;
      readonly rows: readonly CohortLine[];
      /**
       * §21.7b — the rate, its source, its `as_of` and the rate policy behind
       * the money columns, said once for the table rather than once in every
       * cell. Absent when nothing was converted.
       */
      readonly conversions?: ConversionsView;
      /** §21.7 — which of the two views this is, and the way to the other. */
      readonly currencyView?: CurrencyViewControl;
      /**
       * §21.7b — what the reader sees beside a converted number in this
       * table's money columns. One mark for the table, because every money
       * column here converts the same way.
       */
      readonly convertedMark?: string;
      readonly convertedMixed?: boolean;
    }
  | { readonly kind: 'unavailable'; readonly reason: string; readonly reference?: string };

/**
 * §17.8, E27 — the cohort view, which is what the affiliate can have.
 *
 * Answering "per-player is off" and stopping there is technically true and
 * useless: the affiliate came to this screen for player numbers and leaves with
 * a sentence. The refusal and the alternative belong on the same screen.
 */
/**
 * §4.7 — the one refusal that means "per-player reporting is switched off".
 *
 * `playerReport` (reporting.ts) answers 404 for that, and says why it chose
 * the status: "a 403 tells the affiliate that per-player reporting exists and
 * this operator has not bought it, which is the operator's configuration and
 * not the affiliate's business."
 *
 * The console fell back to the cohort table on any ApiClientError at all, so a
 * 500, a 429 or a permission refusal each reached the affiliate as "Your
 * operator reports at cohort level rather than per player." A server fault
 * read as a settled product decision — reassuring, wrong, and not something
 * anybody would think to report.
 */
export function readsAsPerPlayerOff(error: unknown): error is ApiClientError {
  return isApiClientError(error) && error.status === 404;
}

export function cohorts(
  report: Envelope<
    {
      cohort_month: string;
      players: number | null;
      ftds: number | null;
      deposits_minor: string | null;
      withdrawals_minor: string | null;
      ggr_minor: string | null;
      currency: string;
      pooled?: boolean;
      withheld?: boolean;
    }[]
  >,
  refusal: ApiClientError,
  now: Date,
  preference?: CurrencyPreference,
): PlayersView {
  /*
   * §21.7b — this table's provenance. Every money column comes from the same
   * per-row conversion, so one note covers the table; a cohort table can run
   * to a year of months, and twelve rows times three money columns is
   * thirty-six places one sentence would otherwise be repeated.
   */
  const converted = conversions(report);
  const first = converted?.notes[0];
  const currencyView = preference
    ? currencyViewControl({
        view: preference.view,
        chosen: preference.chosen,
        operatorDefault: preference.operatorDefault,
        conversions: converted,
        convertedForReader: report.converted_for_reader,
      })
    : undefined;

  return {
    kind: 'cohorts',
    reason: t('players.cohortOnly'),
    updated: freshness(report.data_as_of, now),
    ...(converted ? { conversions: converted } : {}),
    ...(currencyView ? { currencyView } : {}),
    ...(first ? { convertedMark: first.mark, convertedMixed: first.mixed } : {}),
    rows: report.data.map((row) => row.withheld
      ? {
        month: cohortMonthLabel(row, report.data),
        players: null, ftds: null,
        deposits: t('cohort.withheld'), ggr: t('cohort.withheld'), withdrawals: t('cohort.withheld'),
        withheld: t('cohort.withheld'),
      }
      : {
      month: cohortMonthLabel(row, report.data),
      players: row.players,
      ftds: row.ftds,
      deposits: formatMoney({ amount_minor: row.deposits_minor, currency: row.currency }),
      ggr: formatMoney({ amount_minor: row.ggr_minor, currency: row.currency }),
      // §17.8a — deposits without withdrawals is half a number. A player who
      // deposited 5,000 and withdrew 4,900 is not the player deposits suggest.
      withdrawals: formatMoney({
        amount_minor: row.withdrawals_minor, currency: row.currency,
      }),
    }),
  };
}

/**
 * §17.8b — a cohort's name on the affiliate's tables. The pooled row of small
 * cohorts reads as "Other (small cohorts)", with its currency when the table
 * holds more than one (it is pooled per currency).
 */
function cohortMonthLabel(
  row: { cohort_month: string; currency: string; pooled?: boolean },
  all: readonly { currency: string }[],
): string {
  if (!row.pooled) return row.cohort_month;
  const several = new Set(all.map((r) => r.currency)).size > 1;
  return several ? `${t('cohort.pooled')} · ${row.currency}` : t('cohort.pooled');
}

export function players(
  result:
    | { ok: true; report: Envelope<{ data: Record<string, unknown>[] }> }
    | { ok: false; error: ApiClientError },
  now: Date,
): PlayersView {
  if (result.ok) {
    return {
      kind: 'players',
      updated: freshness(result.report.data_as_of, now),
      // §17.8a a second time. The API refuses the rest and this refuses them
      // again, because a field that must never reach an affiliate reaching them
      // through a UI that "just displays what the API returned" is still a
      // breach.
      rows: result.report.data.data.map(playerColumns),
    };
  }

  return {
    kind: 'unavailable',
    reason: t('players.cohortOnly'),
  };
}

/* ------------------------------------------------------------------ *
 * §17.8b / §35.3 — the by-cohort report, by month and by age
 * ------------------------------------------------------------------ */

export interface CohortAnalysisLine {
  readonly month: string;
  /**
   * The brand currency this row's figures are in.
   *
   * A cohort month is not unique on its own: the API groups by month *and*
   * currency, so an affiliate working two brands in two currencies gets two
   * rows for March. Keying by month alone shows one currency's numbers under
   * both, which is the one mistake §21.7 exists to prevent.
   */
  readonly currency: string;
  /** null on a withheld row (§17.8b): the screen shows `withheld` instead. */
  readonly players: number | null;
  readonly ftds: number | null;
  readonly withheld?: string;
  readonly ftdRate: string;
  readonly retention: string;
  readonly deposits: string;
  readonly withdrawals: string;
  readonly ggr: string;
  /** What this cohort earned *the affiliate* — the same column an operator reads as cost. */
  readonly earned: string;
}

export interface CohortAgeCell {
  readonly monthsSince: number;
  /** §35.4a — false means the figures below are absences, not zeroes. */
  readonly assessable: boolean;
  readonly retention: string;
  readonly ggrPerPlayer: string;
  readonly depositsPerPlayer: string;
}

export interface CohortAgeLine {
  readonly month: string;
  /** See `CohortAnalysisLine.currency` — month alone is not a unique row. */
  readonly currency: string;
  readonly players: number | null;
  readonly withheld?: string;
  readonly cells: readonly CohortAgeCell[];
}

export interface CohortAnalysisView {
  readonly updated: string;
  readonly checkpointsMonths: readonly number[];
  readonly rows: readonly CohortAnalysisLine[];
  readonly matrix: readonly CohortAgeLine[];
  /** §16.4 — earnings that belong to the range but to no one cohort. */
  readonly outsideCohorts: readonly string[];
  readonly outsideCohortsBecause?: string;
  readonly unavailableBecause?: string;
  readonly empty?: string;
  /** §21.7b — the rates behind the money columns, said once for the table. */
  readonly conversions?: ConversionsView;
  readonly currencyView?: CurrencyViewControl;
  readonly convertedMark?: string;
  readonly convertedMixed?: boolean;
}

interface CohortAnalysisWire {
  readonly checkpoints_months: number[];
  readonly rows: {
    cohort_month: string; players: number | null; ftds: number | null; ftd_rate_pct: number | null;
    pooled?: boolean; withheld?: boolean;
    retained_players: number | null; retention_pct: number | null;
    deposits_minor: string | null; withdrawals_minor: string | null;
    ggr_minor: string | null; commission_minor: string | null;
    roi_ratio: number | null; currency: string;
  }[];
  readonly matrix: {
    cohort_month: string; months_since: number; is_assessable: boolean; players: number | null;
    pooled?: boolean; withheld?: boolean;
    active_players: number | null; retention_pct: number | null;
    cumulative_deposits_per_player_minor: string | null;
    cumulative_ggr_per_player_minor: string | null; currency: string;
  }[];
  readonly unattributed_commission: { amount_minor: string; currency: string }[];
  readonly unattributed_because?: string;
  readonly unavailable_because?: string;
}

/** A percentage the API left undefined stays undefined on the screen. */
const cohortPercent = (value: number | null): string =>
  value === null ? UNAVAILABLE : `${value}%`;

/**
 * §17.8b/§35.3 — the cohort report, read two ways.
 *
 * `cohorts()` above is the refusal path: it exists because per-player was
 * asked for and declined, and it says so. This is the report in its own
 * right, which an affiliate opens on purpose — so it carries no refusal
 * sentence, and the thing it adds is the second table: the same cohorts
 * measured at a common set of ages, where a column compares like with like.
 *
 * `roi_ratio` is deliberately not surfaced here. It divides the operator's
 * revenue by what the affiliate was paid, which is the operator's margin read
 * backwards; the affiliate's own question is what each cohort earned them,
 * and that is the column shown instead.
 */
export function cohortAnalysis(
  report: Envelope<CohortAnalysisWire>,
  now: Date,
  preference?: CurrencyPreference,
): CohortAnalysisView {
  const { checkpoints_months: checkpoints, rows, matrix } = report.data;

  const converted = conversions(report);
  const first = converted?.notes[0];
  const currencyView = preference
    ? currencyViewControl({
        view: preference.view,
        chosen: preference.chosen,
        operatorDefault: preference.operatorDefault,
        conversions: converted,
        convertedForReader: report.converted_for_reader,
      })
    : undefined;

  // One lookup per cohort rather than a scan per cell: twelve cohorts times
  // seven ages is eighty-four scans of the same array otherwise.
  const cellKey = (month: string, currency: string) => `${month}\u0000${currency}`;
  const byCohort = new Map<string, Map<number, CohortAnalysisWire['matrix'][number]>>();
  for (const cell of matrix) {
    const key = cellKey(cell.cohort_month, cell.currency);
    const cells = byCohort.get(key) ?? new Map();
    cells.set(cell.months_since, cell);
    byCohort.set(key, cells);
  }

  return {
    updated: freshness(report.data_as_of, now),
    checkpointsMonths: checkpoints,
    ...(converted ? { conversions: converted } : {}),
    ...(currencyView ? { currencyView } : {}),
    ...(first ? { convertedMark: first.mark, convertedMixed: first.mixed } : {}),
    rows: rows.map((r) => r.withheld
      ? {
        month: cohortMonthLabel(r, rows), currency: r.currency, players: null, ftds: null,
        withheld: t('cohort.withheld'),
        ftdRate: t('cohort.withheld'), retention: t('cohort.withheld'), deposits: t('cohort.withheld'),
        withdrawals: t('cohort.withheld'), ggr: t('cohort.withheld'), earned: t('cohort.withheld'),
      }
      : {
      month: cohortMonthLabel(r, rows),
      currency: r.currency,
      players: r.players,
      ftds: r.ftds,
      ftdRate: cohortPercent(r.ftd_rate_pct),
      retention: cohortPercent(r.retention_pct),
      deposits: formatMoney({ amount_minor: r.deposits_minor, currency: r.currency }),
      withdrawals: formatMoney({ amount_minor: r.withdrawals_minor, currency: r.currency }),
      ggr: formatMoney({ amount_minor: r.ggr_minor, currency: r.currency }),
      earned: formatMoney({ amount_minor: r.commission_minor, currency: r.currency }),
    }),
    // Every cohort gets a cell for every checkpoint, present in the response
    // or not: a ragged row reads as "this cohort was not measured at 6
    // months" when what happened is that nobody joined.
    matrix: rows.map((r) => {
      const cells = byCohort.get(cellKey(r.cohort_month, r.currency));
      return {
        month: cohortMonthLabel(r, rows),
        currency: r.currency,
        players: r.players,
        ...(r.withheld ? { withheld: t('cohort.withheld') } : {}),
        cells: checkpoints.map((monthsSince) => {
          const cell = cells?.get(monthsSince);
          // §17.8b — said in the cell itself, not left as a dash.
          if (r.withheld) {
            const words = t('cohort.withheld');
            return { monthsSince, assessable: true, retention: words, ggrPerPlayer: words, depositsPerPlayer: words };
          }
          return {
            monthsSince,
            assessable: cell?.is_assessable ?? false,
            retention: cell ? cohortPercent(cell.retention_pct) : UNAVAILABLE,
            ggrPerPlayer: formatMoney({
              amount_minor: cell?.cumulative_ggr_per_player_minor ?? null,
              currency: cell?.currency ?? r.currency,
            }),
            depositsPerPlayer: formatMoney({
              amount_minor: cell?.cumulative_deposits_per_player_minor ?? null,
              currency: cell?.currency ?? r.currency,
            }),
          };
        }),
      };
    }),
    outsideCohorts: report.data.unattributed_commission.map((u) =>
      formatMoney({ amount_minor: u.amount_minor, currency: u.currency }),
    ),
    ...(report.data.unattributed_because
      ? { outsideCohortsBecause: report.data.unattributed_because }
      : {}),
    ...(report.data.unavailable_because
      ? { unavailableBecause: report.data.unavailable_because }
      : {}),
    ...(rows.length === 0 ? { empty: t('players.cohorts.empty') } : {}),
  };
}

export interface LinksView {
  readonly rows: readonly LinkRow[];
  /** §11.1a — why there is nothing recognisable in the URL. */
  readonly opacity: string;
}

/**
 * The links screen.
 *
 * §11.1a is stated rather than assumed. An affiliate who cannot find their own
 * id anywhere in the URL routinely concludes the link is not tracking them, and
 * the support conversation that follows is about a rule working correctly.
 */
export function links(rows: Parameters<typeof linkRow>[0][]): LinksView {
  return {
    rows: rows.map(linkRow),
    opacity: t('links.opacity'),
  };
}

/**
 * §11.2a / §12.5c / §39.6 — one row per link, clicks and exclusions together.
 *
 * funnel() answers "how many clicks today" and the dimension performance
 * screen answers "how many under this label" — neither answers "which link".
 * A source that reads fine in aggregate can still be one placement quietly
 * rejecting almost everything it sends, and this is the only screen that
 * names the placement rather than a total it's folded into.
 */
export interface LinkReportRow {
  readonly link_id: string;
  readonly campaign: string;
  readonly created: string;
  readonly counted: number;
  readonly excluded: number;
  readonly excludedNote?: string;
}

export interface LinkReportView {
  readonly updated: string;
  readonly rows: readonly LinkReportRow[];
}

export function linkReport(
  report: Envelope<
    {
      link_id: string;
      campaign_name: string;
      brand_name: string;
      created: string;
      clicks: number;
      bot_clicks: number;
      duplicate_clicks: number;
      geo_blocked_clicks: number;
      os_blocked_clicks: number;
      device_blocked_clicks: number;
      targeting_blocked_clicks: number;
    }[]
  >,
  now: Date,
): LinkReportView {
  return {
    updated: freshness(report.data_as_of, now),
    rows: report.data.map((row) => {
      const excluded =
        row.bot_clicks + row.duplicate_clicks + row.geo_blocked_clicks +
        row.os_blocked_clicks + row.device_blocked_clicks + (row.targeting_blocked_clicks ?? 0);

      const causes: string[] = [];
      if (row.bot_clicks > 0) causes.push(`${row.bot_clicks} automated`);
      if (row.duplicate_clicks > 0) causes.push(`${row.duplicate_clicks} duplicate`);
      if (row.geo_blocked_clicks > 0) causes.push(`${row.geo_blocked_clicks} geo-blocked`);
      if (row.os_blocked_clicks > 0) causes.push(`${row.os_blocked_clicks} OS-blocked`);
      if (row.device_blocked_clicks > 0) causes.push(`${row.device_blocked_clicks} device-blocked`);
      if (row.targeting_blocked_clicks > 0) causes.push(`${row.targeting_blocked_clicks} outside targeting`);

      return {
        link_id: row.link_id,
        campaign: `${row.brand_name} · ${row.campaign_name}`,
        created: formatDate(row.created),
        counted: row.clicks,
        excluded,
        ...(causes.length > 0 ? { excludedNote: causes.join(', ') } : {}),
      };
    }),
  };
}

export interface TermsLine {
  /** The deal these terms are on — the key the history panel reads. Empty for an older response. */
  readonly dealId: string;
  /**
   * §7.12d — PERSONAL: a deal with this affiliate. CAMPAIGN: the campaign's
   * standard payout, listed because it is what prices them on a campaign
   * they take part in. It has no per-affiliate history to open.
   */
  readonly source: 'PERSONAL' | 'CAMPAIGN';
  /** The campaign these terms are for, or null for the default deal. */
  readonly campaignName: string | null;
  /** Present only on a campaign's payout: why it pays this affiliate, in words. */
  readonly sourceNote: string | null;
  readonly brand: string;
  /** 0307 — the panel heading: the brand, and the deal's name where it has one. */
  readonly title: string;
  /**
   * §7.12 — whose terms these are: one campaign's alone, or the default deal
   * on the brand. Two panels headed with the same brand are otherwise the
   * same panel twice, and the difference is which traffic each one pays for.
   */
  readonly scope: string;
  readonly forCampaign: boolean;
  readonly summary: string;
  /** Named conditions, in the affiliate's words. Empty when there are none. */
  readonly conditions: readonly { label: string; value: string }[];
  /** §20.4a — numbered, because the order is part of the deal. */
  readonly deductions: readonly string[];
  readonly since: string;
  readonly accepted: string;
  /**
   * §7.8a/§31.2c — payout frequency and settlement terms, with the level each
   * resolved from. Undefined only for a response built before A4 carried it
   * (an older fixture) — a live response always has one.
   */
  readonly payout?: PayoutLine;
  /** §7.8b/§27.2b — the negative-balance policy in force, in words, with its source. */
  readonly negativeBalance?: NegativeBalanceLine;
  /** §7.8c/§20.4a — the NGR/GGR formula as one line, or the turnover wording (§20.10b). Always present: `revenue_basis` always is. */
  readonly formula: FormulaLine;
  /** §7.8d/§20.6a — always present, and reads "0.00%" when the deal carries none. */
  readonly adminFee: AdminFeeLine;
  /** §7.8e/§14.6b — the clamped cookie lifetime the edge actually sets. */
  readonly cookieLifetime?: TermsRow;
  /** §7.8e — the attribution window (click to registration), always a separate figure from the cookie lifetime above. */
  readonly attributionWindow?: TermsRow;
  /** §8.7 (Addendum A)/§30.4d — the minimum payout: general figure, this affiliate's own method, and which applies. */
  readonly minimumPayout?: MinimumPayoutLine;
  /** §7.14/§7.8f/§7.14c — grouped by stated value; always present, defaulting to every axis Not Stated when the version states nothing. */
  readonly trafficRules: TrafficRulesView;
  /** §24.7b/F2 (deal-shape-plan.md) — the CPA price list, plus what pays everywhere a row does not name. Present only when the version states at least one row. */
  readonly cpaPriceList?: CpaPriceListView;
  /** §24.7/F2 — the deal-wide CPA-by-QFTD-count table. Present only when the version carries one. */
  readonly cpaTierBands?: readonly CpaTierBandLine[];
  /** §26.3/F2 — the deal-wide RevShare tier table. Present only when the version carries one. */
  readonly revshareTierBands?: readonly TierBandLine[];
  /** §26.3a — one table per product that has its own, beside the deal-wide one and `productRates` below. */
  readonly productTierTables?: readonly ProductTierTableView[];
  /** §26.3/§26.3a — the one metric every tier table on this version is keyed on; `null` when the version carries no such table. */
  readonly tierKeyMetric?: string | null;
  /** §26.3a — server-composed, in words already (no rule number reaches this screen): present only when the key metric is QFTD_COUNT and at least one product has its own table. */
  readonly productTierQftdNote?: string | null;
  /** §10.5/§25.5 — flat per-product overrides, beside the tier tables above. */
  readonly productRates?: readonly ProductRateLine[];
  /** §7.8e/§14.6d — the CPA component's own attribution window, or the deal's own figure — a third figure, never collapsed into cookie lifetime or the deal-wide attribution window above. */
  readonly cpaWindow?: ComponentWindowLine;
  /** §7.8e/§14.6d — same, for RevShare. */
  readonly revshareWindow?: ComponentWindowLine;
}

export interface TermsScreen {
  readonly lines: readonly TermsLine[];
  /** Where the rules live, said once, because this is the question asked. */
  readonly whereRulesLive: string;
  /**
   * Where the numbers in the stack actually come from.
   *
   * The stack already said *what* is subtracted and in what order. It never
   * said who supplies each figure, so every line read as something we compute
   * — and a deduction you believe your counterparty invented is a dispute.
   * The honest answer is that we invent none of it: the operator reports the
   * revenue and the costs, and the deal decides which of those costs apply.
   */
  readonly whereNumbersComeFrom: string;
  /**
   * §6.16/§6.16a — present only when the account is an agency: how many
   * sub-accounts it has, and that every one of them is paid through this one
   * statement, composed here (not sent as server prose) so it reads in the
   * affiliate's own language.
   */
  readonly agencyNote: string | null;
  /**
   * §7.12d — brand by brand, then campaign by campaign: the terms on that
   * brand (the default deal first) and, for every campaign the affiliate
   * takes part in, which of those terms pays them there. Empty when there
   * are neither terms nor campaigns — the screen's empty state.
   */
  readonly groups: readonly TermsBrandGroup[];
}

export interface TermsCampaignRow {
  readonly campaign: string;
  /** Which terms pay here, in words — never the terms again. */
  readonly paidOn: string;
  /** False when nothing prices the affiliate on this campaign yet. */
  readonly priced: boolean;
  /**
   * Set when the affiliate has left the campaign but brought players through
   * it: the engine still prices those players there, so it stays listed.
   */
  readonly leftNote: string | null;
}

export interface TermsBrandGroup {
  readonly brand: string;
  /** Empty when the affiliate takes part in no campaign on this brand. */
  readonly campaigns: readonly TermsCampaignRow[];
  readonly lines: readonly TermsLine[];
}

/** One campaign the affiliate takes part in, as `GET /v1/me/terms` resolves it. */
export interface TermsCampaignInput {
  readonly campaign_id: string;
  readonly campaign_name: string;
  readonly brand: string;
  /** Absent from an older response, which listed only live participations. */
  readonly participating?: boolean;
  readonly source: 'CAMPAIGN' | 'CUSTOM' | 'DEFAULT' | null;
  readonly deal_id: string | null;
}

/**
 * §7.12d — the screen's grouping. A campaign is "priced" only when the deal
 * the server resolved for it is one of the terms listed: a default deal with
 * no version in force resolves to a deal and still pays nothing.
 */
function termsGroups(lines: readonly TermsLine[], campaigns: readonly TermsCampaignInput[]): TermsBrandGroup[] {
  const brands = [...new Set([...lines.map((l) => l.brand), ...campaigns.map((c) => c.brand)])]
    .sort((a, b) => a.localeCompare(b));
  const listed = new Set(lines.map((l) => l.dealId));
  return brands.map((brand) => ({
    brand,
    campaigns: campaigns
      .filter((c) => c.brand === brand)
      .map((c) => {
        const priced = c.source !== null && c.deal_id !== null && listed.has(c.deal_id);
        return {
          campaign: c.campaign_name,
          priced,
          leftNote: c.participating === false ? t('terms.campaign.left' as AffiliateMessageKey) : null,
          paidOn: priced
            ? t(`source.${c.source}` as AffiliateMessageKey, { brand })
            : t('terms.paidOn.none' as AffiliateMessageKey),
        };
      }),
    lines: lines
      .filter((l) => l.brand === brand)
      .slice()
      // The default deal first, then each campaign's terms by its name.
      .sort((a, b) =>
        a.campaignName === null ? (b.campaignName === null ? 0 : -1)
          : b.campaignName === null ? 1 : a.campaignName.localeCompare(b.campaignName)),
  }));
}

/**
 * The terms screen.
 *
 * §7.8, §24.2b, §26.2a: the affiliate can see the money and, until this screen,
 * could not see the rule that produced it — which is the shape of every
 * commission dispute that cannot be settled.
 *
 * §20.4a is why the deduction stack is numbered rather than listed: "`GGR −
 * fee% − bonus` and `GGR − bonus − fee%` produce different numbers when the fee
 * is percentage-based." An affiliate who reads it as a set reproduces it in a
 * spreadsheet in the wrong order and disputes the difference.
 */
export function termsScreen(
  items: Parameters<typeof termsLine>[0][],
  /** §6.16 — absent/null for a DIRECT affiliate, which is the default and the common case. */
  agency?: { sub_account_count: number } | null,
  /** §7.12d — every campaign the affiliate takes part in; absent from an older response. */
  campaigns?: readonly TermsCampaignInput[],
): TermsScreen {
  const lines = items.map(termsLine);
  return {
    lines,
    groups: termsGroups(lines, campaigns ?? []),
    whereNumbersComeFrom: t('terms.whereNumbersComeFrom'),
    whereRulesLive: t('terms.whereRulesLive'),
    agencyNote: agency
      ? t(
          agency.sub_account_count === 1
            ? 'terms.agencyNote.one' as AffiliateMessageKey
            : 'terms.agencyNote.other' as AffiliateMessageKey,
          { n: agency.sub_account_count },
        )
      : null,
  };
}

/**
 * §26.2a — CPA-recoupable outstanding per player, formatted for the screen.
 * Raw minor units never reach JSX (§1.3a).
 */
export function recoupmentRows(
  items: readonly {
    player_ref: string;
    brand: string;
    cpa_paid_minor: string;
    recouped_minor: string;
    outstanding_minor: string;
    currency: string;
  }[],
): readonly {
  player: string;
  brand: string;
  cpaPaid: string;
  recouped: string;
  outstanding: string;
}[] {
  return items.map((row) => ({
    player: row.player_ref,
    brand: row.brand,
    cpaPaid: formatMoney({ amount_minor: row.cpa_paid_minor, currency: row.currency }),
    recouped: formatMoney({ amount_minor: row.recouped_minor, currency: row.currency }),
    outstanding: formatMoney({
      amount_minor: row.outstanding_minor,
      currency: row.currency,
    }),
  }));
}

/**
 * §6.16 — commission grouped by sub-account, formatted for the screen. Raw
 * minor units never reach JSX (§1.3a): the caller renders `row.amount`, not
 * `group.amount_minor`.
 *
 * `label: null` is the "not filed under a sub-account" bucket D8 decided on
 * for an affiliate-level line (flat fee, CPC, CPM, spend share) and for a
 * player-level line whose own link named no sub-account — the console
 * supplies its own translated label for it (`t('subAccounts.report.
 * unfiled')`), rather than this reading English prose off the wire.
 */
export interface SubAccountReportRow {
  readonly key: string;
  readonly label: string | null;
  readonly lineCount: number;
  readonly amount: string;
}

export function subAccountReportRows(
  groups: readonly {
    sub_account_id: string | null;
    sub_account_code: string | null;
    sub_account_name: string | null;
    archived: boolean;
    currency: string;
    line_count: number;
    amount_minor: string;
  }[],
): readonly SubAccountReportRow[] {
  return groups.map((g, i) => ({
    key: g.sub_account_id ?? `unfiled-${i}`,
    label: g.sub_account_id ? `${g.sub_account_code} — ${g.sub_account_name}` : null,
    lineCount: g.line_count,
    amount: formatMoney({ amount_minor: g.amount_minor, currency: g.currency }),
  }));
}

/** §24.2 — the ten criteria, in words rather than the ref table's short codes. */
const CRITERIA: ReadonlySet<string> = new Set([
  'DEPOSIT_SETTLED',
  'MIN_DEPOSIT',
  'KYC_APPROVED',
  'MIN_TURNOVER',
  'MIN_BETS',
  'MIN_DAYS_ACTIVE',
  'NOT_REVERSED',
  'NO_FRAUD_FLAG',
  'GEO_ELIGIBLE',
  'NOT_SELF_EXCLUDED',
]);

function criterionWords(code: string): string {
  return CRITERIA.has(code) ? t(`criterion.${code}` as AffiliateMessageKey) : code;
}

/**
 * The operator's platform has its own word for this, or it does not.
 *
 * Migration 0173 lets a platform rename a deduction item or a qualification
 * criterion, and the API sends the curated word only where somebody set one —
 * null otherwise. So a rename reaches the affiliate reading their own terms in
 * Spanish as the renamed word, and everything nobody renamed stays translated.
 * Both sides of a deal then call the same thing by the same name, which is the
 * point of being able to rename it at all.
 *
 * The commission type and the revenue basis deliberately do not go through
 * this. §2.8b keeps an identifier an identifier, and the comment on the deal
 * history table says so in as many words: those stay the code, in every
 * language, on both consoles.
 */
function curatedOr(label: string | null | undefined, fallback: string): string {
  return label && label.trim() !== '' ? label : fallback;
}

/** §24.9c — the basis, in words rather than a code. */
const BONUS_PAYOUT_BASES: ReadonlySet<string> = new Set(['DEPOSIT', 'COMMISSION', 'NGR']);

/**
 * The words for a bonus basis, or `null` when there are none for this code.
 *
 * Null rather than a fallback baked in here, because the two callers want
 * different ones and always did: the statement line falls back to a phrase that
 * reads inside a sentence ("…% of the basis in your terms"), and the Terms
 * screen falls back to the raw code, which is the honest thing to show in a
 * table of conditions.
 */
function bonusBasisWords(code: string | null | undefined): string | null {
  return code && BONUS_PAYOUT_BASES.has(code)
    ? t(`bonusBasis.${code}` as AffiliateMessageKey)
    : null;
}

/**
 * §0.5b, §24.5b — the hold and where it came from, in words: the deal's own
 * figure, one the brand set, the operator's standard, or the platform's.
 */
function holdWordsKey(
  source: 'DEAL' | 'BRAND' | 'OPERATOR' | 'PLATFORM' | undefined,
  isDefault: boolean | undefined,
  // A hold of one day is an ordinary setting, and "1 days after qualifying"
  // on a screen about somebody's income reads as a platform that cannot count.
  days: number,
):
  | 'terms.held.value.one' | 'terms.held.value.other'
  | 'terms.held.brand.one' | 'terms.held.brand.other'
  | 'terms.held.operator.one' | 'terms.held.operator.other'
  | 'terms.held.default.one' | 'terms.held.default.other' {
  const n = days === 1 ? 'one' : 'other';
  if (source === 'BRAND') return `terms.held.brand.${n}` as const;
  if (source === 'OPERATOR') return `terms.held.operator.${n}` as const;
  if (source === 'PLATFORM' || (source === undefined && isDefault)) return `terms.held.default.${n}` as const;
  return `terms.held.value.${n}` as const;
}

/* ------------------------------------------------------------------ *
 * §7.8 — the commercial-terms block: payout, negative balance, the NGR
 * formula, the administrative fee, the two tracking figures, the minimum
 * payout, and the traffic rules grouped by their stated value.
 *
 * Every one of these is a §0.5b-resolved setting: a code (or a small set of
 * fields) plus the level it was resolved from, exactly the shape
 * `cpa_hold_period_source` above already has. The API sends the code; the
 * screen supplies the words, the same division of labour the rest of this
 * file keeps everywhere else (§2.8b — an identifier stays an identifier on
 * the wire, and is translated only where a person reads it).
 * ------------------------------------------------------------------ */

const percent = (bps: number) => `${(bps / 100).toFixed(2)}%`;

export interface TermsRow {
  readonly label: string;
  readonly value: string;
}

/** §0.5b — the four levels a resolved commercial-terms figure may say it came from. */
const CONFIG_SOURCES: ReadonlySet<string> = new Set(['DEAL', 'BRAND', 'OPERATOR', 'PLATFORM']);

/** The source of a resolved setting, in words. An unknown source stays its code. */
function configSourceLabel(source: string): string {
  return CONFIG_SOURCES.has(source)
    ? t(`terms.source.${source}` as AffiliateMessageKey)
    : source;
}

/** §31.2c — the eleven cadences the registry validates against (Addendum A §8.4). */
const PAYOUT_FREQUENCIES: ReadonlySet<string> = new Set([
  'DAILY', 'TWICE_A_WEEK', 'WEEKLY', 'ON_REQUEST', 'TWICE_A_MONTH', 'THRICE_A_MONTH',
  'MONTHLY', 'ONCE_IN_SIX_WEEKS', 'ONCE_IN_TWO_MONTHS', 'FORTNIGHTLY', 'QUARTERLY',
]);

/** §7.8a — payout frequency, in words. An unknown code (a future cadence this build has not shipped words for) stays its code rather than showing nothing. */
function payoutFrequencyLabel(code: string): string {
  return PAYOUT_FREQUENCIES.has(code)
    ? t(`terms.payout.frequency.${code}` as AffiliateMessageKey)
    : code;
}

export interface PayoutLine {
  readonly frequency: string;
  readonly settlement: string;
  readonly source: string;
}

/** §7.8a/§31.2c — payout frequency and settlement terms, in words, with the level each resolved from. */
function payoutLine(payout: { frequency: string; settlement_terms_days: number; source: string }): PayoutLine {
  const days = payout.settlement_terms_days;
  return {
    frequency: payoutFrequencyLabel(payout.frequency),
    settlement: t(
      days === 1 ? 'terms.payout.settlement.one' as AffiliateMessageKey : 'terms.payout.settlement.other' as AffiliateMessageKey,
      { days },
    ),
    source: configSourceLabel(payout.source),
  };
}

/** §27.2/§27.2c — the five policy codes `commission.negative_carryover` validates against. */
const NEGATIVE_BALANCE_POLICIES: ReadonlySet<string> = new Set([
  'NONE', 'CARRY_FORWARD', 'RESET_ANNUALLY', 'PARTIAL_RESET', 'RESET_ON_REQUEST',
]);

export interface NegativeBalanceLine {
  /** The code, kept for anything that needs it programmatically — never shown. */
  readonly policy: string;
  /** §27.2b/§7.8b — the words, never the code alone. */
  readonly value: string;
  readonly source: string;
}

/**
 * §7.8b/§27.2b — "the policy in force MUST be stated ... in words ... not the
 * internal policy code." The vocabulary the reference directory uses (Addendum
 * A §8.2/§8.4): Reset · Reset upon request · Partially reset (with the share)
 * · Carried over. A4 already composes this server-side in English
 * (`negativeBalanceWords`, `apps/api-core/src/terms.ts`) — this is the same
 * mapping, translated, for a console that ships in more than one language.
 */
function negativeBalanceLine(nb: { policy: string; carry_bps: number; source: string }): NegativeBalanceLine {
  const value = !NEGATIVE_BALANCE_POLICIES.has(nb.policy)
    ? nb.policy
    : nb.policy === 'PARTIAL_RESET'
      ? t('terms.negativeBalance.PARTIAL_RESET' as AffiliateMessageKey, { pct: (nb.carry_bps / 100).toFixed(2) })
      : t(`terms.negativeBalance.${nb.policy}` as AffiliateMessageKey);
  return { policy: nb.policy, value, source: configSourceLabel(nb.source) };
}

/** §20.4 — the eleven deduction-item codes `ref_deduction_items` validates against (migration 0173). */
const DEDUCTION_ITEMS: ReadonlySet<string> = new Set([
  'BONUS_COST', 'FREE_BET_COST', 'CASHBACK_LOYALTY', 'JACKPOT_CONTRIBUTION', 'GAME_PROVIDER_FEES',
  'PAYMENT_FEES', 'GAMING_DUTY', 'CHARGEBACKS', 'ADMINISTRATIVE_FEE', 'PLATFORM_LICENCE_FEES', 'NEGOTIATED',
]);

/** A deduction item's own word, for the entries nobody has curated a rename for (0173, see `curatedOr`). */
function deductionItemWords(code: string): string {
  return DEDUCTION_ITEMS.has(code) ? t(`deductionItem.${code}` as AffiliateMessageKey) : code;
}

/** The shape `orderedStack` (`apps/api-core/src/terms.ts`) sends per entry — optional here only because the
 *  existing fixture in `screens.test.ts` predates §20.4a's kind/rate/amount fields and this type must still fit it. */
interface DeductionStepInput {
  readonly order: number;
  readonly what: string;
  readonly label: string | null;
  readonly kind?: 'AMOUNT' | 'RATE' | null;
  readonly rate_bps?: number | null;
  readonly amount_minor?: string | null;
  readonly currency?: string | null;
}

/**
 * §20.4a/§7.8c — one stack entry, as it reads inside the composed formula
 * line: a rate reads "2.50% Payment fees", a stored amount reads
 * "€500.00 Platform licence fees" (via `formatMoney`, never formatted here by
 * hand), and an entry with neither — deducted at cost, nothing stored on the
 * deal — reads as its own name alone, e.g. "Bonus cost".
 */
function stackItemPhrase(step: DeductionStepInput): string {
  const words = curatedOr(step.label, deductionItemWords(step.what));
  if (step.kind === 'RATE' && typeof step.rate_bps === 'number') {
    return `${percent(step.rate_bps)} ${words}`;
  }
  if (step.kind === 'AMOUNT' && step.amount_minor !== null && step.amount_minor !== undefined) {
    return `${formatMoney({ amount_minor: step.amount_minor, currency: step.currency ?? '' })} ${words}`;
  }
  return words;
}

export interface FormulaLine {
  /** 'NGR' | 'GGR' | 'TURNOVER', or the raw basis code this build has no formula shape for. */
  readonly basis: string;
  /** The composed line, in the stack's own order. Null where this basis has none (§20.10b, or a basis this build does not compose one for). */
  readonly line: string | null;
  /** §20.10b — set only for `TURNOVER`, where the deduction stack MUST NOT apply. */
  readonly note: string | null;
}

/** §20.4/§7.8c — the two bases the deduction stack actually composes a formula for. */
const FORMULA_BASES: ReadonlySet<string> = new Set(['NGR', 'GGR']);

/**
 * §7.8c/§20.4a — "composed as a single line, in the deduction stack's own
 * order (20.4a) and prefixed with the basis it is applied to (20.10)" — e.g.
 * "NGR = GGR − Bonus cost − 2.50% Payment fees − 5.00% Administrative fee".
 * Sorted by the stack's own `order` field rather than trusted to already be
 * array order, because the order is part of the deal (20.4a) and not an
 * accident of how a caller assembled the array.
 *
 * §20.10b — where the deal pays on turnover, the deduction stack MUST NOT
 * apply (turnover is stakes, before any revenue exists to deduct anything
 * from): no formula line, a dedicated sentence instead.
 */
function formulaLine(revenueBasis: string, stack: readonly DeductionStepInput[]): FormulaLine {
  if (revenueBasis === 'TURNOVER') {
    return { basis: 'TURNOVER', line: null, note: t('terms.formula.turnover' as AffiliateMessageKey) };
  }
  if (!FORMULA_BASES.has(revenueBasis)) {
    return { basis: revenueBasis, line: null, note: null };
  }
  const items = [...stack].sort((a, b) => a.order - b.order).map(stackItemPhrase);
  const named = revenueBasis === 'NGR' ? 'Commissionable NGR' : revenueBasis;
  const line = items.length > 0
    ? `${named} = GGR − ${items.join(' − ')}`
    : `${named} = GGR`;
  return { basis: revenueBasis, line, note: null };
}

export interface AdminFeeLine extends TermsRow {
  readonly enabled: boolean;
}

/**
 * §7.8d/§20.6a — the administrative fee, always its own line: "If enabled, it
 * MUST be disclosed ... as a distinct line with its percentage" and "an
 * absent fee is shown as '0% administrative fee', never omitted as though the
 * question had not been asked." Independent of `formulaLine` above — the fee
 * still appears inline in the formula when the stack carries it (in its own
 * position, like any other item); this is the separate, always-present line
 * 20.6a itself requires.
 */
function adminFeeLine(stack: readonly DeductionStepInput[]): AdminFeeLine {
  const item = stack.find((s) => s.what === 'ADMINISTRATIVE_FEE');
  const bps = item && item.kind === 'RATE' && typeof item.rate_bps === 'number' ? item.rate_bps : 0;
  return { label: t('terms.adminFee' as AffiliateMessageKey), value: percent(bps), enabled: bps > 0 };
}

/** §7.8e/§14.6b — one of the two tracking figures, as its own labelled row. */
function cookieLifetimeLine(days: number): TermsRow {
  return {
    label: t('terms.cookieLifetime' as AffiliateMessageKey),
    value: t(
      days === 1 ? 'terms.cookieLifetime.value.one' as AffiliateMessageKey : 'terms.cookieLifetime.value.other' as AffiliateMessageKey,
      { days },
    ),
  };
}

/** §7.8e/§14.6 — the other one: click to registration, never blended with the cookie's own lifetime. */
function attributionWindowLine(days: number): TermsRow {
  return {
    label: t('terms.attributionWindow' as AffiliateMessageKey),
    value: t(
      days === 1 ? 'terms.attributionWindow.value.one' as AffiliateMessageKey : 'terms.attributionWindow.value.other' as AffiliateMessageKey,
      { days },
    ),
  };
}

export interface MinimumPayoutLine {
  readonly value: string;
  readonly source: string;
  /**
   * §30.4d — this affiliate's own saved payout method, as its own code
   * (§2.8b — an identifier, like `commission_type` elsewhere on this
   * screen), or `null` when none is saved.
   */
  readonly method: string | null;
  /**
   * §30.4d — that method's own minimum, labelled, or `null` when no method
   * is saved or the operator has stated no figure for it — in which case
   * the general figure above is what applies.
   */
  readonly methodRow: TermsRow | null;
  /**
   * §30.4d — the figure that actually governs this affiliate's statements
   * today: the method row's value when there is one, else `value` above —
   * resolved server-side the same way (`terms.ts`'s own
   * `applies_amount_minor`), so this screen never re-applies the fallback
   * and risks disagreeing with it. Always present and its own labelled
   * row, so an affiliate reading only one line of this block still reads
   * the right one.
   */
  readonly applies: TermsRow;
}

/**
 * §8.7 (Addendum A)/§30.4d — the minimum payout: the general figure, this
 * affiliate's own method and its figure where the operator set one, and
 * which of the two actually applies — unambiguous, because it is its own
 * labelled row rather than something the reader has to work out from the
 * other two.
 *
 * `method`/`method_amount_minor`/`method_currency`/`applies_amount_minor`/
 * `applies_currency` are optional here only so the pre-existing fixture in
 * `screens.test.ts` — built before this screen read any of them — still
 * type-checks; a live response always sends all five, and
 * `applies_amount_minor`/`applies_currency` default to the general figure
 * when absent, the same fallback the server itself applies.
 */
function minimumPayoutLine(mp: {
  amount_minor: string;
  currency: string;
  source: string;
  method?: string | null;
  method_amount_minor?: string | null;
  method_currency?: string | null;
  applies_amount_minor?: string;
  applies_currency?: string;
}): MinimumPayoutLine {
  return {
    value: formatMoney({ amount_minor: mp.amount_minor, currency: mp.currency }),
    source: configSourceLabel(mp.source),
    method: mp.method ?? null,
    methodRow:
      mp.method_amount_minor !== null && mp.method_amount_minor !== undefined
        ? {
            label: t('terms.minimumPayout.method' as AffiliateMessageKey),
            value: formatMoney({ amount_minor: mp.method_amount_minor, currency: mp.method_currency ?? mp.currency }),
          }
        : null,
    applies: {
      label: t('terms.minimumPayout.applies' as AffiliateMessageKey),
      value: formatMoney({
        amount_minor: mp.applies_amount_minor ?? mp.amount_minor,
        currency: mp.applies_currency ?? mp.currency,
      }),
    },
  };
}

/* ------------------------------------------------------------------ *
 * F1/F2 (docs/delivery/deal-shape-plan.md) — everything `terms()` derives
 * from `deal_cpa_prices`, `tier_bands` and `product_rates`: the CPA price
 * list and what pays everywhere else, the deal-wide and per-product tier
 * tables, per-product flat overrides, and both component attribution
 * windows. None of the three tables was read by this screen before F1
 * (recorded as the pre-existing gap F2). Every word composed here goes
 * through the catalogue, like the rest of this screen — raw codes
 * (jurisdiction, channel, product, the tier key metric) stay codes, the
 * same treatment §2.8b already gives `commission_type`/`revenue_basis`.
 * ------------------------------------------------------------------ */

export interface CpaPriceRowLine {
  readonly jurisdiction: string;
  readonly channel: string;
  readonly amount: string;
  /**
   * The row's name as one label: the country alone for a row that prices
   * every channel, "Nigeria · SEO" for one that names a channel too. Built
   * here from the codes, not by comparing the words above back against a
   * translation.
   */
  readonly label: string;
}

export interface CpaPriceListView {
  readonly rows: readonly CpaPriceRowLine[];
  /** What pays in every jurisdiction/channel not named by a row above — the same `cpa_amount_minor` figure the deal has always carried. */
  readonly elsewhere: TermsRow;
}

/**
 * §24.7b/F2 — the CPA price list, plus what pays everywhere a row does not
 * name. `undefined` when the version states no rows: the base figure
 * (rendered elsewhere on this screen as "Per qualifying player") already
 * covers every jurisdiction and channel, and an empty price-list section
 * would only repeat that with no new information.
 */
function cpaPriceList(
  rows: readonly { jurisdiction: string; channel: string; amount_minor: string }[],
  cpaAmountMinor: string | null,
  cpaCurrency: string | null,
): CpaPriceListView | undefined {
  if (rows.length === 0) return undefined;
  return {
    rows: rows.map((r) => {
      // The country's name, not its code: "NG" asks the affiliate to know
      // ISO 3166. `geoLabel` rather than `countryLabel` because a row may
      // name a subdivision ("US-NJ"), which it keeps beside the country.
      const jurisdiction = r.jurisdiction === '*'
        ? t('terms.cpaPriceList.anyJurisdiction' as AffiliateMessageKey)
        : geoLabel(r.jurisdiction);
      const channel = r.channel === '*' ? t('terms.cpaPriceList.anyChannel' as AffiliateMessageKey) : r.channel;
      return {
        jurisdiction,
        channel,
        amount: formatMoney({ amount_minor: r.amount_minor, currency: cpaCurrency ?? '' }),
        label: r.channel === '*' ? jurisdiction : `${jurisdiction} · ${channel}`,
      };
    }),
    elsewhere: {
      label: t('terms.cpaPriceList.elsewhere' as AffiliateMessageKey),
      value: formatMoney({ amount_minor: cpaAmountMinor ?? '0', currency: cpaCurrency ?? '' }),
    },
  };
}

export interface TierBandLine {
  readonly range: string;
  readonly rate: string;
}

/**
 * §26.3/§26.3a — one RevShare band, deal-wide or per-product: the range it
 * covers and the rate it pays. `from`/`to` are a count or a volume
 * depending on the version's own `tierKeyMetric`, never money (they arrive
 * as plain numbers, not minor-unit strings), so they run through
 * `formatCount` — the same formatter every other bare count on this screen
 * uses (`cpa_criteria`'s `threshold_count`, the CPC/CPM period caps) —
 * never `formatMoney`.
 */
function tierBandLine(b: { from: number; to: number | null; rate_bps: number }): TierBandLine {
  return {
    range: b.to === null ? `${formatCount(b.from)}+` : `${formatCount(b.from)} - ${formatCount(b.to)}`,
    rate: percent(b.rate_bps),
  };
}

export interface CpaTierBandLine {
  readonly range: string;
  readonly amount: string;
}

/** §24.7 — one band of the deal-wide CPA-by-QFTD-count table: a count range and the flat amount it pays. */
function cpaTierBandLine(b: { from: number; to: number | null; amount_minor: string; currency: string }): CpaTierBandLine {
  return {
    range: b.to === null ? `${formatCount(b.from)}+` : `${formatCount(b.from)} - ${formatCount(b.to)}`,
    amount: formatMoney({ amount_minor: b.amount_minor, currency: b.currency }),
  };
}

export interface ProductTierTableView {
  readonly product: string;
  readonly tiers: readonly TierBandLine[];
}

export interface ProductRateLine {
  readonly product: string;
  /**
   * §10.5/§25.5 — a product's own RevShare override, or `null` when this
   * product's row states none. Both this and `cpa` below can be set at
   * once — the table's own CHECK (0050) requires only that at least one
   * is, never that the other is absent — so both are rendered whenever
   * both are there rather than one being picked over the other.
   */
  readonly revshare: string | null;
  readonly cpa: string | null;
}

/** §10.5/§25.5 — a flat per-product override, beside the tier tables above. */
function productRateLine(p: {
  product: string;
  revshare_rate_bps: number | null;
  cpa_amount_minor: string | null;
  cpa_currency: string | null;
}): ProductRateLine {
  return {
    product: p.product,
    revshare: p.revshare_rate_bps !== null ? percent(p.revshare_rate_bps) : null,
    cpa: p.cpa_amount_minor !== null
      ? formatMoney({ amount_minor: p.cpa_amount_minor, currency: p.cpa_currency ?? '' })
      : null,
  };
}

export interface ComponentWindowLine {
  /** "Never expires", in words — never a bare `0` and never a blank standing for it. */
  readonly value: string;
  /** Kept for anything that needs it programmatically; never shown as a raw code on the screen. */
  readonly source: 'COMPONENT' | 'DEAL';
}

/**
 * §7.8e/§14.6d — one component's own attribution window, or the deal's own
 * figure when the component states none. A `source: 'DEAL'` window is not
 * "no window": `days`/`never_expires` already carry the deal's own
 * resolved figure (`terms.ts`'s `componentWindowDisclosure`), so this
 * always renders a real figure — never an absence that would tell the
 * affiliate something false. Never-expire is rendered as a word: a `0` or
 * a blank on a screen about how long a player stays attributed reads as
 * "immediately", which is the opposite of what it means.
 */
function componentWindowLine(w: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean }): ComponentWindowLine {
  const days = w.days ?? 0;
  const value = w.never_expires
    ? t('terms.componentWindow.neverExpires' as AffiliateMessageKey)
    : t(
        days === 1 ? 'terms.componentWindow.value.one' as AffiliateMessageKey : 'terms.componentWindow.value.other' as AffiliateMessageKey,
        { days },
      );
  return { value, source: w.source };
}

/*
 * §7.14/§7.8f/§7.14c — traffic rules, grouped by their stated value.
 *
 * The API sends only the rules a deal version actually states (`traffic_rules`,
 * `apps/api-core/src/terms.ts`) — never a full catalogue, so this build has no
 * way to enumerate every channel and practice that was left unstated. Two
 * things follow: the three headline axes (Addendum A §8.2/§8.8 — Branded,
 * Cross-branded, Incentivised) are looked up by their known codes and default
 * to Not stated individually when absent; every other stated rule is grouped
 * by its mode below the headline, and a code this build has never seen (a
 * channel or practice added to the catalogue after this build shipped) still
 * shows — as its own code — rather than being dropped.
 */

/** §8.3 (Addendum A) — the thirty-six channel codes ever written, active or withdrawn (0172:139-146 — a withdrawn code stays valid on a row that already names it). */
const TRAFFIC_CHANNELS: ReadonlySet<string> = new Set([
  'SEO', 'PPC', 'SOCIAL', 'STREAMING', 'TELEGRAM', 'EMAIL', 'APP', 'MEDIA_BUYING', 'COUPON', 'COMMUNITY',
  'INCENTIVISED', 'ASA', 'ASO', 'FB_ADS', 'GOOGLE_ADS', 'INFLUENCE', 'NATIVE_ADS', 'POPS', 'PUSH', 'SEM',
  'SMS', 'TIKTOK', 'TWITCH_ADS', 'UAC', 'UNITY', 'YOUTUBE_ADS', 'DOORWAY', 'SCHEME_TRAFFIC', 'CFT', 'DIRECT',
  'IN_PAGE', 'VIDEO', 'BANNER', 'DISPLAY', 'TELEGRAM_MINI_APPS', 'REDDIT',
]);

/** §10.10 — the eight traffic-practice codes `ref_traffic_practices` validates against (migration 0206). */
const TRAFFIC_PRACTICES: ReadonlySet<string> = new Set([
  'BRAND_BIDDING', 'CROSS_BRAND_BIDDING', 'TRADEMARK_DOMAINS', 'ADULT_ADJACENCY', 'MINORS_PLACEMENT',
  'UNSOLICITED_MESSAGING', 'FORCED_CLICKS', 'MISSTATED_CLAIMS',
]);

/** A channel or practice code's own word. A code this build has not shipped words for stays its code. */
function trafficRuleLabel(restriction: string, code: string): string {
  if (restriction === 'TRAFFIC_SOURCE' && TRAFFIC_CHANNELS.has(code)) {
    return t(`trafficChannel.${code}` as AffiliateMessageKey);
  }
  if (restriction === 'PRACTICE' && TRAFFIC_PRACTICES.has(code)) {
    return t(`trafficPractice.${code}` as AffiliateMessageKey);
  }
  return code;
}

export type TrafficRuleStatus = 'ALLOWED' | 'FORBIDDEN' | 'BY_AGREEMENT' | 'NOT_STATED';

/** §7.14c — a mode this build does not recognise reads as Not Stated, the same as no row at all — never as a derived Allowed or Forbidden. */
function normalizedTrafficStatus(mode: string | undefined): TrafficRuleStatus {
  return mode === 'ALLOWED' || mode === 'FORBIDDEN' || mode === 'BY_AGREEMENT' ? mode : 'NOT_STATED';
}

function trafficStatusLabel(status: TrafficRuleStatus): string {
  switch (status) {
    case 'ALLOWED': return t('terms.trafficRules.allowed' as AffiliateMessageKey);
    case 'FORBIDDEN': return t('terms.trafficRules.forbidden' as AffiliateMessageKey);
    case 'BY_AGREEMENT': return t('terms.trafficRules.byAgreement' as AffiliateMessageKey);
    default: return t('terms.trafficRules.notStated' as AffiliateMessageKey);
  }
}

export interface TrafficRuleRow {
  readonly code: string;
  readonly label: string;
  readonly status: TrafficRuleStatus;
  readonly statusLabel: string;
}

export interface TrafficRulesView {
  /** §8.2/§8.8 (Addendum A) — Branded, Cross-branded, Incentivised, always in this order, always three rows. */
  readonly headline: readonly TrafficRuleRow[];
  readonly allowed: readonly TrafficRuleRow[];
  readonly forbidden: readonly TrafficRuleRow[];
  readonly byAgreement: readonly TrafficRuleRow[];
}

/** §8.2/§8.8 (Addendum A) — the three named permission axes, and which restriction row each one is. */
const HEADLINE_TRAFFIC_RULES: readonly { restriction: string; value: string; labelKey: AffiliateMessageKey }[] = [
  { restriction: 'PRACTICE', value: 'BRAND_BIDDING', labelKey: 'terms.trafficRules.branded' as AffiliateMessageKey },
  { restriction: 'PRACTICE', value: 'CROSS_BRAND_BIDDING', labelKey: 'terms.trafficRules.crossBranded' as AffiliateMessageKey },
  { restriction: 'TRAFFIC_SOURCE', value: 'INCENTIVISED', labelKey: 'terms.trafficRules.incentivised' as AffiliateMessageKey },
];

/**
 * §7.14/§7.8f/§7.14c — every stated rule, grouped by its value. `rules`
 * undefined or empty (an older response, or a version that states nothing)
 * reads the same way: every headline axis Not Stated, nothing in the three
 * buckets below — never a derived Allowed or Forbidden either way.
 */
export function trafficRulesView(
  rules: readonly { restriction: string; value: string; mode: string }[] | undefined,
): TrafficRulesView {
  const rows = rules ?? [];
  const headlineKeys = new Set(HEADLINE_TRAFFIC_RULES.map((h) => `${h.restriction}:${h.value}`));

  const headline = HEADLINE_TRAFFIC_RULES.map((h) => {
    const row = rows.find((r) => r.restriction === h.restriction && r.value === h.value);
    const status = normalizedTrafficStatus(row?.mode);
    return { code: h.value, label: t(h.labelKey), status, statusLabel: trafficStatusLabel(status) };
  });

  const toRow = (r: { restriction: string; value: string; mode: string }): TrafficRuleRow => {
    const status = normalizedTrafficStatus(r.mode);
    return { code: r.value, label: trafficRuleLabel(r.restriction, r.value), status, statusLabel: trafficStatusLabel(status) };
  };
  const remaining = rows.filter((r) => !headlineKeys.has(`${r.restriction}:${r.value}`));

  return {
    headline,
    allowed: remaining.filter((r) => r.mode === 'ALLOWED').map(toRow),
    forbidden: remaining.filter((r) => r.mode === 'FORBIDDEN').map(toRow),
    byAgreement: remaining.filter((r) => r.mode === 'BY_AGREEMENT').map(toRow),
  };
}

export function termsLine(row: {
  /** Optional only so a proposal built by hand for this function still type-checks. */
  deal_id?: string;
  /** §7.12d — absent reads as PERSONAL, which every response before it was. */
  terms_source?: 'PERSONAL' | 'CAMPAIGN';
  /** §26.2 — set only on a hybrid deal. */
  hybrid_mode?: string | null;
  brand: string;
  deal_name?: string | null;
  summary: string;
  since: string;
  accepted_by_side: string | null;
  accepted_at: string | null;
  revenue_basis: string;
  revshare_rate_bps: number | null;
  cpa_amount_minor: string | null;
  cpa_currency: string | null;
  cpa_minimum_deposit_minor: string | null;
  cpa_qualification_deadline_days: number | null;
  cpa_qualification_from?: 'SIGNUP' | 'FIRST_DEPOSIT' | null;
  cpa_hold_period_days: number | null;
  /** §24.5b — the hold is the platform's standard rather than the deal's own. */
  cpa_hold_period_is_default?: boolean;
  /** §0.5b — where the hold in force came from. */
  cpa_hold_period_source?: 'DEAL' | 'BRAND' | 'OPERATOR' | 'PLATFORM';
  revshare_amount_per_unit_minor: string | null;
  revshare_amount_per_unit_currency: string | null;
  cpa_criteria: { criterion: string; threshold_minor: string | null; threshold_count: number | null; label: string | null; standard?: boolean; applies?: boolean }[];
  scopes: { dimension: string; value: string }[];
  tier_mode: string | null;
  /**
   * `label` is the platform's own word, null while nobody has set one (0173).
   * `kind`/`rate_bps`/`amount_minor`/`currency` are §20.4a's addition (task
   * A4) and are optional here only so the pre-existing fixture in
   * `screens.test.ts` — built before that addition — still type-checks; a
   * live response always has them.
   */
  deduction_stack: DeductionStepInput[];
  /** §24.9 — an extra payout on top of CPA/RevShare, never the §20.5 stack item above. */
  bonus_payout_enabled: boolean;
  bonus_payout_is_percentage: boolean;
  bonus_payout_basis: string | null;
  bonus_payout_rate_bps: number | null;
  bonus_payout_amount_minor: string | null;
  bonus_payout_currency: string | null;
  bonus_payout_window_days: number | null;
  /*
   * Optional, because an older response carries none of the three and a
   * proposal built by hand for this function may not either. Absent reads as
   * it always did: the default deal, and nothing said about coverage.
   */
  /** §7.12 — set when these terms are for one campaign alone. */
  campaign_name?: string | null;
  /** §24.10 — what the CPA pays on. */
  cpa_trigger?: string | null;
  /** §7.13 — where each component pays. Empty: everywhere. */
  coverage?: readonly { component: string; mode: string; jurisdiction: string }[];
  /**
   * §2.8b — an identifier stays an identifier; used only to pick the right
   * words for a CPA-shaped field CPL reuses (D1: CPL stores its amount and
   * hold on the same columns CPA does) and for the FLAT/CPC conditions below.
   * Optional for the same reason `campaign_name`/`cpa_trigger` are: an older
   * fixture carries none of the new models.
   */
  commission_type?: string;
  /** §26.7 — the flat fee. Both null, or both set. */
  flat_fee_minor?: string | null;
  flat_fee_currency?: string | null;
  /** §26.10/§26.10a — paid per VALID click, up to the mandatory period cap. */
  cpc_amount_minor?: string | null;
  cpc_currency?: string | null;
  cpc_period_cap?: number | null;
  /** §26.11/§26.11a — paid per thousand counted impressions, up to the mandatory period cap. */
  cpm_amount_minor?: string | null;
  cpm_currency?: string | null;
  cpm_period_cap?: number | null;
  /** §26.12/§8.10 — a share of approved media spend; the cap is optional, never mandatory. */
  spend_share_rate_bps?: number | null;
  spend_period_cap_minor?: string | null;
  spend_cap_currency?: string | null;
  /** §7.8a/§31.2c — payout frequency and settlement terms, with the level each resolved from. */
  payout?: { frequency: string; settlement_terms_days: number; source: string };
  /** §7.8b/§27.2b — the negative-balance policy in force. */
  negative_balance?: { policy: string; carry_bps: number; source: string };
  /** §7.8e/§14.6b — the clamped cookie lifetime the edge actually sets. */
  cookie_lifetime_days?: number;
  /** §7.8e — the attribution window (click to registration). */
  attribution_window_days?: number;
  /** §8.7 (Addendum A)/§30.4d — the minimum payout threshold, with its source, and per-method detail. */
  minimum_payout?: {
    amount_minor: string;
    currency: string;
    source: string;
    method?: string | null;
    method_amount_minor?: string | null;
    method_currency?: string | null;
    applies_amount_minor?: string;
    applies_currency?: string;
  };
  /** §7.14/§7.8f — this version's own stated traffic rules; empty or absent reads as not stated (7.14c). */
  traffic_rules?: readonly { restriction: string; value: string; mode: string }[];
  /** §24.7b/F2 — the CPA price list; empty or absent means `cpa_amount_minor` above pays everywhere. */
  cpa_price_rows?: readonly { jurisdiction: string; channel: string; amount_minor: string }[];
  /** §24.7/F2 — the deal-wide CPA-by-QFTD-count table. */
  cpa_tier_bands?: readonly { from: number; to: number | null; amount_minor: string; currency: string }[];
  /** §26.3/F2 — the deal-wide RevShare tier table. */
  revshare_tier_bands?: readonly { from: number; to: number | null; rate_bps: number }[];
  /** §26.3a — one table per product that has its own, beside the deal-wide one above and `product_rates` below. */
  revshare_product_tiers?: readonly { product: string; tiers: readonly { from: number; to: number | null; rate_bps: number }[] }[];
  /** §26.3/§26.3a — the one metric every tier table on this version is keyed on; `null` when the version carries no such table. */
  revshare_tier_key_metric?: string | null;
  /** §26.3a — server-composed, in words already; shown only when the key metric is QFTD_COUNT and at least one product has its own table. */
  product_tier_qftd_note?: string | null;
  /** §10.5/§25.5 — flat per-product overrides, beside the tier tables above. */
  product_rates?: readonly { product: string; revshare_rate_bps: number | null; cpa_amount_minor: string | null; cpa_currency: string | null }[];
  /** §7.8e/§14.6d — the CPA component's own attribution window, or the deal's. */
  cpa_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
  /** §7.8e/§14.6d — same, for RevShare. */
  revshare_window?: { source: 'COMPONENT' | 'DEAL'; days: number | null; never_expires: boolean };
}): TermsLine {
  const conditions: { label: string; value: string }[] = [];

  // §26.7 — a flat fee is independent of any player, so it is its own
  // condition rather than riding the CPA-shaped block below.
  if (row.commission_type === 'FLAT' && row.flat_fee_minor !== null && row.flat_fee_minor !== undefined) {
    conditions.push({
      label: t('terms.flatFee' as AffiliateMessageKey),
      value: formatMoney({ amount_minor: row.flat_fee_minor, currency: row.flat_fee_currency ?? '' }),
    });
  }

  // §26.10/§26.10a — paid per valid click, up to the mandatory period cap.
  if (row.commission_type === 'CPC' && row.cpc_amount_minor !== null && row.cpc_amount_minor !== undefined) {
    conditions.push({
      label: t('terms.perClick' as AffiliateMessageKey),
      value: formatMoney({ amount_minor: row.cpc_amount_minor, currency: row.cpc_currency ?? '' }),
    });
    if (row.cpc_period_cap !== null && row.cpc_period_cap !== undefined) {
      conditions.push({
        label: t('terms.periodCap' as AffiliateMessageKey),
        value: t(
          row.cpc_period_cap === 1 ? 'terms.periodCap.value.one' as AffiliateMessageKey : 'terms.periodCap.value.other' as AffiliateMessageKey,
          // A cap in the thousands reads as a grouped figure, the same way
          // any other count on this console does — never the bare digits.
          { count: formatCount(row.cpc_period_cap) },
        ),
      });
    }
  }

  // §26.11/§26.11a — paid per thousand counted impressions, up to the
  // mandatory period cap. Same shape as CPC above; its own value keys
  // (`terms.periodCap.impressions.value.*`) rather than reusing CPC's,
  // because those say "click(s)" and an impression is not a click (§12.12:
  // an impression never attributes and is a different event on a different
  // path from a click, 26.11b) — the cap text has to say what is capped.
  if (row.commission_type === 'CPM' && row.cpm_amount_minor !== null && row.cpm_amount_minor !== undefined) {
    conditions.push({
      label: t('terms.perThousandImpressions' as AffiliateMessageKey),
      value: formatMoney({ amount_minor: row.cpm_amount_minor, currency: row.cpm_currency ?? '' }),
    });
    if (row.cpm_period_cap !== null && row.cpm_period_cap !== undefined) {
      conditions.push({
        label: t('terms.periodCap' as AffiliateMessageKey),
        value: t(
          row.cpm_period_cap === 1
            ? 'terms.periodCap.impressions.value.one' as AffiliateMessageKey
            : 'terms.periodCap.impressions.value.other' as AffiliateMessageKey,
          { count: formatCount(row.cpm_period_cap) },
        ),
      });
    }
  }

  // §26.12/§8.10 — a share of the affiliate's own approved media spend, paid
  // at the affiliate level like FLAT/CPC/CPM above. Unlike those, the cap is
  // never mandatory (section 8.10 corrects the plan's own D1 line that read
  // it as required), so it is its own condition, shown only when stated —
  // and it is money, not a count, so it reuses `formatMoney` rather than
  // CPC/CPM's `terms.periodCap.*` count-shaped value keys.
  if (
    row.commission_type === 'SPEND_SHARE'
    && row.spend_share_rate_bps !== null && row.spend_share_rate_bps !== undefined
  ) {
    conditions.push({
      label: t('terms.spendShare' as AffiliateMessageKey),
      value: t('terms.spendShare.value' as AffiliateMessageKey, {
        rate: (row.spend_share_rate_bps / 100).toFixed(2),
      }),
    });
    if (row.spend_period_cap_minor !== null && row.spend_period_cap_minor !== undefined) {
      conditions.push({
        label: t('terms.spendShare.cap' as AffiliateMessageKey),
        value: formatMoney({
          amount_minor: row.spend_period_cap_minor,
          currency: row.spend_cap_currency ?? '',
        }),
      });
    }
  }

  // The number itself, first. "A one-off GBP amount per qualifying player" is
  // the shape of the term and not the term: an affiliate cannot plan against it
  // and cannot check a statement line against it.
  //
  // §26.8 — CPL stores its amount on the same `cpa_amount_minor` column CPA
  // does (D1), so it falls through the same branch with its own label and no
  // "CPA pays on" row: a CPL deal always pays on registration, so a trigger
  // row would only repeat what the label already says.
  if (row.cpa_amount_minor !== null) {
    const isCpl = row.commission_type === 'CPL';
    conditions.push({
      label: t(isCpl ? 'terms.perLead' as AffiliateMessageKey : 'terms.perPlayer'),
      value: formatMoney({ amount_minor: row.cpa_amount_minor, currency: row.cpa_currency ?? '' }),
    });
    // §24.10 — what that amount is paid on. "Per qualifying player" reads as
    // a first deposit, and a CPA on registration or on every deposit is a
    // different deal that happens to share the label.
    if (row.cpa_trigger && !isCpl) {
      conditions.push({ label: t('campaignTerms.cpaTrigger'), value: cpaTriggerLabel(row.cpa_trigger) });
    }
  }
  if (row.revshare_rate_bps !== null) {
    conditions.push({
      label: t('terms.revshare'),
      value: t('terms.revshare.value', {
        rate: (row.revshare_rate_bps / 100).toFixed(2),
        // The basis is the operator's own configured wording, not a code of
        // ours, so it goes through unchanged.
        basis: row.revenue_basis,
      }),
    });
  }
  // §20.10 FTD_COUNT — a flat amount per qualified FTD, not a percentage.
  if (row.revshare_amount_per_unit_minor !== null) {
    conditions.push({
      label: t('terms.perFtd'),
      value: formatMoney({
        amount_minor: row.revshare_amount_per_unit_minor,
        currency: row.revshare_amount_per_unit_currency ?? '',
      }),
    });
  }

  // §24.9f — its own condition, never blended into the CPA/RevShare figures
  // above or the §20.5 deduction below: an extra payout, not part of either.
  if (row.bonus_payout_enabled) {
    const value = row.bonus_payout_is_percentage
      ? t('line.bonusPayout.percentage', {
          rate: ((row.bonus_payout_rate_bps ?? 0) / 100).toFixed(2),
          basis: bonusBasisWords(row.bonus_payout_basis) ?? row.bonus_payout_basis ?? '',
        })
      : formatMoney({
          amount_minor: row.bonus_payout_amount_minor ?? '0',
          currency: row.bonus_payout_currency ?? '',
        });
    conditions.push({
      label: t(
        (row.bonus_payout_window_days ?? 90) === 1 ? 'terms.bonusPayout.one' : 'terms.bonusPayout.other',
        { days: row.bonus_payout_window_days ?? 90 },
      ),
      value,
    });
  }

  // §24.2b — every enabled criterion, named, with its own threshold. "Qualifying
  // player" without these is a term the affiliate cannot check their own
  // reporting against, and a failed qualification with no visible reason is
  // exactly the support ticket §24.2b exists to prevent.
  if (row.cpa_criteria.length > 0) {
    for (const c of row.cpa_criteria) {
      const label = curatedOr(c.label, criterionWords(c.criterion));
      // A standard check switched off for this deal is still listed, so the
      // affiliate can see it does not apply. A row without `applies` predates
      // the standard set being listed, and every such row applied.
      const value =
        c.applies === false
          ? t('terms.switchedOff')
          : c.threshold_minor !== null
            ? formatMoney({ amount_minor: c.threshold_minor, currency: row.cpa_currency ?? '' })
            : c.threshold_count !== null
              ? formatCount(c.threshold_count)
              : c.standard
                ? t('terms.requiredStandard')
                : t('terms.required');
      conditions.push({ label, value });
    }
  } else if (row.cpa_minimum_deposit_minor !== null) {
    // Pre-§24.2 deals that never stated explicit criteria still show the one
    // fact the deal itself carries.
    conditions.push({
      label: t('terms.minDeposit'),
      value: formatMoney({
        amount_minor: row.cpa_minimum_deposit_minor,
        currency: row.cpa_currency ?? '',
      }),
    });
  }
  if (row.cpa_qualification_deadline_days !== null) {
    conditions.push({
      label: t('terms.timeToQualify'),
      value: timeToQualify(row.cpa_qualification_deadline_days, row.cpa_qualification_from),
    });
  }
  if (row.cpa_hold_period_days !== null) {
    // §24.5b — "When will I be paid?" must be answerable from the affiliate's
    // own dashboard, and the hold is usually the reason the answer is "not yet".
    // Shown whether the deal set its own or inherits the platform's standard:
    // a hold the affiliate was never told about is the one they dispute.
    conditions.push({
      label: t('terms.held'),
      value: t(
        holdWordsKey(row.cpa_hold_period_source, row.cpa_hold_period_is_default, row.cpa_hold_period_days),
        { days: row.cpa_hold_period_days },
      ),
    });
  }
  // §26.2 — a hybrid's CPA and share do not simply add up under two of the
  // three modes, and the affiliate cannot tell which from the figures alone.
  if (row.commission_type === 'HYBRID' && row.hybrid_mode) {
    conditions.push({
      label: t('terms.hybridMode' as AffiliateMessageKey),
      value: HYBRID_MODES.has(row.hybrid_mode)
        ? t(`terms.hybridMode.${row.hybrid_mode}` as AffiliateMessageKey)
        : row.hybrid_mode,
    });
  }
  if (row.tier_mode !== null) {
    conditions.push({
      label: t('terms.tiers'),
      value:
        row.tier_mode === 'RETROACTIVE'
          ? t('terms.tiers.retroactive')
          : t('terms.tiers.marginal'),
    });
  }

  // §7.1/§14.10/§10.5 — this deal only prices some geos/products, not every
  // attributed player. An affiliate who doesn't know that reads a $0 month on
  // an unscoped product as the deal being broken rather than working as agreed.
  const geoScope = row.scopes.filter((s) => s.dimension === 'GEO').map((s) => s.value);
  const productScope = row.scopes.filter((s) => s.dimension === 'PRODUCT').map((s) => s.value);
  if (geoScope.length > 0) {
    conditions.push({ label: t('terms.geoScope'), value: geoScope.join(', ') });
  }
  if (productScope.length > 0) {
    conditions.push({ label: t('terms.productScope'), value: productScope.join(', ') });
  }

  // §7.13 — where each component pays, but only where the deal says. An
  // "Everywhere" row under a Geo scope row would read as the two
  // contradicting each other; the campaign view, which has no scope rows,
  // says "Everywhere" out loud instead.
  const coverage = row.coverage ?? [];
  for (const component of COVERAGE_COMPONENTS) {
    if (coverage.some((c) => c.component === component)) {
      conditions.push({ label: coverageLabel(component), value: coverageSentence(coverage, component) });
    }
  }

  // §24.7b/F2 — computed once, rather than inside the two spreads that would
  // otherwise both call `cpaPriceList` (one to test truthiness, one to read
  // the value) and risk disagreeing were the row's inputs ever mutated
  // between them.
  const cpaPrices = cpaPriceList(row.cpa_price_rows ?? [], row.cpa_amount_minor, row.cpa_currency);

  const source = row.terms_source ?? 'PERSONAL';
  const campaignName = row.campaign_name ?? null;
  return {
    dealId: row.deal_id ?? '',
    source,
    campaignName,
    sourceNote: source === 'CAMPAIGN' && campaignName
      ? t('terms.campaignPayout.note' as AffiliateMessageKey, { name: campaignName })
      : null,
    brand: row.brand,
    title: source === 'CAMPAIGN' && campaignName
      ? `${row.brand} — ${campaignName}`
      : row.deal_name ? `${row.brand} — ${row.deal_name}` : row.brand,
    scope: termsScopeLabel(source, campaignName),
    forCampaign: Boolean(row.campaign_name),
    summary: row.summary,
    conditions,
    deductions: row.deduction_stack.map((d) => `${d.order}. ${curatedOr(d.label, d.what)}`),
    since: formatDate(row.since),
    accepted:
      row.accepted_at && row.accepted_by_side
        ? t('terms.accepted', {
            side: row.accepted_by_side.toLowerCase(),
            date: formatDate(row.accepted_at),
          })
        : t('terms.inForce'),
    formula: formulaLine(row.revenue_basis, row.deduction_stack),
    adminFee: adminFeeLine(row.deduction_stack),
    trafficRules: trafficRulesView(row.traffic_rules),
    // `exactOptionalPropertyTypes` — a key that may be absent is omitted
    // entirely rather than set to `undefined` (see `headlineNote` above).
    ...(row.payout ? { payout: payoutLine(row.payout) } : {}),
    ...(row.negative_balance ? { negativeBalance: negativeBalanceLine(row.negative_balance) } : {}),
    ...(row.cookie_lifetime_days !== undefined ? { cookieLifetime: cookieLifetimeLine(row.cookie_lifetime_days) } : {}),
    ...(row.attribution_window_days !== undefined
      ? { attributionWindow: attributionWindowLine(row.attribution_window_days) }
      : {}),
    ...(row.minimum_payout ? { minimumPayout: minimumPayoutLine(row.minimum_payout) } : {}),
    ...(cpaPrices ? { cpaPriceList: cpaPrices } : {}),
    ...(row.cpa_tier_bands && row.cpa_tier_bands.length > 0
      ? { cpaTierBands: row.cpa_tier_bands.map(cpaTierBandLine) }
      : {}),
    ...(row.revshare_tier_bands && row.revshare_tier_bands.length > 0
      ? { revshareTierBands: row.revshare_tier_bands.map(tierBandLine) }
      : {}),
    ...(row.revshare_product_tiers && row.revshare_product_tiers.length > 0
      ? {
          productTierTables: row.revshare_product_tiers.map((p) => ({
            product: p.product,
            tiers: p.tiers.map(tierBandLine),
          })),
        }
      : {}),
    ...(row.revshare_tier_key_metric !== undefined ? { tierKeyMetric: row.revshare_tier_key_metric } : {}),
    ...(row.product_tier_qftd_note !== undefined ? { productTierQftdNote: row.product_tier_qftd_note } : {}),
    ...(row.product_rates && row.product_rates.length > 0
      ? { productRates: row.product_rates.map(productRateLine) }
      : {}),
    ...(row.cpa_window ? { cpaWindow: componentWindowLine(row.cpa_window) } : {}),
    ...(row.revshare_window ? { revshareWindow: componentWindowLine(row.revshare_window) } : {}),
  };
}

/**
 * One labelled table on the Terms screen: a heading, its rows, and a note
 * the table needs beside it (the per-product QFTD caveat).
 */
export interface TermsTableSection {
  readonly heading: string;
  readonly rows: readonly TermsRow[];
  readonly note?: string;
}

/** §26.3 — the metrics a tier table may be keyed on (0167's CHECK). */
const TIER_KEY_METRICS: ReadonlySet<string> = new Set([
  'QFTD_COUNT', 'NGR_VOLUME', 'GGR_VOLUME', 'ACTIVE_PLAYERS', 'DEPOSITS',
]);

/**
 * QA round 2 F1 — every table `termsLine` builds from the tier, product and
 * window data, as sections the screen renders one after another: CPA tier
 * bands, the deal-wide RevShare tiers, one table per product that has its
 * own, flat product rates, and the two component windows.
 *
 * `termsLine` has composed all of these since the deal-shape work, and the
 * screen read only the CPA price list, so a tiered or per-product deal showed
 * its headline rate and nothing of the table that actually pays. Empty for a
 * version that states none of them — no section is shown for an absence.
 *
 * The tier key metric is said in words in the RevShare headings; an unknown
 * metric gives the heading without one rather than printing its code. The
 * product stays as the operator named it, as it does in "Product scope".
 */
export function termsTableSections(line: TermsLine): readonly TermsTableSection[] {
  const sections: TermsTableSection[] = [];
  const metric = line.tierKeyMetric && TIER_KEY_METRICS.has(line.tierKeyMetric)
    ? t(`terms.tierMetric.${line.tierKeyMetric}` as AffiliateMessageKey)
    : null;

  if (line.cpaTierBands && line.cpaTierBands.length > 0) {
    sections.push({
      heading: t('terms.cpaTierBands' as AffiliateMessageKey),
      rows: line.cpaTierBands.map((b) => ({ label: b.range, value: b.amount })),
    });
  }
  if (line.revshareTierBands && line.revshareTierBands.length > 0) {
    sections.push({
      heading: metric
        ? t('terms.revshareTierBands.by' as AffiliateMessageKey, { metric })
        : t('terms.revshareTierBands' as AffiliateMessageKey),
      rows: line.revshareTierBands.map((b) => ({ label: b.range, value: b.rate })),
    });
  }
  const productTables = line.productTierTables ?? [];
  productTables.forEach((p, i) => {
    const note = i === productTables.length - 1 && line.productTierQftdNote ? line.productTierQftdNote : null;
    sections.push({
      heading: metric
        ? t('terms.productTierTable.by' as AffiliateMessageKey, { product: p.product, metric })
        : t('terms.productTierTable' as AffiliateMessageKey, { product: p.product }),
      rows: p.tiers.map((b) => ({ label: b.range, value: b.rate })),
      ...(note ? { note } : {}),
    });
  });
  if (line.productRates && line.productRates.length > 0) {
    sections.push({
      heading: t('terms.productRates' as AffiliateMessageKey),
      rows: line.productRates.map((r) => ({
        label: r.product,
        value: [
          r.revshare ? t('terms.productRates.revshare' as AffiliateMessageKey, { rate: r.revshare }) : null,
          r.cpa ? t('terms.productRates.cpa' as AffiliateMessageKey, { amount: r.cpa }) : null,
        ].filter((v): v is string => v !== null).join(' · '),
      })),
    });
  }
  const windows: TermsRow[] = [];
  if (line.cpaWindow) {
    windows.push({
      label: t('terms.componentWindow.cpa' as AffiliateMessageKey),
      value: `${line.cpaWindow.value} · ${t(`terms.componentWindow.source.${line.cpaWindow.source}` as AffiliateMessageKey)}`,
    });
  }
  if (line.revshareWindow) {
    windows.push({
      label: t('terms.componentWindow.revshare' as AffiliateMessageKey),
      value: `${line.revshareWindow.value} · ${t(`terms.componentWindow.source.${line.revshareWindow.source}` as AffiliateMessageKey)}`,
    });
  }
  if (windows.length > 0) {
    sections.push({ heading: t('terms.componentWindows' as AffiliateMessageKey), rows: windows });
  }
  return sections;
}

/* ------------------------------------------------------------------ *
 * Terms on one campaign — §7.12d
 * ------------------------------------------------------------------ */

/** §24.10 — the triggers this build has words for. */
const CPA_TRIGGERS: ReadonlySet<string> = new Set(['FTD', 'REGISTRATION', 'CUMULATIVE_DEPOSIT', 'DEPOSIT']);

/**
 * §24.10 — what a CPA is paid on, in words.
 *
 * An unknown trigger stays its own code, for the reason `describeKind` gives:
 * a trigger this build has never seen is not one to invent words for.
 */
export function cpaTriggerLabel(trigger: string): string {
  return CPA_TRIGGERS.has(trigger) ? t(`trigger.${trigger}` as AffiliateMessageKey) : trigger;
}

const REVENUE_BASES: ReadonlySet<string> = new Set(['NGR', 'GGR', 'FTD_VALUE', 'FTD_COUNT', 'DEPOSITS', 'TURNOVER']);

/**
 * §20.10 / §20.11 — what a revenue share is a share *of*.
 *
 * NGR and GGR keep their letters, because that is what the affiliate's own
 * tracker and every statement call them, with the words beside them: 35% of
 * one and 35% of the other differ by every cost the brand deducts.
 */
export function revenueBasisLabel(basis: string): string {
  return REVENUE_BASES.has(basis) ? t(`basis.${basis}` as AffiliateMessageKey) : basis;
}

const PLANS: ReadonlySet<string> = new Set(['CPA', 'REVSHARE', 'HYBRID']);

/** The plan, in words. A plan this build does not know stays its code. */
export function planLabel(commissionType: string): string {
  return PLANS.has(commissionType) ? t(`plan.${commissionType}` as AffiliateMessageKey) : commissionType;
}

/**
 * §7.12 — which of the three pays here, in plain words.
 *
 * Named for what the affiliate agreed to rather than for our table: the
 * campaign's payout is what they took on by joining, the default deal is the
 * one they hold with the brand, and the third is the only one negotiated for
 * this campaign by itself.
 */
export function termsSourceLabel(source: string, brand: string): string {
  switch (source) {
    case 'CAMPAIGN':
      return t('source.CAMPAIGN');
    case 'DEFAULT':
      return t('source.DEFAULT', { brand });
    case 'CUSTOM':
      return t('source.CUSTOM');
    default:
      return source;
  }
}

const COVERAGE_COMPONENTS = ['CPA', 'REVSHARE'] as const;

function coverageLabel(component: (typeof COVERAGE_COMPONENTS)[number]): string {
  return component === 'CPA' ? t('campaignTerms.coverage.cpa') : t('campaignTerms.coverage.revshare');
}

/**
 * §7.13 — where one component of a deal pays, as one line.
 *
 * "Everywhere" is said rather than left blank: a blank cell reads as "not
 * told", and an affiliate who cannot tell the two apart sends traffic from a
 * country the deal leaves out and then disputes the empty line.
 */
export function coverageSentence(
  coverage: readonly { component: string; mode: string; jurisdiction: string }[] | undefined,
  component: string,
): string {
  const rows = (coverage ?? []).filter((c) => c.component === component);
  if (rows.length === 0) return t('coverage.everywhere');
  const listed = (mode: string) =>
    rows.filter((c) => c.mode === mode).map((c) => c.jurisdiction).join(', ');
  const parts: string[] = [];
  if (listed('INCLUDE')) parts.push(t('coverage.in', { list: listed('INCLUDE') }));
  if (listed('EXCLUDE')) parts.push(t('coverage.notIn', { list: listed('EXCLUDE') }));
  // A mode this build has no words for is shown as its code rather than
  // dropped: dropping it would turn a restriction into "Everywhere".
  for (const c of rows) {
    if (c.mode !== 'INCLUDE' && c.mode !== 'EXCLUDE') parts.push(`${c.mode} ${c.jurisdiction}`);
  }
  return parts.join(' · ');
}

/** §7.12 — "For campaign: …" for terms on one campaign alone; "Default deal" otherwise. */
export function dealScopeLabel(campaignName: string | null | undefined): string {
  return campaignName ? t('terms.forCampaign', { name: campaignName }) : t('terms.defaultDeal');
}

/** §7.12d — the Terms page's chip: the default deal, a personal deal for one campaign, or a campaign's payout. */
export function termsScopeLabel(source: 'PERSONAL' | 'CAMPAIGN', campaignName: string | null): string {
  if (!campaignName) return t('terms.defaultDeal');
  return source === 'CAMPAIGN'
    ? t('terms.scope.campaignPayout' as AffiliateMessageKey, { name: campaignName })
    : t('terms.scope.personalCampaign' as AffiliateMessageKey, { name: campaignName });
}

/** §26.2 — the three hybrid modes (0054's CHECK); an unknown one is shown as sent. */
const HYBRID_MODES: ReadonlySet<string> = new Set(['INDEPENDENT', 'CPA_RECOUPABLE', 'FIRST_PERIOD_EXCLUSION']);

/**
 * One set of terms, as label/value rows — the Terms screen's own layout.
 *
 * Only the half of the plan a deal has is shown. A revenue-share row reading
 * "—" on a CPA deal invites the question of what share was withheld.
 */
/** §24.3b — "60 days from registration" or "60 days from the first deposit", as the brand counts it. */
function timeToQualify(days: number, from: 'SIGNUP' | 'FIRST_DEPOSIT' | null | undefined): string {
  const fromDeposit = from === 'FIRST_DEPOSIT';
  return t(
    days === 1
      ? (fromDeposit ? 'terms.timeToQualify.fromDeposit.one' : 'terms.timeToQualify.value.one')
      : (fromDeposit ? 'terms.timeToQualify.fromDeposit.other' : 'terms.timeToQualify.value.other'),
    { days },
  );
}

export function termsSummaryRows(s: TermsSummary): readonly TermsRow[] {
  const rows: TermsRow[] = [{ label: t('campaignTerms.plan'), value: planLabel(s.commission_type) }];

  const paysCpa = s.commission_type === 'CPA' || s.commission_type === 'HYBRID' || s.cpa_amount_minor !== null;
  const paysRevshare =
    s.commission_type === 'REVSHARE' || s.commission_type === 'HYBRID' ||
    s.revshare_rate_bps !== null || s.revshare_amount_per_unit_minor !== null;

  if (paysCpa) {
    rows.push({ label: t('campaignTerms.cpaTrigger'), value: cpaTriggerLabel(s.cpa_trigger) });
    rows.push({
      label: t('campaignTerms.cpaAmount'),
      value: formatMoney({ amount_minor: s.cpa_amount_minor, currency: s.cpa_currency ?? '' }),
    });
    // §24.10a — the thresholds are set in the brand's base currency, not the
    // CPA's, which is why the summary carries that currency at all.
    if (s.cpa_cumulative_deposit_minor !== null) {
      rows.push({
        label: t('campaignTerms.cumulativeDeposit'),
        value: formatMoney({ amount_minor: s.cpa_cumulative_deposit_minor, currency: s.base_currency }),
      });
    }
    if (s.cpa_cumulative_wagering_minor !== null) {
      rows.push({
        label: t('campaignTerms.cumulativeWagering'),
        value: formatMoney({ amount_minor: s.cpa_cumulative_wagering_minor, currency: s.base_currency }),
      });
    }
    if (s.cpa_qualification_deadline_days !== null) {
      rows.push({
        label: t('terms.timeToQualify'),
        value: timeToQualify(s.cpa_qualification_deadline_days, s.cpa_qualification_from),
      });
    }
    rows.push({ label: coverageLabel('CPA'), value: coverageSentence(s.coverage, 'CPA') });
  }

  if (paysRevshare) {
    if (s.revenue_basis === 'FTD_COUNT' && s.revshare_amount_per_unit_minor !== null) {
      // §20.10 FTD_COUNT — a flat amount per qualified first deposit, in its
      // own currency. An older server does not send that currency, and then
      // none is claimed: the CPA's or the brand's would be a guess.
      rows.push({
        label: t('terms.perFtd'),
        value: formatMoney({
          amount_minor: s.revshare_amount_per_unit_minor,
          currency: s.revshare_amount_per_unit_currency ?? '',
        }).trim(),
      });
    } else {
      // A null rate is a share that is not one number — set per product or
      // per tier — and the summary carries neither table.
      rows.push({
        label: t('terms.revshare'),
        value: s.revshare_rate_bps !== null ? percent(s.revshare_rate_bps) : t('campaignTerms.revshare.varies'),
      });
      rows.push({ label: t('campaignTerms.revshareOf'), value: revenueBasisLabel(s.revenue_basis) });
    }
    rows.push({ label: coverageLabel('REVSHARE'), value: coverageSentence(s.coverage, 'REVSHARE') });
  }

  // How long a recurring CPA keeps paying. Nothing is said when the server
  // states no limit: "unlimited" would be a claim the summary cannot back.
  if (s.commission_type === 'RECURRING_CPA' && s.end_condition_months != null) {
    rows.push({
      label: t('terms.recurringTerm'),
      value: t(s.end_condition_months === 1 ? 'players.months.one' : 'players.months.other', { n: s.end_condition_months }),
    });
  }

  // Per valid click / per thousand impressions, and the cap on each. Shown
  // whenever the amount is stated, so a campaign priced this way is not
  // read as paying nothing.
  if (s.cpc_amount_minor != null) {
    rows.push({
      label: t('terms.perClick'),
      value: formatMoney({ amount_minor: s.cpc_amount_minor, currency: s.cpc_currency ?? '' }),
    });
    if (s.cpc_period_cap != null) {
      rows.push({
        label: t('terms.periodCap'),
        value: t(s.cpc_period_cap === 1 ? 'terms.periodCap.value.one' : 'terms.periodCap.value.other', {
          count: formatCount(s.cpc_period_cap),
        }),
      });
    }
  }
  if (s.cpm_amount_minor != null) {
    rows.push({
      label: t('terms.perThousandImpressions'),
      value: formatMoney({ amount_minor: s.cpm_amount_minor, currency: s.cpm_currency ?? '' }),
    });
    if (s.cpm_period_cap != null) {
      rows.push({
        label: t('terms.periodCap'),
        value: t(
          s.cpm_period_cap === 1 ? 'terms.periodCap.impressions.value.one' : 'terms.periodCap.impressions.value.other',
          { count: formatCount(s.cpm_period_cap) },
        ),
      });
    }
  }

  // §24.9f — its own row, never blended into the CPA or the share above.
  if (s.bonus_payout_enabled) {
    rows.push({
      label: t('campaignTerms.bonus'),
      value: s.bonus_payout_is_percentage
        ? t('line.bonusPayout.percentage', {
            rate: ((s.bonus_payout_rate_bps ?? 0) / 100).toFixed(2),
            basis: bonusBasisWords(s.bonus_payout_basis) ?? s.bonus_payout_basis ?? '',
          })
        : formatMoney({ amount_minor: s.bonus_payout_amount_minor, currency: s.bonus_payout_currency ?? '' }),
    });
  }

  return rows;
}

export interface CampaignTermsView {
  /** Null when nothing pays this affiliate on the campaign yet. */
  readonly applies: {
    /** Which of the three pays here — said before the figures. */
    readonly heading: string;
    /** Empty when the terms themselves could not be read. */
    readonly rows: readonly TermsRow[];
    readonly since: string | null;
  } | null;
  /**
   * §7.2a — what the campaign's payout has been, newest first. Empty on a
   * campaign that has only ever paid one rate, where it would say nothing the
   * table above does not.
   */
  readonly history: readonly {
    readonly key: string;
    /** The run this version covered, already worded. */
    readonly ran: string;
    readonly live: boolean;
    readonly rows: readonly TermsRow[];
  }[];
  /** §7.11 — an offer for this campaign alone, awaiting an answer. */
  readonly offer: {
    readonly dealId: string;
    /** Null when the offered terms could not be read: there is nothing to answer then. */
    readonly version: number | null;
    readonly rows: readonly TermsRow[];
    readonly when: string | null;
  } | null;
}

/**
 * §7.12d — one campaign's terms, as the affiliate reads them.
 *
 * The server's `note` is not used. It is English whatever the reader chose,
 * it repeats what `heading` already says in their language, and when nothing
 * applies it still says "your default deal" — a sentence that would be read
 * as a promise of terms that do not exist.
 */
export function campaignTermsView(r: CampaignTerms, brand: string): CampaignTermsView {
  const applies = r.applies;
  const offer = r.pending;
  return {
    // Terms with nothing in force pay nothing, and are shown as nothing
    // applying — never as "your default deal" over an empty table.
    applies: applies && applies.terms
      ? {
          heading: t('campaignTerms.applies', { source: termsSourceLabel(applies.source, brand) }),
          rows: applies.terms ? termsSummaryRows(applies.terms) : [],
          since: applies.terms ? t('terms.since', { date: formatDate(applies.terms.valid_from) }) : null,
        }
      : null,
    offer: offer
      ? {
          dealId: offer.deal_id,
          version: offer.terms?.version ?? null,
          rows: offer.terms ? termsSummaryRows(offer.terms) : [],
          when: offer.terms
            ? t('campaignTerms.offer.when', { date: formatDate(offer.terms.valid_from) })
            : null,
        }
      : null,
    /*
     * §7.2a — what this campaign paid before today.
     *
     * Shown only where there is more than one version, because on a campaign
     * never repriced it would repeat the table above it. Where there is more,
     * it is the only way an affiliate can check a payment: a player keeps the
     * terms they were attributed under, so one period can be paid at two
     * rates and neither is the rate the screen currently shows.
     */
    history: (r.payout_history ?? []).length > 1
      ? (r.payout_history ?? []).map((v) => ({
          key: v.deal_version_id,
          ran: v.ends_at
            ? `${formatDate(v.valid_from)} → ${formatDate(v.ends_at)}`
            : t('terms.since', { date: formatDate(v.valid_from) }),
          live: v.ends_at === null,
          rows: termsSummaryRows(v),
        }))
      : [],
  };
}

/* ------------------------------------------------------------------ *
 * Performance by dimension — §39.6, §12.5c
 * ------------------------------------------------------------------ */

/**
 * §39.6's dimensions, named as the affiliate thinks of them.
 *
 * The sub-IDs are labelled with the same words the links form uses, because
 * they are the same thing: an affiliate who typed a label into "s1" needs to
 * find it here under a name they recognise, not under `sub_id_1`.
 */
const DIMENSION_VALUES = [
  /*
   * §39.6 — first, because it is the question asked most and the one this
   * picker could not express. Every other dimension answers "where did it come
   * from"; this one answers "when", which is what an affiliate checks after
   * changing anything.
   */
  'day',
  'link_label',
  ...Array.from({ length: SUB_ID_SLOTS }, (_, i) => `sub_id_${i + 1}`),
  'creative',
  'referrer',
  'campaign',
  'landing_page',
  'geo',
  'device',
  'os',
  'browser',
  // An affiliate works across brands and products, and could break their
  // traffic down by neither.
  'brand',
  'product',
] as const;

/*
 * A function rather than a constant, for the reason `payoutMethods()` is one:
 * a list built at import time is built in the language the tab booted in, and
 * the reader's own arrives a moment later with `me()`.
 *
 * The `s1`…`s10` in a sub-ID's name is the query parameter itself and stays as
 * it is in every language — it is what the affiliate types into the links form
 * and reads back off their own URLs.
 */
export function dimensions(): readonly { value: string; label: string }[] {
  return DIMENSION_VALUES.map((value) => ({
    value,
    label: value.startsWith('sub_id_')
      ? t('dim.sub_id', { slot: `s${value.slice('sub_id_'.length)}` })
      : t(`dim.${value}` as AffiliateMessageKey),
  }));
}

export interface PerformanceReportRow {
  readonly value: string | null;
  readonly clicks: number;
  readonly bot_clicks: number;
  readonly duplicate_clicks: number;
  readonly geo_blocked_clicks: number;
  readonly os_blocked_clicks: number;
  readonly device_blocked_clicks: number;
  /** #29 — refused by the campaign's own targeting rules. */
  readonly targeting_blocked_clicks: number;
  /**
   * §12.7 — distinct visitors behind the counted clicks. Optional: a response
   * from a build that predates the column carries none, which is different
   * from a source that reached nobody.
   */
  readonly reach?: number;
  /**
   * §39.6 — one entry per dimension when several were grouped at once, so the
   * table can give each its own column. Absent for a single dimension, which
   * is the shape this row has always had.
   */
  readonly values?: readonly { dimension: string; value: string | null }[];
  /**
   * The row's own identifier, where the thing grouped has one — a campaign, a
   * landing page, a creative. Null for a sub-ID, a country or a referrer,
   * where the value *is* the thing.
   */
  readonly value_id?: string | null;
}

function exclusionFromRow(row: PerformanceReportRow): {
  readonly excluded: number;
  readonly excludedNote?: string;
} {
  const bot = row.bot_clicks ?? 0;
  const duplicate = row.duplicate_clicks ?? 0;
  const geo = row.geo_blocked_clicks ?? 0;
  const os = row.os_blocked_clicks ?? 0;
  const device = row.device_blocked_clicks ?? 0;
  const targeting = row.targeting_blocked_clicks ?? 0;
  const excluded = bot + duplicate + geo + os + device + targeting;
  const causes: string[] = [];
  if (bot > 0) causes.push(`${bot} automated`);
  if (duplicate > 0) causes.push(`${duplicate} duplicate`);
  if (geo > 0) causes.push(`${geo} geo-blocked`);
  if (os > 0) causes.push(`${os} OS-blocked`);
  if (device > 0) causes.push(`${device} device-blocked`);
  if (targeting > 0) causes.push(`${targeting} outside targeting`);
  return {
    excluded,
    ...(causes.length > 0 ? { excludedNote: causes.join(', ') } : {}),
  };
}

export interface PerformanceRow {
  readonly value: string;
  readonly counted: number;
  readonly excluded: number;
  readonly excludedNote?: string;
  /** §12.5c — the share of this source that was automated. */
  readonly botShare: string;
  /** Set where the share is high enough to be worth acting on. */
  readonly warning?: string;
  /** §12.7 — distinct visitors behind `counted`, or a dash where unknown. */
  readonly reach: string;
  /** Set only where the grouped thing has an id of its own. */
  readonly valueId?: string;
  /**
   * Clicks per visitor, to one decimal place.
   *
   * The figure that separates "a thousand people came once" from "one person
   * came a thousand times" — two sources with identical click counts and
   * opposite worth, which a click total alone cannot tell apart.
   */
  readonly frequency: string;
  /**
   * This row's share of the range's counted clicks, 0–100.
   *
   * Share of the total shown rather than width against the largest row: the
   * question a country or referrer breakdown asks is "how much of my traffic
   * is this", and a bar scaled to the biggest row answers a different one.
   * The brand console has quoted this figure since before the affiliate one
   * could, which is the whole reason it is here.
   */
  readonly sharePct: number;
  /** §39.6 — one cell per grouped dimension, where several were asked for. */
  readonly parts?: readonly string[];
  /** The raw `US` / `US-NJ` key on a country row, for the map. */
  readonly geo?: string;
}

export interface PerformanceView {
  readonly updated: string;
  readonly rows: readonly PerformanceRow[];
  readonly unlabelled: string;
  /** §39.6 — the dimensions grouped, in order, where more than one was asked for. */
  readonly columns?: readonly string[];
}

/**
 * A breakdown an affiliate buys traffic against.
 *
 * §12.5c is why bot clicks appear per row rather than only in the total:
 * "affiliates buying traffic need this to police their own sources". A source
 * that is 40% automated is one placement, not a fraction of a global number,
 * and finding it is the entire point of labelling placements.
 */
export function performance(
  report: Envelope<PerformanceReportRow[]>,
  now: Date,
  dimension?: string,
): PerformanceView {
  const isLabelDimension =
    dimension === 'link_label' || (dimension?.startsWith('sub_id_') ?? false);
  const shown = report.data.filter(
    (row) => !isLabelDimension || (row.value !== null && row.value.trim() !== ''),
  );
  /*
   * Denominator is the counted clicks across the rows actually *shown*, so the
   * shares add to 100 for what is on screen rather than to some fraction of a
   * total the reader cannot see — a label dimension hides its unlabelled rows,
   * and a share reckoned against those would never add up on the page.
   */
  const countedTotal = shown.reduce((n, row) => n + row.clicks, 0);
  return {
    updated: freshness(report.data_as_of, now),
    rows: shown
      .map((row) => {
      const { excluded, excludedNote } = exclusionFromRow(row);
      const total = row.clicks + excluded;
      const bot = row.bot_clicks ?? 0;
      const share = total === 0 ? 0 : Math.round((bot / total) * 100);
      return {
        value: dimension === 'geo'
          ? geoLabel(row.value, t('perf.noCountry'))
          : row.value ?? t('perf.noLabel'),
        ...(dimension === 'geo' && row.value ? { geo: row.value } : {}),
        ...(row.values
          ? {
            parts: row.values.map((v) =>
              v.dimension === 'geo' ? geoLabel(v.value, t('perf.noCountry')) : v.value ?? t('perf.noLabel')),
          }
          : {}),
        counted: row.clicks,
        sharePct: countedTotal === 0 ? 0 : Math.round((row.clicks / countedTotal) * 1000) / 10,
        excluded,
        ...(excludedNote !== undefined ? { excludedNote } : {}),
        botShare: `${share}%`,
        /*
         * §12.7 — a dash rather than a nought where the response carried no
         * reach at all, for the reason impressions read as a dash on the
         * dashboard: "nobody was reached" and "this was not measured" are
         * different claims, and only one of them is ours to make.
         *
         * Frequency is derived here rather than sent, so the two figures on
         * the row can never disagree with each other.
         */
        ...(row.value_id ? { valueId: row.value_id } : {}),
        reach: row.reach === undefined ? UNAVAILABLE : formatCount(row.reach),
        frequency:
          row.reach === undefined || row.reach === 0
            ? UNAVAILABLE
            : (row.clicks / row.reach).toFixed(1),
        ...(share >= 20
          ? {
              warning: t('perf.botWarning', { share }),
            }
          : {}),
      };
    }),
    unlabelled: isLabelDimension
      ? t('perf.unlabelled.labels')
      : dimension === 'creative'
        ? t('perf.unlabelled.creative')
        : dimension === 'referrer'
          ? t('perf.unlabelled.referrer')
          : dimension === 'geo'
            ? t('perf.unlabelled.geo')
            : t('perf.unlabelled.other'),
    ...(shown[0]?.values
      ? {
        columns: shown[0].values.map((v) =>
          v.dimension.startsWith('sub_id_')
            ? t('dim.sub_id', { slot: `s${v.dimension.slice('sub_id_'.length)}` })
            : t(`dim.${v.dimension}` as AffiliateMessageKey)),
      }
      : {}),
  };
}

/** §39.9 — export the table the screen is showing, not a second query. */
/**
 * What a download carries beyond the rows themselves.
 *
 * Both default to off, which is the shape every existing caller gets: raw
 * digits and no totals line. A spreadsheet reads `1234567` and a person reads
 * `1,234,567`, and the file cannot be both — so which one it is becomes the
 * downloader's choice rather than ours, the same way the reference console
 * asks.
 */
export interface ExportOptions {
  /**
   * Group the counts for a reader. Off by default: a grouped number is text
   * to every spreadsheet that opens the file, and somebody summing a column
   * that silently became text gets zero rather than an error.
   */
  readonly formatted?: boolean;
  /** Append a row totalling the counts — what the screen shows above them. */
  readonly totals?: boolean;
  /**
   * Add the row's own identifier where it has one.
   *
   * For reconciling a download against another system: a campaign renamed on
   * our side is the same campaign, and only its id says so.
   */
  readonly referenceIds?: boolean;
}

/** The counts a totals row sums. Kept beside both writers so they agree. */
function performanceTotals(rows: readonly PerformanceRow[]): {
  counted: number;
  excluded: number;
} {
  return {
    counted: rows.reduce((n, r) => n + r.counted, 0),
    excluded: rows.reduce((n, r) => n + r.excluded, 0),
  };
}

function exportCount(n: number, options: ExportOptions | undefined): string {
  return options?.formatted ? formatCount(n) : String(n);
}

export function performanceCsv(
  title: string,
  rows: readonly PerformanceRow[],
  options?: ExportOptions,
): string {
  const header = [
    t('csv.value'),
    t('csv.countedClicks'),
    t('col.share'),
    t('csv.excluded'),
    t('csv.whyExcluded'),
    t('csv.automatedShare'),
    t('col.reach'),
    t('col.frequency'),
    ...(options?.referenceIds ? [t('csv.referenceId')] : []),
  ];
  // Guarded against running as a formula, like every other download (csvCell).
  const cell = csvCell;
  const lines = rows.map((r) =>
    [r.value, exportCount(r.counted, options), `${r.sharePct}%`,
     exportCount(r.excluded, options), r.excludedNote ?? '',
     r.botShare, r.reach, r.frequency,
     ...(options?.referenceIds ? [r.valueId ?? ''] : [])]
      .map(cell)
      .join(','),
  );
  if (options?.totals) {
    const totals = performanceTotals(rows);
    // Named in the reader's own language, and last — a totals row sorted into
    // the middle of a spreadsheet is a row somebody sums twice.
    lines.push(
      [t('csv.total'), exportCount(totals.counted, options), '',
       exportCount(totals.excluded, options),
       '', '', '', '', ...(options?.referenceIds ? [''] : [])]
        .map(cell)
        .join(','),
    );
  }
  return [`# ${title}`, header.join(','), ...lines].join('\n');
}

/**
 * One escaped attribute, name and value together.
 *
 * The quote is a constant rather than written into the template, because
 * `xmlText` has already turned every quote in the value into `&quot;` and
 * JSON.stringify would additionally escape backslashes the way JSON wants
 * them, which is not the way XML does.
 */
const XML_QUOTE = '"';
function xmlAttr(name: string, value: string): string {
  return `${name}=${XML_QUOTE}${xmlText(value)}${XML_QUOTE}`;
}

/** XML's five, so a dimension value containing one cannot end a tag early. */
function xmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * The same report as XML.
 *
 * Offered beside the CSV because the two are read by different things: a
 * person opens the CSV in a spreadsheet, and an affiliate's own reporting
 * stack ingests the XML on a schedule. The element names are stable and in
 * English — they are a wire format, not a screen, and a feed whose tags change
 * with the reader's language is a feed that breaks when somebody switches
 * their console to Spanish.
 */
export function performanceXml(
  title: string,
  rows: readonly PerformanceRow[],
  options?: ExportOptions,
): string {
  const row = (r: PerformanceRow) =>
    [
      '  <row>',
      `    <value>${xmlText(r.value)}</value>`,
      `    <counted>${xmlText(exportCount(r.counted, options))}</counted>`,
      `    <share>${xmlText(`${r.sharePct}%`)}</share>`,
      `    <excluded>${xmlText(exportCount(r.excluded, options))}</excluded>`,
      `    <whyExcluded>${xmlText(r.excludedNote ?? '')}</whyExcluded>`,
      `    <automatedShare>${xmlText(r.botShare)}</automatedShare>`,
      `    <reach>${xmlText(r.reach)}</reach>`,
      `    <frequency>${xmlText(r.frequency)}</frequency>`,
      ...(options?.referenceIds ? [`    <referenceId>${xmlText(r.valueId ?? '')}</referenceId>`] : []),
      '  </row>',
    ].join('\n');

  const body = rows.map(row);
  if (options?.totals) {
    const totals = performanceTotals(rows);
    body.push(
      [
        '  <totals>',
        `    <counted>${xmlText(exportCount(totals.counted, options))}</counted>`,
        `    <excluded>${xmlText(exportCount(totals.excluded, options))}</excluded>`,
        '  </totals>',
      ].join('\n'),
    );
  }

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    // Built through `xmlAttr` rather than written out. Spelling the pair
    // inline reads as a JSX `title=` prop to the console-text check, which
    // then asks for an XML attribute name to be translated — and an escaping
    // helper is the better shape for it regardless.
    `<report ${xmlAttr('title', title)}>`,
    ...body,
    '</report>',
  ].join('\n');
}

/* --- The detailed activity report — one row per day --- */

export interface DetailedActivityRow {
  readonly key: string;
  readonly day: string;
  /** Empty on a row that carries only traffic, which has no currency. */
  readonly currency: string;
  readonly clicks: string;
  readonly impressions: string;
  readonly signups: string;
  readonly ftds: string;
  readonly deposits: string;
  readonly withdrawals: string;
  readonly chargebacks: string;
  readonly adjustedNetDeposits: string;
  readonly commission: string;
}

export interface DetailedActivityView {
  readonly updated: string;
  readonly rows: readonly DetailedActivityRow[];
  readonly currencyNote?: string;
}

/**
 * Traffic and money for a day, on one row.
 *
 * Money is printed only where the row carries a currency. A row with clicks
 * and no currency is a day that had traffic and no settled activity, and
 * printing "0.00" against it would assert that the traffic earned nothing
 * rather than that nothing has arrived yet.
 *
 * The note appears only where more than one currency is on screen, because
 * that is the only time the reader needs telling that the columns do not add
 * up down the page.
 */
export function detailedActivityView(
  report: Envelope<readonly {
    day: string; currency: string | null;
    clicks: number; impressions: number; signups: number; ftd_count: number;
    deposits_minor: string; withdrawals_minor: string; chargebacks_minor: string;
    adjusted_net_deposits_minor: string; commission_minor: string;
  }[]>,
  now: Date,
): DetailedActivityView {
  const currencies = new Set(report.data.map((r) => r.currency).filter(Boolean));
  const rows = report.data.map((r): DetailedActivityRow => {
    const m = (minor: string) =>
      r.currency ? formatMoney({ amount_minor: minor, currency: r.currency }) : UNAVAILABLE;
    return {
      key: `${r.day}|${r.currency ?? ''}`,
      day: formatDay(r.day),
      currency: r.currency ?? '',
      clicks: formatCount(r.clicks),
      impressions: r.impressions === 0 ? UNAVAILABLE : formatCount(r.impressions),
      signups: formatCount(r.signups),
      ftds: formatCount(r.ftd_count),
      deposits: m(r.deposits_minor),
      withdrawals: m(r.withdrawals_minor),
      chargebacks: m(r.chargebacks_minor),
      adjustedNetDeposits: m(r.adjusted_net_deposits_minor),
      commission: m(r.commission_minor),
    };
  });
  return {
    updated: freshness(report.data_as_of, now),
    rows,
    ...(currencies.size > 1 ? { currencyNote: t('detailedActivity.currencyNote') } : {}),
  };
}

/* --- Clicks and impressions, a column pair per brand --- */

export interface ClicksImpressionsCell {
  readonly brand: string;
  readonly impressions: string;
  readonly clicks: string;
  readonly reach: string;
  readonly ctr: string;
}

export interface ClicksImpressionsRow {
  readonly day: string;
  readonly perBrand: readonly ClicksImpressionsCell[];
  readonly totalImpressions: string;
  readonly totalClicks: string;
  readonly overallReach: string;
  readonly overallCtr: string;
  /** Clicks per visitor across every brand on the row. */
  readonly overallFrequency: string;
}

export interface ClicksImpressionsView {
  readonly updated: string;
  /** Every brand seen in the range, so each row lays out the same columns. */
  readonly brands: readonly string[];
  readonly rows: readonly ClicksImpressionsRow[];
}

/**
 * Impressions and clicks per day, split by brand rather than summed.
 *
 * The brand list is computed across the whole range and not per row, so a day
 * one brand served nothing still lays out that brand's column — a table whose
 * columns move from row to row cannot be read down, which is the only way this
 * one is worth reading.
 *
 * Reach is summed across brands rather than recomputed: the same visitor on
 * two brands is two rows in the source and the API counts distinct visitors
 * per (day, brand). Summing overstates a visitor who did both, so the figure
 * is named "overall reach" and not "people", and frequency is derived from it
 * consistently rather than from a second number that would disagree.
 */
export function clicksImpressionsView(
  report: Envelope<readonly {
    day: string; brand: string | null;
    impressions: number; clicks: number; reach: number;
  }[]>,
  now: Date,
): ClicksImpressionsView {
  const brands = [...new Set(report.data.map((r) => r.brand ?? UNAVAILABLE))].sort();
  const byDay = new Map<string, Map<string, { impressions: number; clicks: number; reach: number }>>();
  for (const r of report.data) {
    const day = byDay.get(r.day) ?? new Map();
    day.set(r.brand ?? UNAVAILABLE, {
      impressions: r.impressions, clicks: r.clicks, reach: r.reach,
    });
    byDay.set(r.day, day);
  }

  const ratio = (clicks: number, impressions: number): string =>
    impressions === 0 ? UNAVAILABLE : `${((clicks / impressions) * 100).toFixed(2)}%`;

  return {
    updated: freshness(report.data_as_of, now),
    brands,
    rows: [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, cells]) => {
      const totals = [...cells.values()].reduce(
        (acc, c) => ({
          impressions: acc.impressions + c.impressions,
          clicks: acc.clicks + c.clicks,
          reach: acc.reach + c.reach,
        }),
        { impressions: 0, clicks: 0, reach: 0 },
      );
      return {
        day: formatDay(day),
        perBrand: brands.map((brand) => {
          const c = cells.get(brand) ?? { impressions: 0, clicks: 0, reach: 0 };
          return {
            brand,
            impressions: c.impressions === 0 ? UNAVAILABLE : formatCount(c.impressions),
            clicks: formatCount(c.clicks),
            reach: c.reach === 0 ? UNAVAILABLE : formatCount(c.reach),
            ctr: ratio(c.clicks, c.impressions),
          };
        }),
        totalImpressions: totals.impressions === 0 ? UNAVAILABLE : formatCount(totals.impressions),
        totalClicks: formatCount(totals.clicks),
        overallReach: totals.reach === 0 ? UNAVAILABLE : formatCount(totals.reach),
        overallCtr: ratio(totals.clicks, totals.impressions),
        overallFrequency: totals.reach === 0
          ? UNAVAILABLE
          : (totals.clicks / totals.reach).toFixed(1),
      };
    }),
  };
}

/* --- §27.9f — the players held out of revenue share --- */

export interface PlayerQuarantineRow {
  readonly playerRef: string;
  /** Where the player stands overall. Negative while they are behind. */
  readonly lifetime: string;
  /** What is left to close before they count again. */
  readonly shortfall: string;
}

export interface PlayerQuarantineView {
  readonly updated: string;
  readonly rows: readonly PlayerQuarantineRow[];
  /** Set where the brand does not run the policy at all. */
  readonly offBecause?: string;
  readonly empty: string;
  readonly lead: string;
}

/**
 * Who is behind, and by how much.
 *
 * Two different empties, and the difference is the point: a brand that does not
 * run the policy has nobody quarantined *and cannot have*, which is a different
 * statement from a brand that runs it and has everybody in good standing. A
 * single "no players" line would let the first read as the second.
 */
export function playerQuarantineView(
  report: Envelope<readonly {
    player_ref: string; currency: string;
    lifetime_ngr_minor: string; shortfall_minor: string;
  }[]> & { unavailable_because?: string },
  now: Date,
): PlayerQuarantineView {
  return {
    updated: freshness(report.data_as_of, now),
    rows: report.data.map((r) => ({
      playerRef: r.player_ref,
      lifetime: formatMoney({ amount_minor: r.lifetime_ngr_minor, currency: r.currency }),
      shortfall: formatMoney({ amount_minor: r.shortfall_minor, currency: r.currency }),
    })),
    ...(report.unavailable_because ? { offBecause: report.unavailable_because } : {}),
    empty: t('quarantine.empty'),
    lead: t('quarantine.lead'),
  };
}

/* ------------------------------------------------------------------ *
 * Held lines — §24.5b
 * ------------------------------------------------------------------ */

export interface HeldLineView {
  readonly id: string;
  readonly period: string;
  readonly brand: string;
  readonly kind: string;
  readonly amount: string;
  readonly reason: string;
  readonly releaseOn: string;
}

/**
 * §24.5b — held lines and their release date.
 *
 * "When will I be paid?" must be answerable from the affiliate's own dashboard.
 */
export function heldLines(
  items: readonly {
    id: string;
    type: string;
    amount_minor: string;
    currency: string;
    brand: string;
    period_label: string;
    reason: string;
    release_on: string | null;
  }[],
): readonly HeldLineView[] {
  return items.map((row) => ({
    id: row.id,
    period: row.period_label,
    brand: row.brand,
    // QA round 2 F2 — the kind in words, the same words the Periods table uses.
    kind: describeKind(row.type, row.amount_minor),
    amount: formatMoney({ amount_minor: row.amount_minor, currency: row.currency }),
    reason: row.reason,
    releaseOn: row.release_on ? formatDate(row.release_on) : t('held.noReleaseDate'),
  }));
}

/**
 * QA round 2 F4 — the revenue deficit still carried, one figure per brand and
 * currency.
 *
 * The API sums every non-void carryover line (`carryover_deficits` on
 * `/v1/statements`): below zero is a deficit this affiliate's revenue share
 * earns back before anything is payable. It is shown as the size of the
 * deficit, a positive figure, because the label already says it is owed back
 * and "−300.00" under "Deficit" reads as a double negative. Because the sign
 * is dropped, only a sum below zero is shown: a positive or zero sum is not a
 * deficit (nothing is owed back), and printed unsigned under the same label
 * +400.00 would read exactly as a 400.00 deficit does. An unreadable figure is
 * left out for the same reason. An older response without the field shows
 * nothing.
 */
export function carryoverDeficitFigures(rows: readonly CarryoverDeficit[] | undefined): readonly FigureView[] {
  return (rows ?? []).flatMap((r) => {
    const raw = /^-?\d+$/.test(r.deficit_minor) ? BigInt(r.deficit_minor) : 0n;
    if (raw >= 0n) return [];
    return [{
      label: t('carryover.deficit.label' as AffiliateMessageKey, { brand: r.brand }),
      value: formatMoney({ amount_minor: (-raw).toString(), currency: r.currency }),
      provisional: false,
      note: t('carryover.note' as AffiliateMessageKey),
    }];
  });
}

/**
 * D3 — the country on a click comes from DB-IP's free database, whose licence
 * asks for a credit and a link wherever the data is shown. The words go
 * through the catalogue; the address is the same in every language.
 */
export function ipGeolocationAttribution(): { readonly text: string; readonly href: string } {
  return { text: t('attribution.ipGeolocation' as AffiliateMessageKey), href: 'https://db-ip.com' };
}

/* ------------------------------------------------------------------ *
 * Conversion sampling — §39.13
 * ------------------------------------------------------------------ */

/**
 * §39.13 — the rate, as the campaign states it to the affiliate.
 *
 * Said in terms of what it does — a share of qualifying first deposits that
 * earn nothing — rather than as "sampling", which reads as a reporting
 * sample. "None" is a sentence too: an affiliate who cannot see that
 * sampling is off cannot tell it from not being told.
 */
export function samplingRateLabel(rateBps: number): string {
  return rateBps > 0
    ? t('sampling.rate', { rate: (rateBps / 100).toFixed(2) })
    : t('sampling.none');
}

/** §39.13 — the same rate, short enough for a list row. Null when off. */
export function samplingChip(rateBps: number): string | null {
  return rateBps > 0 ? t('sampling.chip', { rate: (rateBps / 100).toFixed(2) }) : null;
}

export interface PeriodSamplingView {
  readonly rows: readonly { campaign: string; rate: string; sampledAway: number }[];
  /** The sentence under the table, or the whole answer when nothing was sampled. */
  readonly note: string;
}

/**
 * §39.13 — what sampling did to one period's statement, per campaign.
 *
 * A sampled-away conversion "MUST NOT be presented as a failed qualification,
 * and MUST NOT simply be absent". The server's note says the first; this
 * function is what stops the second — a period with nothing sampled still
 * says so, rather than showing nothing.
 */
export function periodSampling(
  sampling:
    | {
        by_campaign: readonly { campaign: string; rate_bps: number; sampled_away: number }[];
        note: string;
      }
    | undefined,
): PeriodSamplingView {
  if (!sampling || sampling.by_campaign.length === 0) {
    return { rows: [], note: t('sampling.allPaid') };
  }
  return {
    rows: sampling.by_campaign.map((s) => ({
      campaign: s.campaign,
      rate: `${(s.rate_bps / 100).toFixed(2)}%`,
      sampledAway: s.sampled_away,
    })),
    note: sampling.note,
  };
}

/* ------------------------------------------------------------------ *
 * Smart links — #46
 * ------------------------------------------------------------------ */

export interface SmartLinkPoolEntry {
  readonly name: string;
  readonly kind: 'campaign' | 'category';
  /** True where a click could go there today; null where the answer is a set. */
  readonly reachable: boolean | null;
}

export interface SmartLinkView {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly chooses: string;
  readonly pool: readonly SmartLinkPoolEntry[];
  readonly clicks: string;
  readonly unmatched: string;
  readonly off: boolean;
  /** Set where something about the pool is worth saying out loud. */
  readonly warning: string;
}

/**
 * One smart link, as the affiliate reads it.
 *
 * Two things are decided here rather than shown raw, and both are about a
 * failure that is otherwise silent:
 *
 * **A campaign in the pool that the affiliate is not on.** It is not an error
 * — the pool deliberately keeps it, and the click skips it until they are
 * added — but a list that shows it identically to a working member is one
 * where somebody wonders for a month why a campaign never gets traffic.
 *
 * **Unmatched clicks.** A click no member would take still reached the
 * brand's page, so nothing looks broken anywhere: the affiliate's click count
 * is up and their conversions are not. It is called out with the number,
 * because the fix — widen the pool, or ask to be added to a campaign — is
 * theirs to make and they cannot make it from a figure they never see.
 */
export function smartLinkView(
  row: {
    id: string;
    name: string;
    selection: string;
    status: string;
    token: string | null;
    url: string | null;
    members: readonly {
      name: string | null;
      kind: 'campaign' | 'category';
      participating: boolean | null;
    }[];
    clicks_30d: number;
    unmatched_30d: number;
  },
): SmartLinkView {
  const pool = row.members.map((m) => ({
    name: m.name ?? '—',
    kind: m.kind,
    // A category is a set that changes; asking whether "it" is reachable is
    // the wrong question, and answering false would read as broken.
    //
    // For a campaign the test is `!== false`, not `=== true`, and the
    // difference is a whole state: null means there is no pairing row at all,
    // which 0138 deliberately treats as *not* blocking. Reading it as
    // unreachable would tell an affiliate a campaign will never take their
    // traffic when in fact it takes all of it.
    reachable: m.kind === 'category' ? null : m.participating !== false,
  }));

  const unreachable = pool.filter((p) => p.reachable === false).length;

  return {
    id: row.id,
    name: row.name,
    url: row.url ?? '',
    chooses: row.selection === 'SPREAD' ? t('smart.chooses.spread') : t('smart.chooses.priority'),
    pool,
    clicks: formatCount(row.clicks_30d),
    unmatched: formatCount(row.unmatched_30d),
    off: row.status !== 'ACTIVE',
    warning:
      row.unmatched_30d > 0
        ? t('smart.unmatched.hint')
        : unreachable > 0
          ? t('smart.notOn.hint')
          : '',
  };
}


/* ------------------------------------------------------------------ *
 * Players → Qualification: sorting by the placement that brought the player.
 * ------------------------------------------------------------------ */



/* ------------------------------------------------------------------ *
 * Statements → Details: every line behind the figure, and how it was counted.
 * ------------------------------------------------------------------ */

export interface StatementLineRow {
  readonly key: string;
  readonly player: string;
  readonly campaign: string;
  readonly link: string;
  readonly kind: string;
  /** "one-off ₦300 per qualified acquisition" · "5.00% of NGR ₦200.00" */
  readonly how: string;
  readonly amount: string;
  readonly state: string;
}

/** §30.2a — how much of earned was brought forward from earlier statements. */
export function statementBroughtForwardAmount(statement: {
  readonly brought_forward_minor?: string;
  readonly currency?: string;
}): string | null {
  const minor = String(statement.brought_forward_minor ?? '0');
  if (minor === '0') return null;
  return formatMoney({ amount_minor: minor, currency: String(statement.currency ?? '') });
}

/**
 * §30.2 — a statement is a total, and a total is checked line by line. Each
 * row says who, through which campaign and placement, what kind of commission,
 * what it was taken of, and how much — the affiliate's own arithmetic.
 */
export function statementLineRows(
  lines: readonly {
    readonly type: string;
    readonly product?: string | null;
    readonly amount_minor: string;
    readonly currency: string;
    readonly state?: string;
    readonly player_ref?: string | null;
    readonly basis_amount_minor?: string | null;
    readonly basis_kind?: string | null;
    readonly rate_bps?: number | null;
    readonly campaign?: string | null;
    readonly link_label?: string | null;
    /** §8.2 — the recruit a SUB_OVERRIDE was earned on. */
    readonly source_affiliate?: string | null;
    /** L31 — why an adjustment or clawback moved money, as the operator recorded it. */
    readonly adjustment_reason_code?: string | null;
    readonly adjustment_justification?: string | null;
    readonly clawback_ground?: string | null;
    /** §34.4 — why a hold the operator placed keeps this line back, while it does. */
    readonly hold_reason?: string | null;
  }[],
): readonly StatementLineRow[] {
  return lines.map((row, i) => {
    const amount = formatMoney({ amount_minor: row.amount_minor, currency: row.currency });
    let how: string;
    if (row.basis_amount_minor != null && row.rate_bps != null) {
      how = t('statementLine.share', {
        rate: `${(row.rate_bps / 100).toFixed(2)}%`,
        // QA round 2 F2 — "NGR (net gaming revenue)", never the bare code.
        basis: row.basis_kind ? revenueBasisLabel(row.basis_kind) : '',
        amount: formatMoney({ amount_minor: row.basis_amount_minor, currency: row.currency }),
      });
    } else if (/CPA/.test(row.type)) {
      how = t('statementLine.perAcquisition', { amount });
    } else {
      how = '';
    }
    /*
     * An override has no player, campaign or link, so three of this row's
     * columns are a dash and the fourth says the same words on every one of
     * them. Naming the recruit here rather than in a column of its own: the
     * column would be empty on every line that is not an override, and this
     * one already exists to explain where the amount came from.
     */
    if (row.source_affiliate) {
      how = how
        ? t('statementLine.earnedOn', { how, affiliate: row.source_affiliate })
        : t('statementLine.earnedOnAlone', { affiliate: row.source_affiliate });
    }
    /*
     * L31 — an adjustment or a clawback is the line an affiliate is most
     * likely to question, and the one this column said nothing about. The
     * reason is the operator's own, the same words the commission report
     * already shows beside it (`explainKind`).
     */
    if (row.adjustment_reason_code) {
      const why = row.adjustment_reason_code.replaceAll('_', ' ').toLowerCase();
      how = row.adjustment_justification ? `${why} — ${row.adjustment_justification}` : why;
    } else if (row.clawback_ground) {
      how = t('line.clawback.ground', { ground: row.clawback_ground.replaceAll('_', ' ').toLowerCase() });
    }
    return {
      key: `${row.type}:${row.player_ref ?? row.source_affiliate ?? ''}:${i}`,
      player: row.player_ref ?? '—',
      campaign: row.campaign ?? '—',
      link: row.link_label ?? '—',
      kind: describeKind(row.type, row.amount_minor),
      how,
      amount,
      // §30.2a, §34.4 — a line a hold covers says why, beside the word.
      state: row.hold_reason
        ? `${describeLineState(row.state ?? '')} — ${row.hold_reason}`
        : describeLineState(row.state ?? ''),
    };
  });
}

/* ------------------------------------------------------------------ *
 * Statements → Details: the calculation, deal by deal
 * ------------------------------------------------------------------ */

/** One statement line, as `/v1/statements/:period` sends it. */
export interface CalculationLineInput {
  readonly type: string;
  readonly product?: string | null;
  readonly amount_minor: string;
  readonly currency: string;
  readonly state?: string;
  readonly player_ref?: string | null;
  readonly basis_amount_minor?: string | null;
  readonly basis_kind?: string | null;
  readonly rate_bps?: number | null;
  readonly deal_id?: string | null;
  readonly deal_version_id?: string | null;
  /** §20.4b — what came off GGR to reach the basis, in the stack's order; zero rows are never sent. */
  readonly deductions?: readonly {
    readonly item: string;
    readonly amount_minor: string;
    readonly running_total_minor: string;
  }[];
  readonly source_affiliate?: string | null;
  readonly adjustment_reason_code?: string | null;
  readonly adjustment_justification?: string | null;
  readonly clawback_ground?: string | null;
}

/** The terms one deal version priced the statement's lines on (`deals` on the statement). */
export interface CalculationDealInput {
  readonly deal_id: string;
  readonly deal_version_id: string;
  readonly version: number;
  readonly brand: string;
  readonly campaign_name: string | null;
  readonly commission_type: string;
  readonly revenue_basis: string;
  readonly revshare_rate_bps: number | null;
  readonly cpa_amount_minor: string | null;
  readonly cpa_currency: string | null;
  readonly deduction_stack: readonly DeductionStepInput[];
  readonly cpa_criteria: readonly {
    readonly criterion: string;
    readonly threshold_minor: string | null;
    readonly threshold_count: number | null;
    readonly label: string | null;
    readonly applies?: boolean;
  }[];
}

/** `/v1/statements/:period/qualification` — first deposits that earned no CPA, per deal. */
export interface CalculationOutcomesInput {
  readonly assessed_at: string;
  readonly data: readonly {
    readonly deal_id: string | null;
    readonly not_qualified: readonly { readonly reason: string; readonly players: number }[];
    readonly not_yet_decided: number;
  }[];
}

export interface CalculationRow {
  readonly label: string;
  /** The figure or count; a deduction reads as a negative amount. */
  readonly value: string;
  /** §20.4b — the running balance after a deduction, where the row is one. */
  readonly balance?: string;
  /** Why, or what the figure is not part of, in words. */
  readonly detail?: string;
  /** A subtotal the next rows work from, or the figure the part pays. */
  readonly tone?: 'subtotal' | 'total' | 'sub';
}

export interface CalculationPart {
  readonly key: string;
  readonly title: string;
  readonly rows: readonly CalculationRow[];
  readonly note?: string;
}

export interface CalculationDeal {
  readonly key: string;
  readonly title: string;
  readonly parts: readonly CalculationPart[];
  readonly total: CalculationRow;
}

export interface StatementCalculationView {
  readonly deals: readonly CalculationDeal[];
  readonly reconciliation: {
    readonly rows: readonly CalculationRow[];
    /** False when the lines no longer add up to the issued Earned (a restatement since issue). */
    readonly agrees: boolean;
    readonly mismatch?: string;
  };
  /** The sum of the deal totals, in minor units of the statement's currency. */
  readonly periodMinor: string;
  /** Period + brought forward − held, as worked here; equals the statement's payable when `agrees`. */
  readonly payableMinor: string;
  /** When the not-qualified counts were judged, or why they are missing. */
  readonly outcomesNote: string | null;
}

/** The states a line is in the statement's Earned in (payments.ts issues on these). */
const IN_STATEMENT: ReadonlySet<string> = new Set(['EARNED', 'PAYABLE', 'PAID', 'HELD']);
const REVSHARE_LINES: ReadonlySet<string> = new Set(['REVSHARE', 'HYBRID_RS']);
const CPA_LINES: ReadonlySet<string> = new Set(['CPA', 'HYBRID_CPA', 'RECURRING_CPA']);
const ADJUSTMENT_LINES: ReadonlySet<string> = new Set(['ADJUSTMENT', 'CLAWBACK']);

function negated(minor: string): string {
  return (-BigInt(minor)).toString();
}

function sentenceCase(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * §30.2, §33.4a — the statement worked deal by deal, from the brand's figures
 * to the payable, so an affiliate can redo every step and arrive at the same
 * number.
 *
 * Every figure is one the statement already carries — the lines, their
 * deductions with running totals, the terms of the version that priced each
 * line — and nothing is recomputed: the RevShare commission is the line's own
 * amount, not the basis times the rate, because a line can pay less than that
 * where it recovered a carried deficit, and a recomputed figure that differs
 * from the one paid is the dispute this screen exists to prevent. The last
 * block adds the deals up and takes them to the statement's own Earned, Held
 * and Payable, and says so when the two no longer agree.
 *
 * Labels follow the owner's naming: NGR is GGR less bonus; Commissionable NGR
 * is the figure after the administrative fee and any other agreed deduction,
 * the one the RevShare rate is applied to.
 */
export function statementCalculation(
  statement: Record<string, unknown>,
  lines: readonly CalculationLineInput[],
  deals: readonly CalculationDealInput[],
  outcomes: CalculationOutcomesInput | null | 'unavailable',
): StatementCalculationView {
  const currency = String(statement['currency'] ?? '');
  const money = (minor: string, cur: string = currency) => formatMoney({ amount_minor: minor, currency: cur });
  const counts = (l: CalculationLineInput) =>
    IN_STATEMENT.has(l.state ?? '') && l.type !== 'CARRYOVER' && l.currency === currency;
  // Why a line's figure is outside the totals, or nothing when it is inside.
  const outside = (l: CalculationLineInput): string | undefined => {
    if (l.type === 'CARRYOVER') return t('calc.carryoverNote');
    if (l.currency !== currency) return t('calc.otherCurrency', { currency: l.currency });
    if (!IN_STATEMENT.has(l.state ?? '')) return t('calc.notCounted', { state: describeLineState(l.state ?? '') });
    return undefined;
  };
  const sum = (ls: readonly CalculationLineInput[]) =>
    ls.filter(counts).reduce((acc, l) => acc + BigInt(l.amount_minor), 0n);

  const termsByVersion = new Map(deals.map((d) => [d.deal_version_id, d] as const));
  const outcomeByDeal = new Map(
    outcomes && outcomes !== 'unavailable' ? outcomes.data.map((o) => [o.deal_id, o] as const) : [],
  );

  // One group per version, in the order the statement lists its deals, then
  // lines under no deal at all.
  const groupKeys: string[] = deals.map((d) => d.deal_version_id);
  const linesByGroup = new Map<string, CalculationLineInput[]>();
  for (const line of lines) {
    const key = line.deal_version_id && termsByVersion.has(line.deal_version_id) ? line.deal_version_id : '';
    if (!groupKeys.includes(key)) groupKeys.push(key);
    linesByGroup.set(key, [...(linesByGroup.get(key) ?? []), line]);
  }
  // The not-qualified players of a deal that paid nothing this period still
  // belong on the page; they hang off a group of their own.
  const outcomeOnlyDeals = [...outcomeByDeal.keys()].filter(
    (dealId) => dealId !== null && !deals.some((d) => d.deal_id === dealId),
  );
  const versionsPerDeal = new Map<string, number>();
  for (const d of deals) versionsPerDeal.set(d.deal_id, (versionsPerDeal.get(d.deal_id) ?? 0) + 1);
  const outcomeClaimed = new Set<string | null>();

  const revsharePart = (line: CalculationLineInput, terms: CalculationDealInput | undefined, i: number): CalculationPart => {
    const rows: CalculationRow[] = [];
    const basis = line.basis_amount_minor ?? null;
    const kind = line.basis_kind ?? null;
    if (basis !== null && (kind === 'NGR' || kind === null)) {
      const steps = line.deductions ?? [];
      const ggr = steps.length > 0
        ? (BigInt(steps[0]!.running_total_minor) + BigInt(steps[0]!.amount_minor)).toString()
        : basis;
      rows.push({ label: t('calc.ggr'), value: money(ggr, line.currency) });
      let rest = steps;
      const bonusAt = steps.findIndex((s) => s.item === 'BONUS_COST');
      if (bonusAt === 0 || bonusAt === -1) {
        // NGR is GGR less bonus; with no bonus taken it is GGR itself.
        const bonus = bonusAt === 0 ? steps[0]! : null;
        rows.push({
          label: deductionItemWords('BONUS_COST'),
          value: money(negated(bonus?.amount_minor ?? '0'), line.currency),
        });
        rows.push({ label: t('calc.ngr'), value: money(bonus?.running_total_minor ?? ggr, line.currency), tone: 'subtotal' });
        rest = bonusAt === 0 ? steps.slice(1) : steps;
      }
      const seen = new Set<string>();
      for (const step of rest) {
        const entry = terms?.deduction_stack.find((s) => s.what === step.item);
        const words = curatedOr(entry?.label, deductionItemWords(step.item));
        let label = words;
        if (step.item === 'ADMINISTRATIVE_FEE' && seen.has(step.item)) {
          // §20.6b — the fee an earlier negative period left out, charged
          // beside this period's own.
          label = t('calc.adminFee.carried');
        } else if (entry?.kind === 'RATE' && typeof entry.rate_bps === 'number') {
          label = t('calc.deduction.rate', { item: words, rate: percent(entry.rate_bps) });
        }
        seen.add(step.item);
        rows.push({
          label,
          value: money(negated(step.amount_minor), line.currency),
          balance: money(step.running_total_minor, line.currency),
        });
      }
      const reached = steps.length > 0 ? steps[steps.length - 1]!.running_total_minor : ggr;
      if (BigInt(reached) !== BigInt(basis)) {
        // §16.4a — netted products: another product's loss came off this one.
        rows.push({
          label: t('calc.offset'),
          value: money((BigInt(basis) - BigInt(reached)).toString(), line.currency),
        });
      }
      rows.push({ label: t('calc.commissionableNgr'), value: money(basis, line.currency), tone: 'subtotal' });
    } else if (basis !== null) {
      rows.push({
        label: kind === 'GGR' ? t('calc.ggr') : revenueBasisLabel(kind ?? ''),
        value: money(basis, line.currency),
        tone: 'subtotal',
      });
    }
    if (line.rate_bps !== null && line.rate_bps !== undefined) {
      rows.push({ label: t('calc.revshareRate'), value: percent(line.rate_bps) });
    } else if (basis !== null) {
      rows.push({ label: t('calc.revshareRate'), value: t('calc.revshareRate.tiered') });
    }
    const why = outside(line);
    rows.push({
      label: t('calc.revshareCommission'),
      value: money(line.amount_minor, line.currency),
      tone: 'total',
      ...(why ? { detail: why } : {}),
    });
    return {
      key: `rs:${i}`,
      title: line.product ? t('calc.revshare.titleProduct', { product: line.product }) : t('calc.revshare.title'),
      rows,
    };
  };

  const cpaPart = (
    cpaLines: readonly CalculationLineInput[],
    terms: CalculationDealInput | undefined,
    outcome: CalculationOutcomesInput['data'][number] | undefined,
  ): CalculationPart => {
    const rows: CalculationRow[] = [];
    rows.push({
      label: t('calc.cpaRate'),
      value: terms?.cpa_amount_minor
        ? money(terms.cpa_amount_minor, terms.cpa_currency ?? currency)
        : UNAVAILABLE,
    });
    const criteria = (terms?.cpa_criteria ?? []).map((c) => {
      const label = curatedOr(c.label, criterionWords(c.criterion));
      if (c.applies === false) return `${label}: ${t('terms.switchedOff')}`;
      if (c.threshold_minor !== null) {
        return `${label}: ${money(c.threshold_minor, terms?.cpa_currency ?? currency)}`;
      }
      if (c.threshold_count !== null) return `${label}: ${formatCount(c.threshold_count)}`;
      return label;
    });
    if (criteria.length > 0) rows.push({ label: t('calc.criteria'), value: criteria.join(' · ') });
    const qualified = new Set(
      cpaLines.filter((l) => IN_STATEMENT.has(l.state ?? '')).map((l, i) => l.player_ref ?? `line:${i}`),
    );
    rows.push({ label: t('calc.qualified'), value: formatCount(qualified.size) });
    if (outcomes === 'unavailable' || outcomes === null) {
      rows.push({ label: t('calc.notQualified'), value: UNAVAILABLE });
    } else {
      const reasons = outcome?.not_qualified ?? [];
      rows.push({
        label: t('calc.notQualified'),
        value: formatCount(reasons.reduce((acc, r) => acc + r.players, 0)),
      });
      for (const r of reasons) {
        rows.push({ label: sentenceCase(reasonWords(r.reason)), value: formatCount(r.players), tone: 'sub' });
      }
      if ((outcome?.not_yet_decided ?? 0) > 0) {
        rows.push({ label: t('calc.notYetDecided'), value: formatCount(outcome!.not_yet_decided) });
      }
    }
    for (const l of cpaLines) {
      const why = outside(l);
      if (why) {
        rows.push({ label: `${describeKind(l.type, l.amount_minor)} ${l.player_ref ?? ''}`.trim(), value: money(l.amount_minor, l.currency), detail: why });
      }
    }
    rows.push({ label: t('calc.cpaCommission'), value: money(sum(cpaLines).toString()), tone: 'total' });
    return { key: 'cpa', title: t('calc.cpa.title'), rows };
  };

  const lineRow = (l: CalculationLineInput): CalculationRow => {
    const why = outside(l);
    const reason = explainKind(l);
    const label = [describeKind(l.type, l.amount_minor), l.player_ref ?? l.source_affiliate ?? ''].filter(Boolean).join(' · ');
    return {
      label,
      value: money(l.amount_minor, l.currency),
      detail: why ? `${reason} — ${why}` : reason,
    };
  };

  const built: CalculationDeal[] = [];
  let period = 0n;
  const groups: { key: string; dealId: string | null }[] = [
    ...groupKeys.map((key) => ({ key, dealId: termsByVersion.get(key)?.deal_id ?? null })),
    ...outcomeOnlyDeals.map((dealId) => ({ key: `deal:${dealId}`, dealId })),
  ];
  for (const { key, dealId } of groups) {
    const terms = termsByVersion.get(key);
    const ls = linesByGroup.get(key) ?? [];
    const parts: CalculationPart[] = [];

    const rsLines = ls.filter((l) => REVSHARE_LINES.has(l.type));
    rsLines.forEach((l, i) => parts.push(revsharePart(l, terms, i)));
    const paysRevshare = terms && (terms.revshare_rate_bps !== null || terms.commission_type === 'REVSHARE' || terms.commission_type === 'HYBRID');
    if (rsLines.length === 0 && paysRevshare) {
      parts.push({ key: 'rs:none', title: t('calc.revshare.title'), rows: [], note: t('calc.noRevshareLine') });
    }

    const cpaLines = ls.filter((l) => CPA_LINES.has(l.type));
    // One version of a deal carries the deal's not-qualified count; a deal
    // repriced mid-period would otherwise count each player twice.
    const outcome = dealId !== null && !outcomeClaimed.has(dealId) ? outcomeByDeal.get(dealId) : undefined;
    if (outcome) outcomeClaimed.add(dealId);
    if (cpaLines.length > 0 || terms?.cpa_amount_minor || outcome) {
      parts.push(cpaPart(cpaLines, terms, outcome));
    }

    const others = ls.filter((l) => !REVSHARE_LINES.has(l.type) && !CPA_LINES.has(l.type) && !ADJUSTMENT_LINES.has(l.type));
    if (others.length > 0) {
      parts.push({ key: 'other', title: t('calc.other.title'), rows: others.map(lineRow) });
    }
    const adjustments = ls.filter((l) => ADJUSTMENT_LINES.has(l.type));
    if (adjustments.length > 0) {
      parts.push({ key: 'adj', title: t('calc.adjustments.title'), rows: adjustments.map(lineRow) });
    }

    const total = sum(ls);
    period += total;
    const title = terms
      ? [
          terms.brand,
          dealScopeLabel(terms.campaign_name),
          planLabel(terms.commission_type),
          ...((versionsPerDeal.get(terms.deal_id) ?? 0) > 1 ? [t('calc.deal.version', { version: terms.version })] : []),
        ].join(' · ')
      : dealId !== null
        ? t('calc.deal.withoutLines')
        : t('calc.deal.noDeal');
    built.push({
      key: key || 'none',
      title,
      parts,
      total: { label: t('calc.dealTotal'), value: money(total.toString()), tone: 'total' },
    });
  }

  const broughtForward = BigInt(String(statement['brought_forward_minor'] ?? '0'));
  const earned = BigInt(String(statement['earned_minor'] ?? '0'));
  const held = BigInt(String(statement['held_minor'] ?? '0'));
  const payable = BigInt(String(statement['payable_minor'] ?? '0'));
  const agrees = period + broughtForward === earned;
  const rows: CalculationRow[] = [{ label: t('calc.recon.period'), value: money(period.toString()) }];
  if (broughtForward !== 0n) {
    rows.push({ label: t('calc.recon.broughtForward'), value: money(broughtForward.toString()) });
  }
  rows.push({ label: t('col.earned'), value: money(earned.toString()), tone: 'subtotal' });
  rows.push({ label: t('col.held'), value: money((-held).toString()) });
  rows.push({ label: t('col.payable'), value: money(payable.toString()), tone: 'total' });

  return {
    deals: built,
    reconciliation: { rows, agrees, ...(agrees ? {} : { mismatch: t('calc.recon.mismatch') }) },
    periodMinor: period.toString(),
    payableMinor: (period + broughtForward - held).toString(),
    outcomesNote:
      outcomes === 'unavailable'
        ? t('calc.outcomesUnavailable')
        : outcomes
          ? t('calc.outcomesAssessed', { date: formatDate(outcomes.assessed_at) })
          : null,
  };
}

/* ------------------------------------------------------------------ *
 * Sub-affiliates, self-service — §8.9, §12.3
 * ------------------------------------------------------------------ */

/** §8.9a — why this affiliate cannot recruit right now, in plain words. */
const WHY_NOT_WORDS: Record<SubAffiliateWhyNot, AffiliateMessageKey> = {
  PROGRAMME_OFF: 'subaffiliates.whyNot.programmeOff',
  NOT_ACTIVE: 'subaffiliates.whyNot.notActive',
  DEPTH_LIMIT: 'subaffiliates.whyNot.depthLimit',
  NO_APPLICATION_PAGE: 'subaffiliates.whyNot.noApplicationPage',
};

/**
 * §8.9a — the reason a recruiter cannot recruit right now, in the words this
 * screen shows (never the code itself): the programme is off, their account
 * is not active yet, or they are already as deep as this operator allows.
 */
export function subAffiliateWhyNotWords(whyNot: SubAffiliateWhyNot): string {
  return t(WHY_NOT_WORDS[whyNot]);
}

/** §8.2 — the basis an override is computed on, in the affiliate's own words. */
const OVERRIDE_BASIS_WORDS: Record<'SUB_COMMISSION' | 'SUB_NGR', AffiliateMessageKey> = {
  SUB_COMMISSION: 'subaffiliates.basis.commission',
  SUB_NGR: 'subaffiliates.basis.ngr',
};

/** §8.6 — how long the override runs, in the affiliate's own words. */
function overrideLifetimeWords(
  lifetime: 'FIXED_TERM' | 'LIFETIME' | 'ACTIVE_ONLY',
  months: number,
): string {
  switch (lifetime) {
    case 'LIFETIME':
      return t('subaffiliates.lifetime.lifetime');
    case 'ACTIVE_ONLY':
      return t('subaffiliates.lifetime.activeOnly');
    default:
      return t(
        months === 1 ? 'subaffiliates.lifetime.fixedTerm.one' : 'subaffiliates.lifetime.fixedTerm.other',
        { months },
      );
  }
}

/**
 * §8.2b — "An override on the *sub's commission* basis at 5% is a very
 * different cost to the operator than 5% on the sub's *NGR* — the UI MUST
 * make the basis unmissable." One sentence, built from the three facts that
 * actually decide what it means — the rate, the basis and how long it runs —
 * rather than from the operator's own codes for them, and it always ends on
 * §8.2a's fact: paid on top of the sub-affiliate's own commission, never out
 * of it.
 */
export function subAffiliateTermsSentence(row: {
  readonly override_bps: number;
  readonly basis: 'SUB_COMMISSION' | 'SUB_NGR';
  readonly lifetime: 'FIXED_TERM' | 'LIFETIME' | 'ACTIVE_ONLY';
  readonly fixed_term_months: number;
}): string {
  return t('subaffiliates.terms.sentence', {
    rate: percent(row.override_bps),
    basis: t(OVERRIDE_BASIS_WORDS[row.basis]),
    lifetime: overrideLifetimeWords(row.lifetime, row.fixed_term_months),
  });
}

/** One direct recruit, §8.9e's view of them and nothing more. */
export interface SubAffiliateRow {
  readonly key: string;
  readonly joined: string;
  readonly status: string;
  /** DESIGN.md §4's tone table — Active locked, everything else neutral. */
  readonly statusTone: 'locked' | 'neutral';
  readonly linked: boolean;
  /** Present on a `linked: false` row — still an applicant, not yet a referral. */
  readonly note?: string;
  readonly clicks: number;
  readonly ftds: number;
  /**
   * §8.9e — never provisional: this is what the commission run has already
   * written, not a figure that can still move.
   */
  readonly overrideEarned: readonly { readonly currency: string; readonly amount: string }[];
}

export interface SubAffiliateInvitationRow {
  readonly email: string;
  readonly sent: string;
  readonly joined: boolean;
}

export interface SubAffiliatesView {
  readonly canRecruit: boolean;
  /** Present exactly when `canRecruit` is false. */
  readonly whyNot?: string;
  readonly referralCode: string | null;
  readonly signupPath: string | null;
  /** §8.2b's sentence — present only where a link could ever be offered. */
  readonly terms: string;
  readonly applyNote: string;
  readonly invitationsLeftToday: number;
  readonly invitationsLeftNote: string;
  readonly rows: readonly SubAffiliateRow[];
  /** §30.1a's pattern — what to say when there is genuinely nobody yet. */
  readonly empty?: string;
  readonly invitations: readonly SubAffiliateInvitationRow[];
  readonly invitationsEmpty?: string;
}

/**
 * §8.9e — the recruiter's own view: no names, no contact details, no rates,
 * no players, no payments. Only when a sub-affiliate joined, their portal
 * status (§6.3g), clicks, FTDs and the override earned on them.
 */
export function subAffiliates(report: SubAffiliatesReport): SubAffiliatesView {
  // §8.9a — defensive: a referral code with nowhere to send it (the operator
  // has no application page) is treated as "cannot recruit" even if the
  // backend's own gate somehow said otherwise — a code with a null
  // `signup_path` is a link box with nothing to show and no copy button, and
  // an invite form with nothing to send.
  const noApplicationPage = report.referral_code !== null && report.signup_path === null;
  const canRecruit = report.programme.can_recruit && !noApplicationPage;
  const whyNot = noApplicationPage
    ? subAffiliateWhyNotWords('NO_APPLICATION_PAGE')
    : report.programme.why_not
      ? subAffiliateWhyNotWords(report.programme.why_not)
      : undefined;

  return {
    canRecruit,
    ...(whyNot ? { whyNot } : {}),
    referralCode: report.referral_code,
    signupPath: report.signup_path,
    terms: subAffiliateTermsSentence(report.programme),
    applyNote: t('subaffiliates.link.applyNote'),
    invitationsLeftToday: report.programme.invitations_left_today,
    invitationsLeftNote: t(
      report.programme.invitations_left_today === 1
        ? 'subaffiliates.invitationsLeft.one'
        : 'subaffiliates.invitationsLeft.other',
      { n: report.programme.invitations_left_today },
    ),
    rows: report.subs.map((s) => ({
      key: s.key,
      joined: t('subaffiliates.row.joined', { date: formatDate(s.joined_at) }),
      status: explainStatus(s.status).status,
      statusTone: s.status === 'Active' ? ('locked' as const) : ('neutral' as const),
      linked: s.linked,
      /*
       * §8.9c — two different unlinked rows. One is still an applicant and
       * is waiting on a decision; the other was approved and is waiting on
       * the referral itself, which is the company's to make and not a
       * decision about them at all.
       */
      ...(s.linked
        ? {}
        : { note: t(s.status === 'Active' ? 'subaffiliates.row.notLinked' : 'subaffiliates.row.pending') }),
      clicks: s.clicks,
      ftds: s.ftds,
      overrideEarned: s.override_earned.map((o) => ({
        currency: o.currency,
        amount: formatMoney({ amount_minor: o.amount_minor, currency: o.currency }),
      })),
    })),
    ...(report.subs.length === 0 ? { empty: t('subaffiliates.empty') } : {}),
    invitations: report.invitations.map((i) => ({
      email: i.email,
      sent: formatDate(i.sent_at),
      joined: i.joined,
    })),
    ...(report.invitations.length === 0 ? { invitationsEmpty: t('subaffiliates.invitations.empty') } : {}),
  };
}

/**
 * §12.3 — whether the Sub-affiliates tab is worth showing at all.
 *
 * The programme being on is the ordinary reason, but a recruiter who already
 * has recruits keeps the tab even after the programme is switched off
 * underneath them: their sub-affiliates' overrides keep being paid, and the
 * tab is the only place that history and those earnings are shown. Hiding it
 * along with the programme made recruits — and what they had earned the
 * recruiter — disappear from the portal entirely.
 */
export function subAffiliatesTabVisible(report: {
  readonly programme: { readonly enabled: boolean };
  readonly subs: { length: number };
}): boolean {
  return report.programme.enabled || report.subs.length > 0;
}

/* ------------------------------------------------------------------ *
 * §7.12d / §10.11a — the campaigns directory
 * ------------------------------------------------------------------ */

export interface CampaignDirectoryRow {
  readonly key: string;
  readonly campaignId: string;
  readonly name: string;
  readonly brand: string;
  /** What pays here, in a phrase, or null where nothing does yet. */
  readonly paidOn: string | null;
  /** The plan and the headline figure, so the list answers the question. */
  readonly plan: string | null;
  readonly rate: string | null;
  readonly products: string;
  readonly jurisdictions: string;
}

export interface CampaignDirectoryView {
  /** Campaigns this affiliate is on, and what each pays them. */
  readonly on: readonly CampaignDirectoryRow[];
  /** §10.10 — invited by the operator, not yet joined. */
  readonly invited: readonly CampaignDirectoryRow[];
  /** §10.11a — open to ask for. Nothing pays until the operator accepts. */
  readonly canAskFor: readonly CampaignDirectoryRow[];
  readonly askNote: string | null;
  readonly empty?: string;
}

const list = (xs: readonly string[]) => (xs.length > 0 ? xs.join(', ') : '—');

/**
 * §7.12d — the figures on the list, not one dialogue at a time.
 *
 * An affiliate on a hundred brands could read their terms only by opening a
 * hundred campaigns, and only if they already knew which to open. The rate
 * belongs beside the name.
 */
export function campaignDirectoryView(
  r: Awaited<ReturnType<AffiliateApi['campaigns']>>,
  brandOf: (id: string) => string,
): CampaignDirectoryView {
  const priced = (c: {
    campaign_id: string; campaign_name: string; brand_name: string;
    products: readonly string[]; jurisdictions: readonly string[];
    paid_on?: 'CAMPAIGN' | 'DEFAULT' | 'CUSTOM' | null;
    terms?: TermsSummary | null;
  }): CampaignDirectoryRow => ({
    key: c.campaign_id,
    campaignId: c.campaign_id,
    name: c.campaign_name,
    brand: c.brand_name || brandOf(c.campaign_id),
    paidOn: c.paid_on ? termsSourceLabel(c.paid_on, c.brand_name) : null,
    plan: c.terms ? planLabel(c.terms.commission_type) : null,
    // The headline number only. The full table is a click away, and a row
    // that tried to carry every rung of a hybrid would carry none of it
    // legibly.
    rate: c.terms
      ? c.terms.cpa_amount_minor
        ? formatMoney({
            amount_minor: c.terms.cpa_amount_minor,
            currency: c.terms.cpa_currency ?? c.terms.base_currency,
          })
        : c.terms.revshare_rate_bps !== null
          ? percent(c.terms.revshare_rate_bps)
          : null
      : null,
    products: list(c.products),
    jurisdictions: list(c.jurisdictions),
  });

  const on = r.items.map(priced);
  const invited = r.offered.map(priced);
  const canAskFor = r.discoverable.map(priced);
  return {
    on,
    invited,
    canAskFor,
    askNote: r.discoverable_note ?? null,
    ...(on.length + invited.length + canAskFor.length === 0
      ? { empty: t('campaignsDirectory.empty') }
      : {}),
  };
}

/* ------------------------------------------------------------------ *
 * §39.6 — the click log
 * ------------------------------------------------------------------ */

export interface ClickLogRowView {
  readonly key: string;
  readonly when: string;
  /**
   * The time of day in UTC, "14:05". The traffic table's days are UTC days,
   * so a list opened from one of them reads its clicks on the same clock:
   * in local time, a click late in the UTC day shows the next date.
   */
  readonly timeUtc: string;
  readonly status: string;
  /** Why it was refused, where there is a reason worth reading. */
  readonly why: string | null;
  readonly campaign: string;
  readonly placement: string;
  /** The placement without the landing page's address, for a narrow table. */
  readonly labels: string;
  readonly where: string;
  readonly what: string;
  /** Counted, refused, or valid and still not a player. */
  readonly outcome: 'ATTRIBUTED' | 'REFUSED' | 'NO_PLAYER';
}

export interface ClickLogView {
  readonly updated: string;
  readonly rows: readonly ClickLogRowView[];
  readonly hasMore: boolean;
  readonly empty?: string;
}

export function clickLogView(
  report: Envelope<{
    data: readonly {
      occurred_at: string; status: string; bot_reason: string | null; campaign: string;
      landing_page: string | null; link_token: string | null;
      sub_id_1: string | null; sub_id_2: string | null; sub_id_3: string | null;
      ip_country: string | null; device: string | null; os: string | null;
      browser: string | null; attributed: boolean;
      referrer?: string | null; brand?: string | null;
      media?: string | null; channel?: string | null;
    }[];
    has_more: boolean;
  }>,
  now: Date,
): ClickLogView {
  const rows = report.data.data.map((r, i): ClickLogRowView => ({
    key: `${r.occurred_at}-${i}`,
    when: formatDateTime(r.occurred_at),
    timeUtc: new Date(r.occurred_at).toISOString().slice(11, 16),
    status: r.status,
    why: r.bot_reason,
    // The brand a click belonged to, which one row could not say before: an
    // affiliate on three brands read a campaign name and had to know which
    // brand it was under.
    campaign: [r.brand, r.campaign].filter((x): x is string => !!x).join(' · '),
    /*
     * The labels the affiliate put on the link, which is how they recognise
     * which placement this was — now with the banner and the channel they
     * declared, both already on the click and neither previously returned.
     */
    placement: [r.landing_page, r.media, r.channel, r.sub_id_1, r.sub_id_2, r.sub_id_3]
      .filter((x): x is string => !!x && x.trim() !== '')
      .join(' · ') || '—',
    labels: [r.media, r.channel, r.sub_id_1, r.sub_id_2, r.sub_id_3]
      .filter((x): x is string => !!x && x.trim() !== '')
      .join(' · ') || '—',
    // Country, then where the click came from. A referrer is how an affiliate
    // tells placements apart when they did not label the link.
    where: [r.ip_country, r.referrer].filter((x): x is string => !!x).join(' · ') || '—',
    what: [r.device, r.os, r.browser].filter((x): x is string => !!x).join(' · ') || '—',
    /*
     * Three outcomes, not two. A refused click and a valid one that never
     * became a player are different problems with different answers — the
     * first is a rule, the second is the landing page — and a single
     * "attributed: no" would fold them together.
     */
    outcome: r.attributed ? 'ATTRIBUTED' : r.status === 'VALID' ? 'NO_PLAYER' : 'REFUSED',
  }));
  return {
    updated: freshness(report.data_as_of, now),
    rows,
    hasMore: report.data.has_more,
    ...(rows.length === 0 ? { empty: t('clickLog.empty') } : {}),
  };
}

/* ------------------------------------------------------------------ *
 * §17.8 / §22.2a — a player, day by day
 * ------------------------------------------------------------------ */

export interface DailyActivityRowView {
  readonly key: string;
  readonly day: string;
  readonly player: string;
  readonly turnover: string;
  readonly wins: string;
  readonly ggr: string;
  readonly deposits: string;
  readonly withdrawals: string;
}

export interface DailyActivityView {
  readonly updated: string;
  readonly rows: readonly DailyActivityRowView[];
  readonly hasMore: boolean;
  readonly empty?: string;
}

export function dailyActivityView(
  report: Envelope<{
    data: readonly {
      player_ref: string; day: string; turnover_minor: string; wins_minor: string;
      ggr_minor: string; deposits_minor: string; withdrawals_minor: string; currency: string;
    }[];
    has_more: boolean;
  }>,
  now: Date,
): DailyActivityView {
  const rows = report.data.data.map((r): DailyActivityRowView => {
    const m = (amount: string) => formatMoney({ amount_minor: amount, currency: r.currency });
    return {
      key: `${r.player_ref}-${r.day}`,
      day: formatDate(r.day),
      player: r.player_ref,
      turnover: m(r.turnover_minor),
      wins: m(r.wins_minor),
      ggr: m(r.ggr_minor),
      deposits: m(r.deposits_minor),
      withdrawals: m(r.withdrawals_minor),
    };
  });
  return {
    updated: freshness(report.data_as_of, now),
    rows,
    hasMore: report.data.has_more,
    ...(rows.length === 0 ? { empty: t('dailyActivity.empty') } : {}),
  };
}

/* ------------------------------------------------------------------ *
 * Getting paid, at a glance — §31.1a
 * ------------------------------------------------------------------ */

export interface PaymentSummaryView {
  /** What is invoiced and not yet marked paid, per currency. */
  readonly awaiting: readonly { readonly currency: string; readonly amount: string }[];
  /** The most recent payment the operator says it made, if any. */
  readonly lastPayment?: { readonly amount: string; readonly when: string };
  /** §31.1a travels with the claim, never separately. */
  readonly caveat?: string;
  /**
   * Set when no payment method is saved. A balance with nowhere to send it is
   * the one thing on this panel the affiliate can act on today, so it is a
   * prompt rather than a blank field.
   */
  readonly needsMethod?: string;
}

/**
 * The three facts the "when do I get paid" question is actually made of.
 *
 * Per currency rather than summed: an affiliate paid in two currencies has two
 * balances, and one added-up figure would be a number that is true in no
 * currency at all (§1.3).
 */
export function paymentSummary(
  rows: readonly Parameters<typeof paymentRow>[0][],
  method: string | null,
): PaymentSummaryView {
  const outstanding = new Map<string, bigint>();
  for (const r of rows) {
    if (r.operator_marked_paid_at) continue;
    outstanding.set(r.currency, (outstanding.get(r.currency) ?? 0n) + BigInt(r.net_minor));
  }

  // The latest of the ones the operator has marked. Compared as the dates they
  // are, rather than by list order, which is the API's and not a promise.
  const paid = rows
    .filter((r) => r.operator_marked_paid_at !== null)
    .sort((a, b) => (a.operator_marked_paid_at! < b.operator_marked_paid_at! ? 1 : -1));
  const latest = paid[0];

  return {
    awaiting: [...outstanding.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([currency, minor]) => ({
        currency,
        amount: formatMoney({ amount_minor: String(minor), currency }),
      })),
    ...(latest
      ? {
          lastPayment: {
            amount: formatMoney({ amount_minor: latest.net_minor, currency: latest.currency }),
            // Never "Paid on". The operator says so; we did not see it.
            when: t('payment.operatorMarkedPaid', {
              date: formatDay(latest.operator_marked_paid_at!),
            }),
          },
          caveat: t('payment.caveat'),
        }
      : {}),
    ...(method === null ? { needsMethod: t('dash.paid.noMethod') } : {}),
  };
}

/* ------------------------------------------------------------------ *
 * The media gallery — §10.6
 * ------------------------------------------------------------------ */

/** One published creative, as the gallery draws it. */
export interface GalleryItem {
  readonly id: string;
  readonly campaignId: string;
  readonly typeLabel: string;
  readonly language: string;
  /** `728×90`, or a dash where the operator recorded no dimensions. */
  readonly size: string;
  /** What the operator filed it under; empty where they filed it under nothing. */
  readonly product: string;
  readonly geos: readonly string[];
  readonly languageCode: string;
  readonly assetUri: string;
  /**
   * Whether an `<img>` pointed at `assetUri` will draw something.
   *
   * A banner is a picture and previews; an HTML5 unit, a video or a feed is
   * not, and a broken-image icon in a gallery reads as "this creative is
   * broken" rather than "this one is not a picture".
   */
  readonly previewable: boolean;
}

export interface GalleryView {
  /** The current page's items, already sliced. */
  readonly items: readonly GalleryItem[];
  /** Every size present in the unfiltered set, as filter options. */
  readonly sizes: readonly { readonly value: string; readonly label: string }[];
  /** Every type present in the unfiltered set, as filter options. */
  readonly types: readonly { readonly value: string; readonly label: string }[];
  /** Products, languages and markets present in the unfiltered set. */
  readonly products: readonly { readonly value: string; readonly label: string }[];
  readonly languages: readonly { readonly value: string; readonly label: string }[];
  readonly geos: readonly { readonly value: string; readonly label: string }[];
  readonly total: number;
  readonly page: number;
  readonly pages: number;
  /** Set when there is nothing to draw, with the reason it differs. */
  readonly empty?: string;
}

/** One of the affiliate's own live links on a campaign, as a creative's code can carry it. */
export interface CreativeLinkChoice {
  readonly id: string;
  /** The landing page the link goes to, or the campaign's default. */
  readonly label: string;
  readonly url: string;
}

/**
 * The links a creative's code may carry: this affiliate's own, on this
 * campaign of this brand, that are alive and not turned away. Matched on the
 * brand as well as the name, because two brands can run campaigns of the same
 * name and the other brand's link credits the wrong programme.
 */
export function creativeLinkChoices(
  links: readonly {
    link_id: string; campaign_name: string; brand_id: string; landing_page: string | null;
    url: string; revoked: boolean; stopped_because?: string;
  }[],
  campaign: { readonly campaign_name: string; readonly brand_id: string } | undefined,
): CreativeLinkChoice[] {
  if (campaign === undefined) return [];
  return links
    .filter((l) =>
      l.campaign_name === campaign.campaign_name && l.brand_id === campaign.brand_id &&
      !l.revoked && !l.stopped_because)
    .map((l) => ({ id: l.link_id, label: l.landing_page ?? t('creatives.link.default'), url: l.url }));
}

/** `728×90` — the multiplication sign, not the letter x. */
function sizeOf(width: number | null | undefined, height: number | null | undefined): string {
  return width && height ? `${width}×${height}` : UNAVAILABLE;
}

function creativeTypeLabel(type: string): string {
  const key = `creativeType.${type}` as AffiliateMessageKey;
  const label = t(key);
  // An operator can publish a type this console has no word for yet; showing
  // the key would put `creativeType.FEED` on the screen, so the raw code is
  // the better fallback of the two.
  return label === key ? type : label;
}

/**
 * The gallery, filtered and paged.
 *
 * The filters are built from what is actually present rather than from the
 * type CHECK: an affiliate on one brand's campaigns sees the three sizes that
 * brand publishes, not the ten the schema permits, and a filter that can only
 * ever return nothing is not a filter.
 */
export function creativeGallery(
  rows: readonly {
    id: string;
    campaign_id: string;
    type: string;
    language: string | null;
    width?: number | null;
    height?: number | null;
    asset_uri: string;
    product?: string | null;
    /** Markets the creative is for; the database hands it back as a list or as `{GB,DE}`. */
    geos?: readonly string[] | string | null;
  }[],
  opts: {
    size: string; type: string; page: number; perPage: number; assetBase?: string;
    product?: string; language?: string; geo?: string;
  },
): GalleryView {
  const geosOf = (v: readonly string[] | string | null | undefined): string[] =>
    v == null ? [] : Array.isArray(v) ? [...v] : String(v).replace(/^\{|\}$/g, '').split(',').filter((g) => g !== '');
  const all = rows.map((r): GalleryItem => ({
    id: r.id,
    campaignId: r.campaign_id,
    typeLabel: creativeTypeLabel(r.type),
    language: r.language ?? UNAVAILABLE,
    size: sizeOf(r.width, r.height),
    product: r.product ?? '',
    geos: geosOf(r.geos),
    languageCode: r.language ?? '',
    // L21 — an uploaded creative is served from this platform's API, named
    // by a path; the affiliate's site needs the whole address.
    assetUri: r.asset_uri.startsWith('/v1/') && opts.assetBase !== undefined
      ? resolveRequestUrl(opts.assetBase, r.asset_uri).toString()
      : r.asset_uri,
    previewable: r.type === 'BANNER',
  }));

  const sizes = [...new Set(rows.map((r) => sizeOf(r.width, r.height)))]
    .filter((s) => s !== UNAVAILABLE)
    .sort((a, b) => Number(a.split('×')[0]) - Number(b.split('×')[0]))
    .map((value) => ({ value, label: value }));
  const types = [...new Set(rows.map((r) => r.type))]
    .sort()
    .map((value) => ({ value, label: creativeTypeLabel(value) }));

  const distinct = (values: readonly string[]) =>
    [...new Set(values.filter((v) => v !== ''))].sort().map((value) => ({ value, label: value }));
  const products = distinct(all.map((i) => i.product));
  const languages = distinct(all.map((i) => i.languageCode));
  const geos = distinct(all.flatMap((i) => i.geos));

  const matching = all.filter(
    (i) =>
      (opts.size === '' || i.size === opts.size) &&
      (opts.type === '' || i.typeLabel === creativeTypeLabel(opts.type)) &&
      (!opts.product || i.product === opts.product) &&
      (!opts.language || i.languageCode === opts.language) &&
      // A creative filed under no market is for every market, so a market
      // filter keeps it rather than hiding the unrestricted banners.
      (!opts.geo || i.geos.length === 0 || i.geos.includes(opts.geo)),
  );

  const perPage = Math.max(1, opts.perPage);
  const pages = Math.max(1, Math.ceil(matching.length / perPage));
  // Clamped rather than trusted: narrowing a filter while on page 4 of 6
  // otherwise lands on a page that no longer exists and draws nothing, which
  // reads as "the filter matched nothing".
  const page = Math.min(Math.max(1, opts.page), pages);
  const items = matching.slice((page - 1) * perPage, page * perPage);

  return {
    items,
    sizes,
    types,
    products,
    languages,
    geos,
    total: matching.length,
    page,
    pages,
    ...(matching.length === 0
      ? { empty: rows.length === 0 ? t('creatives.empty') : t('creatives.emptyForFilter') }
      : {}),
  };
}

/* ------------------------------------------------------------------ *
 * The media report — §12.13
 * ------------------------------------------------------------------ */

export interface MediaRow {
  readonly creativeId: string;
  readonly typeLabel: string;
  readonly size: string;
  readonly impressions: string;
  readonly clicks: string;
  readonly reach: string;
  /** Clicks over impressions, or a dash where nothing was served. */
  readonly ctr: string;
}

/**
 * One row per creative, with the ratio an affiliate actually compares by.
 *
 * CTR reads as a dash where nothing was served, for the same reason it does on
 * the dashboard: a banner nobody counted an impression for and a banner served
 * a thousand times to nobody who clicked are different facts, and `0.00%`
 * asserts the second when only the first may be true.
 */
export function mediaReport(
  report: Envelope<readonly {
    creative_id: string; type: string;
    width: number | null; height: number | null;
    impressions: number; clicks: number; reach: number;
  }[]>,
  now: Date,
): { readonly updated: string; readonly rows: readonly MediaRow[]; readonly empty?: string } {
  const rows = report.data.map((r): MediaRow => ({
    creativeId: r.creative_id,
    typeLabel: creativeTypeLabel(r.type),
    size: sizeOf(r.width, r.height),
    impressions: r.impressions === 0 ? UNAVAILABLE : formatCount(r.impressions),
    clicks: formatCount(r.clicks),
    reach: r.reach === 0 ? UNAVAILABLE : formatCount(r.reach),
    ctr: r.impressions === 0 ? UNAVAILABLE : `${((r.clicks / r.impressions) * 100).toFixed(2)}%`,
  }));

  return {
    updated: freshness(report.data_as_of, now),
    rows,
    ...(rows.length === 0 ? { empty: t('media.empty') } : {}),
  };
}


/* ------------------------------------------------------------------ *
 * The running figure, on every page
 * ------------------------------------------------------------------ */

/**
 * What has been earned in the open period, summed per currency.
 *
 * Small enough for the rail and the one figure an affiliate opens the console
 * to see. Per currency, never added across them — the same rule the payment
 * summary keeps, for the same reason: a total in no currency is true in none.
 *
 * Absent rather than zero when there is nothing to show, so the rail does not
 * carry a permanent `0.00` for an affiliate between periods.
 */
export function runningCommission(
  rows: readonly { currency: string; computed_minor: string; pipeline_minor: string }[],
): readonly string[] {
  const byCurrency = new Map<string, bigint>();
  for (const r of rows) {
    // Computed *and* pipeline: what the engine has priced, plus what has
    // qualified and is waiting to be. An affiliate reading only the first
    // wonders where yesterday's conversions went.
    const total = BigInt(r.computed_minor) + BigInt(r.pipeline_minor);
    if (total === 0n) continue;
    byCurrency.set(r.currency, (byCurrency.get(r.currency) ?? 0n) + total);
  }
  return [...byCurrency.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([currency, minor]) => formatMoney({ amount_minor: String(minor), currency }));
}

/** The click statuses the platform records (0162, plus REVOKED from 0240). */
export const CLICK_STATUSES = [
  'VALID', 'DUPLICATE', 'BOT', 'INVALID_TOKEN', 'REVOKED', 'LINK_INACTIVE',
  'CAMPAIGN_EXPIRED', 'AFFILIATE_BLOCKED', 'NOT_PARTICIPATING', 'GEO_BLOCKED',
  'OS_BLOCKED', 'DEVICE_BLOCKED', 'TARGETING_BLOCKED', 'NO_ELIGIBLE_CAMPAIGN',
] as const;
const CLICK_STATUS_SET: ReadonlySet<string> = new Set(CLICK_STATUSES);

/** A click's status in words; a status this build has never seen stays its code. */
export function clickStatusWords(code: string): string {
  return CLICK_STATUS_SET.has(code) ? t(`clickStatus.${code}` as AffiliateMessageKey) : code;
}

/* ------------------------------------------------------------------ *
 * The dashboard's commission for the chosen range
 * ------------------------------------------------------------------ */

/**
 * One tile, one line per currency (never added across currencies), marked
 * provisional off the report's own envelope. No earning in the range is a
 * plain dash with a note, not a zero in a currency the affiliate may not be
 * paid in.
 */
export function commissionInRangeFigure(
  report: Envelope<readonly { currency: string; amount_minor: string; lines: number }[]>,
): FigureView {
  const value = report.data.length === 0
    ? '—'
    : report.data.map((r) => formatMoney({ amount_minor: r.amount_minor, currency: r.currency })).join(' / ');
  const base = figure(t('dash.commissionInRange'), value, report);
  const note = report.data.length === 0 ? t('dash.commissionInRange.none') : report.commission_note;
  return note ? { ...base, note: base.note ? `${base.note} ${note}` : note } : base;
}
